import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import { isSupabaseConfigured, supabase } from "./supabaseClient";

/*
 * ETFowl — 美股台股 ETF 即時資訊平台
 * Data: Yahoo Finance v8/chart API (no auth) + quoteSummary (crumb auth)
 * Observe · Watch · Learn
 */

// ── API via local server (port 3001) ──
const WORKER_API_FALLBACK = "https://etf-api.melvinlee-bi.workers.dev/api";
const ADMIN_EMAILS = ["melvinlee.bi@gmail.com"];
// 登入/註冊功能開關:暫時隱藏帳號 UI(改 true 即恢復)
const SHOW_AUTH = true;
// Google 多色「G」標誌(OAuth 按鈕用)
function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ display: "block" }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
function fmtNum(n) {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
}
const currentHost = typeof window === "undefined" ? "localhost" : window.location.hostname;
const currentProtocol = typeof window === "undefined" ? "http:" : window.location.protocol;
const isLoopbackHost = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(currentHost);
const isLanHost = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(currentHost);
const localApi = isLanHost ? `${currentProtocol}//${currentHost}:3001/api` : "http://localhost:3001/api";
const configuredApi = process.env.REACT_APP_API_URL;
const configuredApiIsLoopback = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(?::\d+)?\/api\/?$/.test(configuredApi || "");
const API = configuredApi && !configuredApiIsLoopback
  ? configuredApi
  : (isLoopbackHost || isLanHost ? localApi : WORKER_API_FALLBACK);
const isUsingWorkerApi = API === WORKER_API_FALLBACK;
const MARKET_CACHE_VERSION = "v3";
const MARKET_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const ENABLE_MARKET_CACHE = false;

function hashSymbols(symbols) {
  const value = symbols.join("|");
  let hash = 0;
  for (let i = 0; i < value.length; i++) hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  return Math.abs(hash).toString(36);
}

function marketCacheKey(market, symbols) {
  return `etfowl:market:${MARKET_CACHE_VERSION}:${market}:${hashSymbols(symbols)}`;
}

function readMarketCache(market, symbols) {
  if (!ENABLE_MARKET_CACHE) return null;
  try {
    const raw = localStorage.getItem(marketCacheKey(market, symbols));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.quotes || Date.now() - parsed.savedAt > MARKET_CACHE_TTL_MS) return null;
    return parsed.quotes;
  } catch {
    return null;
  }
}

function writeMarketCache(market, symbols, quotes) {
  if (!ENABLE_MARKET_CACHE) return;
  try {
    localStorage.setItem(marketCacheKey(market, symbols), JSON.stringify({
      savedAt: Date.now(),
      quotes,
    }));
  } catch {
    // Ignore quota/private-mode failures; live data still works.
  }
}

async function fetchChartsInChunks(symbols, onProgress, chunkSize = 45) {
  const allResults = {};
  let loaded = 0;
  for (let i = 0; i < symbols.length; i += chunkSize) {
    const chunk = symbols.slice(i, i + chunkSize);
    const res = await fetch(`${API}/charts?symbols=${chunk.join(",")}&range=5y&interval=1d&compact=1`, { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      Object.assign(allResults, data);
    }
    loaded = Math.min(i + chunk.length, symbols.length);
    if (onProgress) onProgress(loaded, symbols.length, { ...allResults });
  }
  return allResults;
}

async function fetchAllCharts(symbols, onProgress) {
  try {
    return await fetchChartsInChunks(symbols, onProgress, 45);
  } catch (err) {
    console.error("Batch chart fetch failed:", err);
    return {};
  }
}

async function fetchTwseCharts(symbols, onProgress) {
  try {
    return await fetchChartsInChunks(symbols, onProgress, 45);
  } catch (err) {
    console.error("TW batch fetch failed:", err);
    return {};
  }
}

async function fetchDetail(symbol) {
  try {
    const res = await fetch(`${API}/detail/${symbol}`);
    if (!res.ok) return await fetchCachedDetail(symbol);
    const data = await res.json();
    const detail = data?.quoteSummary?.result?.[0] || null;
    if (detail?.fundIntro) return detail;
    const cached = await fetchCachedDetail(symbol);
    if (cached?.fundIntro) return { ...(detail || {}), fundIntro: cached.fundIntro };
    const workerDetail = await fetchDetailFromWorker(symbol);
    return workerDetail?.fundIntro ? { ...(detail || {}), fundIntro: workerDetail.fundIntro } : detail;
  } catch {
    const cached = await fetchCachedDetail(symbol);
    if (cached?.fundIntro) return cached;
    return await fetchDetailFromWorker(symbol);
  }
}

async function fetchDetailFromWorker(symbol) {
  if (!symbol || API === WORKER_API_FALLBACK) return null;
  try {
    const res = await fetch(`${WORKER_API_FALLBACK}/detail/${symbol}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.quoteSummary?.result?.[0] || null;
  } catch {
    return null;
  }
}

async function fetchCachedDetail(symbol) {
  if (!isSupabaseConfigured || !symbol) return null;
  try {
    const { data, error } = await supabase
      .from("etf_details")
      .select("data")
      .eq("symbol", symbol.toUpperCase())
      .maybeSingle();
    if (error) return null;
    return data?.data || null;
  } catch {
    return null;
  }
}

async function fetchHoldings(symbol) {
  try {
    const res = await fetch(`${API}/holdings/${symbol}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function fetchNews(symbol) {
  try {
    const res = await fetch(`${API}/news/${encodeURIComponent(symbol)}`, { cache: "no-store" });
    if (!res.ok) return { items: [] };
    return normalizeNewsPayload(await res.json());
  } catch {
    return { items: [] };
  }
}

function normalizeNewsPayload(payload) {
  const fetchedAt = payload?.fetchedAt || new Date().toISOString();
  const items = (payload?.items || []).map((item) => {
    const publishedAt = item.publishedAt || item.pubDate || item.date || item.updatedAt || fetchedAt;
    return {
      ...item,
      publishedAt,
      isDateFallback: !(item.publishedAt || item.pubDate || item.date || item.updatedAt),
    };
  });
  return { ...(payload || {}), fetchedAt, items };
}

async function fetchChartSingle(symbol, range = "1y", interval = "1wk") {
  try {
    const res = await fetch(`${API}/chart/${symbol}?range=${range}&interval=${interval}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.chart?.result?.[0] || null;
  } catch {
    return null;
  }
}

async function fetchHoldingTrendCharts(symbols, onProgress) {
  try {
    const batchSize = 10;
    const allResults = {};

    for (let i = 0; i < symbols.length; i += batchSize) {
      const batch = symbols.slice(i, i + batchSize);
      const res = await fetch(
        `${API}/batch-chart?symbols=${batch.join(",")}&range=3y&interval=1d`
      );
      if (res.ok) {
        const data = await res.json();
        Object.assign(allResults, data);
      }
      if (onProgress) onProgress(Math.min(i + batchSize, symbols.length), symbols.length, { ...allResults });
    }

    return allResults;
  } catch (err) {
    console.error("Holding trend fetch failed:", err);
    return {};
  }
}

async function fetchTwStockNames(codes) {
  try {
    const res = await fetch(`${API}/tw-names?codes=${codes.join(",")}`);
    if (!res.ok) return {};
    return await res.json();
  } catch {
    return {};
  }
}

async function searchEtfsByHolding(symbol, etfs) {
  try {
    const res = await fetch(`${API}/holding-search?symbol=${encodeURIComponent(symbol)}&etfs=${etfs.join(",")}`);
    if (!res.ok) throw new Error("持股搜尋失敗");
    return await res.json();
  } catch (err) {
    return { error: err.message, matches: [] };
  }
}

// ── ETF Universe ──
const US_ETF_CATS = {
  "核心/大盤": [
    "VOO",
    "IVV",
    "SPY",
    "VTI",
    "QQQ",
    "VUG",
    "VTV",
    "IWF",
    "IJH",
    "VIG",
    "IJR",
    "VO",
    "QQQM",
    "ITOT",
    "RSP",
    "VB",
    "IWM",
    "IWD",
    "VT",
    "IVW",
    "SCHX",
    "SCHG",
    "IWR",
    "SPYG",
    "VV",
    "QUAL",
    "IVE",
    "IWB",
    "DFAC",
    "VONG",
    "DIA",
    "SCHB",
    "DYNF",
    "VBR",
    "CGDV",
    "SPYV",
    "MGK",
    "IUSG",
    "VXF",
    "AVUV",
    "MTUM",
    "IUSV",
    "MDY",
    "VOOG",
    "FNDX",
    "AVEM",
    "FNDF",
    "CGGR",
    "VBK",
    "USMV",
    "VOE",
    "SCHA",
    "DFUS",
    "OEF",
    "IWP",
    "DFIV",
    "VONV",
    "IWV",
    "AVDV",
    "VOT",
    "SPMO",
    "SPHQ",
    "SPMD",
    "ESGU",
    "IWY"
  ],
  "國際市場": [
    "VEA",
    "IEFA",
    "IEMG",
    "VXUS",
    "VWO",
    "EFA",
    "VEU",
    "SCHF",
    "IXUS",
    "SPDW",
    "ACWI",
    "IDEV",
    "EFV",
    "VGK",
    "EEM",
    "EMXC",
    "EWY",
    "EWJ",
    "VYMI",
    "SPEM",
    "BBJP"
  ],
  "債券/現金": [
    "BND",
    "AGG",
    "SGOV",
    "BNDX",
    "VCIT",
    "IEF",
    "BIL",
    "BSV",
    "MUB",
    "VTEB",
    "TLT",
    "VCSH",
    "GOVT",
    "VGIT",
    "MBB",
    "JPST",
    "IUSB",
    "VGSH",
    "LQD",
    "BIV",
    "JAAA",
    "USHY",
    "FBND",
    "SHY",
    "IGSB",
    "SHV",
    "VTIP",
    "IEI",
    "IGIB",
    "BINC",
    "USIG",
    "USFR"
  ],
  "商品/另類": [
    "GLD",
    "IAU",
    "IBIT",
    "SLV",
    "GLDM",
    "PPLT"
  ],
  "產業板塊": [
    "VGT",
    "XLK",
    "SMH",
    "XLF",
    "XLE",
    "XLV",
    "SOXX",
    "XLI",
    "GDX",
    "XLC",
    "IYW",
    "XLU",
    "XLY",
    "FTEC",
    "DRAM",
    "GRID",
    "URA",
    "ITA",
    "UFO",
    "HACK",
    "BOTZ",
    "TAN"
  ],
  "收益/股息": [
    "SCHD",
    "VYM",
    "JEPI",
    "DGRO",
    "JEPQ",
    "DVY",
    "RDVY",
    "SDY",
    "COWZ"
  ],
  "槓桿/反向": [
    "TQQQ",
    "SOXL"
  ],
  "不動產/REITs": [
    "VNQ"
  ]
};
const TW_ETF_CATS = {
  "台股大盤": [
    "0050.TW",
    "006208.TW",
    "00929.TW",
    "009816.TW",
    "00922.TW",
    "00692.TW",
    "00712.TW",
    "00850.TW",
    "00923.TW",
    "009820.TW",
    "00888.TWO",
    "009802.TW",
    "00905.TW",
    "00896.TW",
    "00690.TW",
    "00701.TW",
    "00733.TW",
    "009813.TW",
    "00625K.TW",
    "006205.TW"
  ],
  "高股息": [
    "0056.TW",
    "00878.TW",
    "00919.TW",
    "00713.TW",
    "00918.TW",
    "00940.TW",
    "00900.TW",
    "00882.TW",
    "00939.TW",
    "00915.TW",
    "00934.TW",
    "00946.TW",
    "00936.TW"
  ],
  "科技/半導體": [
    "0052.TW",
    "00881.TW",
    "00891.TW",
    "00830.TW",
    "00935.TW",
    "00927.TW",
    "00893.TW",
    "00965.TW",
    "00892.TW",
    "00941.TW",
    "00910.TW",
    "00735.TW",
    "00894.TW",
    "00947.TW",
    "00904.TW",
    "00876.TW",
    "00909.TW",
    "009819.TW",
    "00861.TW",
    "00770.TW",
    "00762.TW",
    "00952.TW",
    "00902.TW"
  ],
  "海外市場": [
    "00662.TW",
    "00646.TW",
    "00757.TW",
    "00924.TW",
    "00885.TW",
    "009805.TW",
    "00895.TW",
    "00752.TW",
    "00949.TW",
    "009800.TW"
  ],
  "商品": [
    "00738U.TW",
    "00635U.TW"
  ],
  "債券": [
    "00937B.TWO",
    "00679B.TWO",
    "00751B.TWO",
    "00687B.TWO",
    "00720B.TWO",
    "00725B.TWO",
    "00772B.TWO",
    "00933B.TWO",
    "00724B.TWO",
    "00761B.TWO",
    "00773B.TWO",
    "00746B.TWO",
    "00740B.TWO",
    "00792B.TWO",
    "00777B.TWO",
    "00950B.TWO",
    "00722B.TWO",
    "00778B.TWO",
    "00754B.TWO",
    "00723B.TWO",
    "00756B.TWO",
    "00795B.TWO",
    "00764B.TWO",
    "00953B.TW",
    "00948B.TWO",
    "00726B.TWO",
    "00768B.TWO",
    "00785B.TWO",
    "00779B.TWO",
    "00749B.TWO",
    "00942B.TWO",
    "00931B.TWO",
    "00862B.TWO",
    "00760B.TWO",
    "00789B.TWO",
    "00696B.TWO",
    "00863B.TWO",
    "00849B.TWO",
    "00836B.TWO",
    "00719B.TWO",
    "00867B.TWO",
    "00870B.TWO",
    "00791B.TWO",
    "00711B.TW",
    "00857B.TWO",
    "00981B.TWO",
    "00945B.TW",
    "00865B.TW",
    "00775B.TW",
    "00985B.TW"
  ],
  "槓桿/反向": [
    "00631L.TW",
    "00675L.TW",
    "00632R.TW",
    "00637L.TW",
    "00670L.TW",
    "00685L.TW",
    "00680L.TW",
    "00663L.TW",
    "00673R.TW",
    "00708L.TW",
    "00633L.TW",
    "00753L.TW",
    "00715L.TW",
    "00688L.TW",
    "00665L.TW"
  ],
  "主動型": [
    "00981A.TW",
    "00403A.TW",
    "00982A.TW",
    "00992A.TW",
    "00991A.TW",
    "00988A.TW",
    "00990A.TW",
    "00400A.TW",
    "00980A.TW",
    "00999A.TW",
    "00997A.TW",
    "00993A.TW",
    "00985A.TW",
    "00984A.TW",
    "00995A.TW",
    "00994A.TW",
    "00996A.TW",
    "00405A.TW",
    "00402A.TW",
    "00406A.TW",
    "00998A.TW",
    "00401A.TW",
    "00987A.TW",
    "00404A.TW",
    "00983A.TW",
    "00989A.TW",
    "00986A.TW"
  ]
};
const EMPTY_ARRAY = [];
const US_ETF_THEMES = {
  "SMH": "半導體",
  "DRAM": "記憶體/HBM",
  "GRID": "AI電力/電網",
  "URA": "核能/鈾礦",
  "ITA": "航太國防",
  "UFO": "太空衛星",
  "GDX": "黃金礦業",
  "HACK": "網路安全",
  "BOTZ": "機器人/自動化",
  "TAN": "太陽能/新能源",
  "XLK": "科技板塊",
  "XLF": "金融板塊",
  "XLE": "能源板塊",
  "XLV": "醫療保健",
  "SOXX": "半導體",
  "XLI": "工業板塊",
  "XLC": "通訊服務",
  "IYW": "科技板塊",
  "XLU": "公用事業",
  "XLY": "非必需消費",
  "FTEC": "科技板塊",
  "SOXL": "半導體槓桿",
  "TQQQ": "NASDAQ槓桿"
};
const TW_ETF_THEMES = {
  "0052.TW": "台股科技",
  "00881.TW": "5G通訊",
  "00891.TW": "半導體",
  "00892.TW": "半導體",
  "00927.TW": "半導體收益",
  "00830.TW": "費城半導體",
  "00935.TW": "創新科技50",
  "00941.TW": "上游半導體",
  "00965.TW": "航太防衛科技",
  "00910.TW": "太空衛星",
  "00952.TW": "台灣AI 50",
  "00951.TW": "日本半導體",
  "00954.TW": "日本半導體",
  "00646.TW": "S&P500",
  "00757.TW": "FANG+",
  "00662.TW": "NASDAQ",
  "009820.TW": "NASDAQ精選",
  "009800.TW": "NASDAQ",
  "00924.TW": "S&P500成長",
  "00885.TW": "越南市場",
  "00895.TW": "未來車",
  "009805.TW": "美國電力基建",
  "00403A.TW": "主動台股50",
  "00400A.TW": "主動高息",
  "00980A.TW": "主動選股",
  "00981A.TW": "主動選股",
  "00982A.TW": "主動選股",
  "00983A.TW": "ARK創新",
  "00984A.TW": "主動高息",
  "00985A.TW": "主動台股50",
  "00988A.TW": "主動全球創新",
  "00990A.TW": "主動AI新經濟",
  "00991A.TW": "主動未來50",
  "00992A.TW": "主動科技創新",
  "00993A.TW": "主動台股",
  "00994A.TW": "主動台股優選",
  "00995A.TW": "主動台股",
  "00996A.TW": "主動台股豐收",
  "00997A.TW": "主動美股成長",
  "00998A.TW": "主動金融股息",
  "00999A.TW": "主動高息",
  "00401A.TW": "主動台股鑫收",
  "00402A.TW": "主動美國科技",
  "00404A.TW": "主動動能50",
  "00405A.TW": "主動台灣龍耀",
  "00406A.TW": "主動台灣收益",
  "00983A.TW": "ARK創新",
  "00986A.TW": "主動龍頭成長",
  "00987A.TW": "主動優勢成長",
  "00989A.TW": "主動美國科技",
  "00922.TW": "台股領袖50",
  "00692.TW": "公司治理",
  "009816.TW": "台灣TOP50",
  "009802.TW": "台股旗艦50",
  "00923.TW": "ESG低碳",
  "00850.TW": "ESG永續",
  "00900.TW": "高股息",
  "00882.TW": "中國高股息",
  "00930.TW": "ESG高息",
  "00946.TW": "科技高息",
  "00738U.TW": "白銀期貨",
  "00635U.TW": "黃金期貨",
  "00708L.TW": "黃金正2"
};
const TW_ETF_NAMES = {
  "0050.TW": "元大台灣50",
  "0056.TW": "元大高股息",
  "00878.TW": "國泰永續高股息",
  "00919.TW": "群益台灣精選高息",
  "006208.TW": "富邦台50",
  "00981A.TW": "主動統一台股增長",
  "00937B.TWO": "群益ESG投等債20+",
  "00403A.TW": "主動統一升級50",
  "00679B.TWO": "元大美債20年",
  "00751B.TWO": "元大AAA至A公司債",
  "00631L.TW": "元大台灣50正2",
  "00687B.TWO": "國泰20年美債",
  "0052.TW": "富邦科技",
  "00929.TW": "復華台灣科技優息",
  "00720B.TWO": "元大投資級公司債",
  "009816.TW": "凱基台灣TOP50",
  "00725B.TWO": "國泰投資級公司債",
  "00881.TW": "國泰台灣科技龍頭",
  "00772B.TWO": "中信高評級公司債",
  "00933B.TWO": "國泰10Y+金融債",
  "00713.TW": "元大台灣高息低波",
  "00724B.TWO": "群益投資級金融債",
  "00761B.TWO": "國泰A級公司債",
  "00662.TW": "富邦NASDAQ",
  "00773B.TWO": "中信優先金融債",
  "00746B.TWO": "富邦A級公司債",
  "00918.TW": "大華優利高填息30",
  "00740B.TWO": "富邦全球投等債",
  "00922.TW": "國泰台灣領袖50",
  "00792B.TWO": "群益A級公司債",
  "00940.TW": "元大台灣價值高息",
  "00891.TW": "中信關鍵半導體",
  "00777B.TWO": "凱基AAA至A公司債",
  "00830.TW": "國泰費城半導體",
  "00950B.TWO": "凱基A級公司債",
  "00982A.TW": "主動群益台灣強棒",
  "00692.TW": "富邦公司治理",
  "00992A.TW": "主動群益科技創新",
  "00722B.TWO": "群益投資級電信債",
  "00991A.TW": "主動復華未來50",
  "00778B.TWO": "凱基金融債20+",
  "00754B.TWO": "群益AAA-AA公司債",
  "00723B.TWO": "群益投資級科技債",
  "00756B.TWO": "群益投等新興公債",
  "00795B.TWO": "中信美國公債20年",
  "00712.TW": "復華富時不動產(原簡稱:FH富時不動產)",
  "00764B.TWO": "群益25年美債",
  "00988A.TW": "主動統一全球創新",
  "00953B.TW": "群益優選非投等債",
  "00646.TW": "元大S&P500",
  "00948B.TWO": "中信優息投資級債",
  "00726B.TWO": "國泰新興投等債",
  "00757.TW": "統一FANG+",
  "00935.TW": "野村臺灣新科技50",
  "00768B.TWO": "復華20年美債",
  "00785B.TWO": "富邦金融投等債",
  "00850.TW": "元大臺灣ESG永續",
  "00779B.TWO": "凱基美債25+",
  "00990A.TW": "主動元大AI新經濟",
  "00675L.TW": "富邦臺灣加權正2",
  "00923.TW": "群益台ESG低碳50(原簡稱:群益台灣ESG低碳)",
  "00749B.TWO": "凱基新興債10+",
  "00900.TW": "富邦特選高股息30",
  "00882.TW": "中信中國高股息",
  "00927.TW": "群益半導體收益",
  "00942B.TWO": "台新美A公司債20+",
  "00931B.TWO": "統一美債20年",
  "00862B.TWO": "中信投資級公司債",
  "00939.TW": "統一台灣高息動能",
  "00632R.TW": "元大台灣50反1",
  "00760B.TWO": "復華新興企業債",
  "00400A.TW": "主動國泰動能高息",
  "00789B.TWO": "復華公司債A3",
  "00696B.TWO": "富邦美債20年",
  "00980A.TW": "主動野村臺灣優選",
  "00863B.TWO": "中信全球電信債",
  "00849B.TWO": "中信EM主權債0-5",
  "009820.TW": "元大納斯達克精選",
  "00893.TW": "國泰智能電動車",
  "00915.TW": "凱基優選高股息30",
  "00637L.TW": "元大滬深300正2",
  "00999A.TW": "主動野村臺灣高息",
  "00836B.TWO": "永豐10年A公司債",
  "00719B.TWO": "元大美債1-3",
  "00670L.TW": "富邦NASDAQ正2",
  "00867B.TWO": "新光A-BBB電信債",
  "00870B.TWO": "元大15年EM主權債",
  "00791B.TWO": "復華信用債1-5",
  "00738U.TW": "期元大道瓊白銀",
  "00924.TW": "復華S&P500成長",
  "00685L.TW": "群益臺灣加權正2",
  "00711B.TW": "復華彭博新興債(原簡稱:FH彭博新興債)",
  "00635U.TW": "期元大S&P黃金",
  "00857B.TWO": "永豐20年美公債",
  "00888.TWO": "永豐台灣ESG",
  "00965.TW": "元大航太防衛科技",
  "00981B.TWO": "第一金優選非投債",
  "00997A.TW": "主動群益美國增長",
  "00680L.TW": "元大美債20正2",
  "00945B.TW": "凱基美國非投等債",
  "00892.TW": "富邦台灣半導體",
  "00885.TW": "富邦越南",
  "00993A.TW": "主動安聯台灣",
  "00663L.TW": "國泰臺灣加權正2",
  "009805.TW": "新光美國電力基建",
  "00865B.TW": "國泰US短期公債",
  "00941.TW": "中信上游半導體",
  "00985A.TW": "主動野村台灣50",
  "009802.TW": "富邦旗艦50",
  "00934.TW": "中信成長高股息",
  "00673R.TW": "期元大S&P原油反1",
  "00708L.TW": "期元大S&P黃金正2",
  "00905.TW": "FT臺灣Smart",
  "00910.TW": "第一金太空衛星",
  "00895.TW": "富邦未來車",
  "00735.TW": "國泰臺韓科技",
  "00894.TW": "中信小資高價30",
  "00947.TW": "台新臺灣IC設計",
  "00896.TW": "中信綠能及電動車",
  "00775B.TW": "新光投等債15+",
  "00633L.TW": "富邦上証正2",
  "00904.TW": "新光臺灣半導體30",
  "00984A.TW": "主動安聯台灣高息",
  "00753L.TW": "中信中國50正2",
  "00946.TW": "群益科技高息成長",
  "00752.TW": "中信中國50",
  "00876.TW": "元大全球5G",
  "00909.TW": "國泰數位支付服務",
  "009819.TW": "中信數據及電力",
  "00690.TW": "兆豐藍籌30",
  "00861.TW": "元大全球未來通訊",
  "00701.TW": "國泰股利精選30",
  "00995A.TW": "主動中信台灣卓越",
  "00994A.TW": "主動第一金台股優",
  "00733.TW": "富邦臺灣中小",
  "00770.TW": "國泰北美科技",
  "00762.TW": "元大全球AI",
  "00715L.TW": "期街口布蘭特正2",
  "00688L.TW": "國泰20年美債正2",
  "00985B.TW": "群益ESG投等債0-5",
  "00996A.TW": "主動兆豐台灣豐收",
  "00998A.TW": "主動復華金融股息",
  "00401A.TW": "主動摩根台灣鑫收",
  "00402A.TW": "主動安聯美國科技",
  "00404A.TW": "主動聯博動能50",
  "00405A.TW": "主動富邦台灣龍耀",
  "00406A.TW": "主動中信台灣收益",
  "00983A.TW": "主動中信ARK創新",
  "00986A.TW": "主動台新龍頭成長",
  "00987A.TW": "主動台新優勢成長",
  "00989A.TW": "主動摩根美國科技",
  "009813.TW": "貝萊德標普卓越50",
  "00936.TW": "台新永續高息中小",
  "00665L.TW": "富邦恒生國企正2",
  "00952.TW": "凱基台灣AI 50",
  "00625K.TW": "富邦上証+R(人民幣)",
  "006205.TW": "富邦上証(新臺幣)",
  "00902.TW": "中信電池及儲能",
  "00949.TW": "復華日本龍頭",
  "009800.TW": "中信NASDAQ"
};
const US_ETF_DESC = {
  // 核心/大盤
  VOO: "追蹤S&P 500指數，持有美國500大企業，Vanguard發行，費用率極低。",
  IVV: "iShares發行的S&P 500 ETF，與VOO追蹤相同指數，流動性極佳。",
  SPY: "全球最早也最大的ETF，追蹤S&P 500，日均交易量最高，適合短線交易。",
  VTI: "追蹤CRSP全美股市指數，涵蓋大中小型約4000檔美股，一檔買下整個美股市場。",
  QQQ: "追蹤NASDAQ-100指數，高度集中於科技成長股，前幾大持股為AAPL、MSFT、NVDA等。",
  VUG: "Vanguard美國大型成長股ETF，聚焦營收與盈利高成長的企業。",
  VTV: "Vanguard美國大型價值股ETF，投資本益比較低的成熟企業。",
  IWF: "iShares羅素1000成長股ETF，投資美國大型成長型股票。",
  IJH: "iShares S&P 400中型股ETF，投資美國中型企業。",
  VIG: "Vanguard股息成長ETF，篩選連續10年以上增加配息的企業。",
  IJR: "iShares S&P 600小型股ETF，投資美國小型企業。",
  VO: "Vanguard中型股ETF，追蹤CRSP美國中型股指數。",
  QQQM: "QQQ的低費版，同樣追蹤NASDAQ-100，適合長期持有。",
  ITOT: "iShares全美股市ETF，涵蓋大中小型股，類似VTI。",
  RSP: "等權重S&P 500 ETF，每檔持股比重相同，降低大型股集中風險。",
  VB: "Vanguard小型股ETF，投資美國小型企業。",
  IWM: "iShares羅素2000小型股ETF，是最具代表性的美國小型股指數。",
  IWD: "iShares羅素1000價值股ETF，投資美國大型價值型股票。",
  VT: "Vanguard全世界股票ETF，一檔涵蓋全球已開發與新興市場約9000檔股票。",
  IVW: "iShares S&P 500成長股ETF，從S&P 500中篩選成長特徵較強的股票。",
  SCHX: "Schwab美國大型股ETF，追蹤美國前750大企業，費用率極低。",
  SCHG: "Schwab美國大型成長股ETF，費用率極低。",
  IWR: "iShares羅素中型股ETF，投資美國中型企業。",
  SPYG: "SPDR S&P 500成長股ETF。",
  VV: "Vanguard大型股ETF，追蹤CRSP美國大型股指數。",
  QUAL: "iShares MSCI美國品質因子ETF，篩選高ROE、穩定盈利成長的企業。",
  IVE: "iShares S&P 500價值股ETF。",
  IWB: "iShares羅素1000 ETF，涵蓋美國前1000大企業。",
  DFAC: "Dimensional美國核心股票ETF，結合學術因子模型的主動式管理。",
  VONG: "Vanguard羅素1000成長股ETF。",
  DIA: "追蹤道瓊工業平均指數，持有美國30大藍籌股。",
  SCHB: "Schwab美國全市場ETF，涵蓋約2500檔美股。",
  DYNF: "BlackRock美國動態因子ETF，動態配置價值、動能、品質等因子。",
  VBR: "Vanguard小型價值股ETF。",
  CGDV: "Capital Group股息價值ETF，主動管理追求股息與資本增長。",
  SPYV: "SPDR S&P 500價值股ETF。",
  MGK: "Vanguard超大型成長股ETF，集中投資美國最大型的成長企業。",
  IUSG: "iShares核心成長股ETF。",
  VXF: "Vanguard延伸市場ETF，投資S&P 500以外的中小型股。",
  AVUV: "Avantis美國小型價值股ETF，結合學術研究的因子策略。",
  MTUM: "iShares MSCI美國動能因子ETF，投資近期漲勢強勁的股票。",
  MDY: "SPDR S&P 400中型股ETF。",
  OEF: "iShares S&P 100 ETF，投資美國100大超級藍籌股。",
  // 國際市場
  VEA: "Vanguard FTSE已開發市場（不含美國）ETF，涵蓋歐日等成熟市場。",
  IEFA: "iShares核心MSCI歐澳遠東ETF，投資已開發市場（不含美加）。",
  IEMG: "iShares核心MSCI新興市場ETF，涵蓋中國、印度、巴西等。",
  VXUS: "Vanguard全球（不含美國）股票ETF，涵蓋已開發與新興市場。",
  VWO: "Vanguard FTSE新興市場ETF，投資新興國家股票。",
  EFA: "iShares MSCI歐澳遠東ETF，歷史最悠久的國際股票ETF之一。",
  VEU: "Vanguard FTSE全球（不含美國）ETF。",
  SCHF: "Schwab國際股票ETF，投資已開發市場（不含美國），費用率極低。",
  IXUS: "iShares核心MSCI全球（不含美國）ETF。",
  SPDW: "SPDR已開發市場（不含美國）ETF。",
  ACWI: "iShares MSCI全球股票ETF，一檔涵蓋全球已開發與新興市場。",
  EEM: "iShares MSCI新興市場ETF。",
  VGK: "Vanguard FTSE歐洲ETF，投資歐洲主要市場股票。",
  EWJ: "iShares MSCI日本ETF。",
  EWY: "iShares MSCI韓國ETF。",
  // 債券/現金
  BND: "Vanguard美國總體債券ETF，涵蓋公債、公司債、房貸債，是最核心的債券配置。",
  AGG: "iShares核心美國總體債券ETF，與BND類似。",
  SGOV: "iShares 0-3個月美國國庫券ETF，近似現金的超短期公債。",
  BNDX: "Vanguard國際（不含美國）總體債券ETF，投資全球債券並做匯率避險。",
  VCIT: "Vanguard中期公司債ETF，投資投資等級中期公司債。",
  IEF: "iShares 7-10年美國公債ETF，中天期公債代表。",
  BIL: "SPDR 1-3個月國庫券ETF，近似現金。",
  BSV: "Vanguard短期債券ETF。",
  TLT: "iShares 20年以上美國公債ETF，長天期公債，利率敏感度高。",
  VCSH: "Vanguard短期公司債ETF。",
  LQD: "iShares投資等級公司債ETF，投資BBB以上評級公司債。",
  USHY: "iShares高收益公司債ETF，投資非投資等級（高收益）債券。",
  JAAA: "Janus Henderson AAA CLO ETF，投資最高評級的CLO。",
  MUB: "iShares全國市政債ETF，利息免聯邦稅。",
  VTEB: "Vanguard免稅市政債ETF。",
  SHY: "iShares 1-3年美國公債ETF。",
  // 商品/另類
  GLD: "SPDR黃金ETF，持有實體黃金，是全球最大的黃金ETF。",
  IAU: "iShares黃金ETF，費用率比GLD低。",
  IBIT: "iShares比特幣ETF，追蹤比特幣現貨價格。",
  SLV: "iShares白銀ETF，持有實體白銀。",
  GLDM: "SPDR迷你黃金ETF，費用率比GLD更低，每股單價較親民。",
  // 產業板塊
  VGT: "Vanguard資訊科技ETF，投資美國科技產業。",
  XLK: "SPDR科技產業ETF，追蹤S&P 500科技板塊。",
  SMH: "VanEck半導體ETF，投資全球頂尖半導體公司，NVDA、TSM為前幾大持股。",
  XLF: "SPDR金融產業ETF，投資美國銀行、保險、資產管理等金融股。",
  XLE: "SPDR能源產業ETF，投資美國石油、天然氣等能源企業。",
  XLV: "SPDR醫療保健ETF，投資美國製藥、醫療設備、保險等。",
  SOXX: "iShares半導體ETF，與SMH類似但持股略有不同。",
  XLI: "SPDR工業產業ETF，投資航太、國防、機械等工業股。",
  GDX: "VanEck黃金礦業ETF，投資全球黃金開採公司。",
  XLC: "SPDR通訊服務ETF，持有META、GOOG、NFLX等。",
  DRAM: "iShares半導體ETF，聚焦記憶體與HBM高頻寬記憶體供應鏈。",
  GRID: "First Trust清潔能源電網ETF，投資AI電力基礎設施與電網。",
  URA: "Global X鈾礦ETF，投資鈾礦開採與核能相關企業。",
  ITA: "iShares美國航太國防ETF，投資軍工與航太企業。",
  UFO: "Procure太空ETF，投資衛星、火箭發射等太空產業。",
  HACK: "ETFMG網路安全ETF，投資全球資安企業。",
  BOTZ: "Global X機器人與AI ETF，投資自動化與人工智慧企業。",
  TAN: "Invesco太陽能ETF，投資全球太陽能與清潔能源企業。",
  // 收益/股息
  SCHD: "Schwab美國股息股票ETF，篩選連續10年配息且基本面穩健的企業，最受歡迎的股息ETF。",
  VYM: "Vanguard高殖利率ETF，投資預期殖利率較高的美國大型股。",
  JEPI: "JPMorgan股票溢價收入ETF，結合持股與選擇權策略產生月配息收入。",
  DGRO: "iShares核心股息成長ETF，篩選連續5年以上增加配息的企業。",
  JEPQ: "JPMorgan NASDAQ股票溢價收入ETF，以NASDAQ股票搭配選擇權策略月配息。",
  DVY: "iShares精選高股息ETF。",
  RDVY: "First Trust崛起股息成長ETF。",
  SDY: "SPDR S&P高股息貴族ETF，投資連續20年以上增加配息的企業。",
  COWZ: "Pacer美國現金牛100 ETF，篩選自由現金流殖利率最高的100家企業。",
  // 槓桿/反向
  IUSV: "iShares核心價值股ETF。",
  VOOG: "Vanguard S&P 500成長股ETF。",
  FNDX: "Schwab基本面美國大型股ETF，以營收、現金流等基本面加權。",
  AVEM: "Avantis新興市場股票ETF，結合學術因子的新興市場策略。",
  FNDF: "Schwab基本面國際大型股ETF，以基本面加權的國際股票。",
  CGGR: "Capital Group成長ETF，主動管理的美國成長股。",
  VBK: "Vanguard小型成長股ETF。",
  USMV: "iShares MSCI美國最小波動ETF，篩選波動率最低的美股。",
  VOE: "Vanguard中型價值股ETF。",
  SCHA: "Schwab美國小型股ETF，費用率極低。",
  DFUS: "Dimensional美國股票ETF，結合學術因子模型。",
  IWP: "iShares羅素中型成長股ETF。",
  DFIV: "Dimensional國際價值股ETF。",
  VONV: "Vanguard羅素1000價值股ETF。",
  IWV: "iShares羅素3000 ETF，涵蓋美國全市場約3000檔股票。",
  AVDV: "Avantis國際小型價值股ETF。",
  VOT: "Vanguard中型成長股ETF。",
  SPMO: "Invesco S&P 500動能ETF，投資近期表現最強的S&P 500成分股。",
  SPHQ: "Invesco S&P 500品質ETF，篩選財務品質最高的企業。",
  SPMD: "SPDR S&P 400中型股ETF。",
  ESGU: "iShares ESG感知MSCI美國ETF，整合ESG因子的美股。",
  IWY: "iShares羅素Top 200成長股ETF，投資美國最大型成長企業。",
  IDEV: "iShares核心MSCI國際已開發市場ETF。",
  EFV: "iShares MSCI歐澳遠東價值股ETF。",
  EMXC: "iShares MSCI新興市場（不含中國）ETF。",
  VYMI: "Vanguard國際高殖利率ETF，投資美國以外高配息股票。",
  SPEM: "SPDR新興市場ETF。",
  BBJP: "JPMorgan BetaBuilders日本ETF，低成本投資日本股市。",
  GOVT: "iShares美國公債ETF，涵蓋各天期美國國債。",
  VGIT: "Vanguard中期美國公債ETF，投資3-10年期公債。",
  MBB: "iShares房貸抵押債券ETF，投資美國政府擔保的MBS。",
  JPST: "JPMorgan超短期收入ETF，主動管理的超短天期債券。",
  IUSB: "iShares核心總體美元債券ETF。",
  VGSH: "Vanguard短期美國公債ETF，投資1-3年期公債。",
  BIV: "Vanguard中期債券ETF，涵蓋公債與公司債。",
  FBND: "Fidelity總體債券ETF，主動管理的核心債券。",
  IGSB: "iShares 1-5年投資等級公司債ETF。",
  SHV: "iShares短期美國國庫券ETF，近似現金。",
  VTIP: "Vanguard短期抗通膨公債ETF，對抗通貨膨脹。",
  IEI: "iShares 3-7年美國公債ETF。",
  IGIB: "iShares 5-10年投資等級公司債ETF。",
  BINC: "PIMCO靈活收入主動型ETF，主動管理多元債券策略。",
  USIG: "iShares美國投資等級公司債ETF。",
  USFR: "WisdomTree浮動利率公債ETF，利率上升時受益。",
  PPLT: "abrdn實體鉑金ETF，持有實體鉑金。",
  IYW: "iShares美國科技ETF。",
  XLU: "SPDR公用事業ETF，投資電力、天然氣等公用事業股。",
  XLY: "SPDR非必需消費ETF，持有AMZN、TSLA等消費類股。",
  FTEC: "Fidelity MSCI資訊科技ETF，費用率極低的科技板塊。",
  // 槓桿/反向
  TQQQ: "ProShares NASDAQ-100三倍做多ETF，追蹤NASDAQ-100單日正向3倍報酬，僅適合短線交易。",
  SOXL: "Direxion半導體三倍做多ETF，追蹤半導體指數單日正向3倍報酬。",
  // 其他
  VNQ: "Vanguard房地產ETF，投資美國REITs不動產投資信託。",
};
const TW_ETF_DESC = {
  "0050.TW": "追蹤臺灣50指數，持有台股市值前50大企業，是最具代表性的台灣大盤ETF。",
  "006208.TW": "追蹤臺灣50指數，與0050相同標的但管理費較低，適合長期定期定額。",
  "0051.TW": "追蹤臺灣中型100指數，投資市值排名51-150的中型股。",
  "006204.TW": "追蹤臺灣加權股價指數，涵蓋全市場上市股票。",
  "006203.TW": "追蹤MSCI臺灣指數，依國際標準篩選台灣大中型股。",
  "00922.TW": "追蹤臺灣領袖50指數，精選50檔具領導地位的台股。",
  "00692.TW": "追蹤公司治理100指數，篩選治理評鑑優良的上市公司。",
  "0056.TW": "從台灣50與中型100中挑選未來一年預測殖利率最高的30檔股票。",
  "00878.TW": "篩選ESG評級優良且具高殖利率的台股，季配息。",
  "00929.TW": "聚焦台灣科技股中殖利率較高者，月配息。",
  "00919.TW": "精選台灣高息股票，結合獲利成長與殖利率雙因子，月配息。",
  "00713.TW": "篩選高殖利率且低波動的台股，適合穩健型投資人。",
  "00881.TW": "投資台灣5G相關供應鏈企業，涵蓋晶片、設備、電信等。",
  "00891.TW": "聚焦台灣半導體產業鏈的關鍵企業。",
  "00892.TW": "投資台灣半導體產業，以市值加權配置。",
  "00927.TW": "結合半導體產業與收益策略的ETF。",
  "00830.TW": "追蹤費城半導體指數，投資全球頂尖半導體公司。",
  "00646.TW": "追蹤S&P500指數，一檔投資美國500大企業。",
  "00757.TW": "追蹤NYSE FANG+指數，投資美國科技巨頭如META、AAPL、NVDA等。",
  "00662.TW": "追蹤NASDAQ-100指數，投資美國科技成長股。",
  "00668.TW": "追蹤道瓊工業平均指數，投資美國30大藍籌股。",
  "00850.TW": "投資通過ESG永續評鑑的台灣企業。",
  "00896.TW": "投資綠能、電動車及相關供應鏈企業。",
  "00893.TW": "投資全球智能電動車產業鏈。",
  "00876.TW": "投資全球未來通訊技術相關企業。",
  "00861.TW": "投資全球未來關鍵科技領域。",
  "00895.TW": "投資全球未來車產業鏈企業。",
  "00875.TW": "投資全球網路資安產業相關企業。",
  "00851.TW": "投資全球AI人工智慧相關企業。",
  "00631L.TW": "追蹤臺灣50指數單日正向2倍報酬，適合短線看多操作。",
  "00632R.TW": "追蹤臺灣50指數單日反向1倍報酬，適合短線避險。",
  "00663L.TW": "追蹤臺灣加權指數單日正向2倍報酬。",
  "00675L.TW": "追蹤臺灣加權指數單日正向2倍報酬。",
  "00685L.TW": "追蹤臺灣加權指數單日正向2倍報酬。",
  "00670L.TW": "追蹤NASDAQ-100指數單日正向2倍報酬。",
  "00647L.TW": "追蹤S&P500指數單日正向2倍報酬。",
  "00637L.TW": "追蹤滬深300指數單日正向2倍報酬。",
  "00680L.TW": "追蹤美國20年期以上公債單日正向2倍報酬。",
  "00910.TW": "投資全球太空衛星產業相關企業。",
  "00920.TW": "投資全球5G通訊相關企業。",
  "00862.TW": "投資越南市場機會型股票。",
  "00980A.TW": "主動式管理，由基金經理人擇股，聚焦台灣優質企業。",
  "00981A.TW": "主動式管理，追求台股長期增長機會。",
  "00982A.TW": "主動式管理，精選台灣強勢股票。",
  "00983A.TW": "主動式管理，投資ARK創新主題相關企業。",
  "00984A.TW": "主動式管理，聚焦台灣高息成長型股票。",
  "00985A.TW": "主動式管理，以臺灣50指數為基礎增強報酬。",
  // 台股大盤補充
  "009816.TW": "追蹤臺灣TOP50指數，投資台股市值前50大企業。",
  "00712.TW": "追蹤富時台灣不動產指數，投資台灣REITs與不動產相關股票。",
  "00923.TW": "追蹤ESG低碳50指數，篩選台灣低碳排且ESG表現佳的企業。",
  "009820.TW": "追蹤納斯達克精選指數，精選台股科技龍頭。",
  "00888.TWO": "追蹤台灣ESG永續指數，投資ESG評鑑優良企業。",
  "009802.TW": "追蹤臺灣50指數，富邦旗艦級台股大盤ETF。",
  "00905.TW": "追蹤FT臺灣Smart指數，結合多因子策略選股。",
  "00690.TW": "追蹤藍籌30指數，投資台股30檔大型藍籌股。",
  "00701.TW": "追蹤台灣股利精選30指數，篩選配息穩定的30檔股票。",
  "00733.TW": "投資台灣中小型股，追求中小企業成長機會。",
  "009813.TW": "追蹤標普台灣卓越50指數。",
  "00625K.TW": "追蹤上證180指數（人民幣計價），投資中國A股。",
  "006205.TW": "追蹤上證180指數（新臺幣計價），投資中國A股。",
  // 高股息補充
  "00918.TW": "篩選填息機率高的30檔台股，追求高填息率，季配息。",
  "00940.TW": "篩選台灣價值型高息股，結合價值與殖利率因子。",
  "00900.TW": "篩選台灣殖利率最高的30檔股票。",
  "00882.TW": "投資中國高股息企業，追求人民幣計價的股息收入。",
  "00939.TW": "結合動能與高息因子，篩選台灣高息動能股。",
  "00915.TW": "篩選殖利率較高的30檔優質台股，季配息。",
  "00934.TW": "篩選兼具成長性與高殖利率的台灣企業。",
  "00946.TW": "結合科技與高息成長策略的台股ETF。",
  "00936.TW": "投資台灣永續高息中小型股。",
  // 科技/半導體補充
  "0052.TW": "追蹤臺灣資訊科技指數，投資台灣科技產業。",
  "00935.TW": "追蹤臺灣創新科技50指數，投資50檔創新科技企業。",
  "00965.TW": "投資全球航太防衛科技相關企業。",
  "00941.TW": "聚焦台灣半導體上游產業鏈企業。",
  "00735.TW": "投資台灣與韓國科技產業。",
  "00894.TW": "投資台灣高價股中的小資族精選30檔。",
  "00947.TW": "聚焦台灣IC設計產業鏈企業。",
  "00904.TW": "投資台灣半導體前30大企業。",
  "00909.TW": "投資數位支付與金融科技相關企業。",
  "009819.TW": "投資數據中心與電力基礎設施相關企業。",
  "00770.TW": "投資北美科技產業龍頭企業。",
  "00762.TW": "投資全球AI人工智慧產業鏈。",
  "00952.TW": "投資台灣AI相關前50大企業。",
  // 海外市場補充
  "00924.TW": "追蹤S&P 500成長股指數，投資美國成長型企業。",
  "00885.TW": "投資越南股市，追蹤越南主要上市企業。",
  "009805.TW": "投資美國電力基礎建設相關企業。",
  "00752.TW": "追蹤中國50指數，投資中國前50大企業。",
  "00949.TW": "投資日本龍頭企業。",
  "009800.TW": "追蹤NASDAQ指數，投資美國科技成長股。",
  // 主題型補充
  "00902.TW": "投資電池、儲能及相關技術企業。",
  "00897.TW": "投資全球基因免疫與生技產業。",
  "00901.TW": "投資智能車供應鏈企業。",
  "00916.TW": "投資全球50大品牌價值企業。",
  "00920.TW": "投資全球5G通訊基礎建設與應用企業。",
  "00757U.TW": "追蹤FANG+指數單日正向2倍報酬。",
  // 商品
  "00738U.TW": "追蹤道瓊白銀指數，投資白銀期貨。",
  "00635U.TW": "追蹤S&P黃金指數，投資黃金期貨。",
  "00708L.TW": "追蹤S&P黃金指數單日正向2倍報酬。",
  // 債券
  "00937B.TWO": "投資ESG投資等級債券，20年以上天期。",
  "00679B.TWO": "投資美國20年期以上公債，長天期美債代表。",
  "00751B.TWO": "投資AAA至A評級公司債。",
  "00687B.TWO": "投資美國20年期以上公債。",
  "00720B.TWO": "投資美元計價投資級公司債。",
  "00725B.TWO": "投資美元計價投資級公司債。",
  "00772B.TWO": "投資高評級公司債券。",
  "00933B.TWO": "投資10年期以上金融機構債券。",
  "00724B.TWO": "投資投資等級金融債券。",
  "00761B.TWO": "投資A級公司債券。",
  "00773B.TWO": "投資優先順位金融債券。",
  "00746B.TWO": "投資A級公司債券。",
  "00740B.TWO": "投資全球投資等級債券。",
  "00792B.TWO": "投資A級公司債券。",
  "00777B.TWO": "投資AAA至A評級公司債。",
  "00950B.TWO": "投資A級公司債券。",
  "00722B.TWO": "投資投資等級電信業債券。",
  "00778B.TWO": "投資20年期以上金融債券。",
  "00754B.TWO": "投資AAA至AA評級公司債。",
  "00723B.TWO": "投資投資等級科技業債券。",
  "00756B.TWO": "投資投資等級新興市場公債。",
  "00795B.TWO": "投資美國20年期以上公債。",
  "00764B.TWO": "投資25年期以上美國公債。",
  "00953B.TW": "投資非投資等級（高收益）債券。",
  "00948B.TWO": "投資優息投資級債券。",
  "00726B.TWO": "投資新興市場投資等級債券。",
  "00768B.TWO": "投資20年期以上美國公債。",
  "00785B.TWO": "投資金融機構投資等級債券。",
  "00779B.TWO": "投資25年期以上美國公債。",
  "00749B.TWO": "投資10年期以上新興市場債券。",
  "00942B.TWO": "投資美國A級20年以上公司債。",
  "00931B.TWO": "投資美國20年期以上公債。",
  "00862B.TWO": "投資投資級公司債券。",
  "00760B.TWO": "投資新興市場企業債券。",
  "00789B.TWO": "投資A3評級公司債券。",
  "00696B.TWO": "投資美國20年期以上公債。",
  "00863B.TWO": "投資全球電信業債券。",
  "00849B.TWO": "投資新興市場0-5年期主權債。",
  "00836B.TWO": "投資10年期以上A級公司債。",
  "00719B.TWO": "投資美國1-3年期公債。",
  "00867B.TWO": "投資A至BBB級電信業債券。",
  "00870B.TWO": "投資15年期以上新興市場主權債。",
  "00791B.TWO": "投資1-5年期信用債券。",
  "00711B.TW": "投資彭博新興市場債券指數。",
  "00857B.TWO": "投資20年期以上美國公債。",
  "00981B.TWO": "投資非投資等級債券。",
  "00945B.TW": "投資美國非投資等級債券。",
  "00865B.TW": "投資美國短期公債。",
  "00775B.TW": "投資15年期以上投資等級債券。",
  "00985B.TW": "投資ESG投資等級0-5年短期債券。",
  // 槓桿/反向補充
  "00673R.TW": "追蹤S&P原油指數單日反向1倍報酬。",
  "00708L.TW": "追蹤S&P黃金指數單日正向2倍報酬。",
  "00633L.TW": "追蹤上證180指數單日正向2倍報酬。",
  "00753L.TW": "追蹤中國50指數單日正向2倍報酬。",
  "00715L.TW": "追蹤布蘭特原油指數單日正向2倍報酬。",
  "00688L.TW": "追蹤美國20年期公債單日正向2倍報酬。",
  "00665L.TW": "追蹤恒生國企指數單日正向2倍報酬。",
  // 主動型補充
  "00403A.TW": "主動式管理，追求台灣升級轉型企業投資機會。",
  "00992A.TW": "主動式管理，聚焦科技創新主題。",
  "00991A.TW": "主動式管理，投資台灣未來50強潛力企業。",
  "00988A.TW": "主動式管理，投資全球創新企業。",
  "00990A.TW": "主動式管理，聚焦AI新經濟主題。",
  "00400A.TW": "主動式管理，追求動能與高息雙因子。",
  "00999A.TW": "主動式管理，聚焦台灣高息股票。",
  "00997A.TW": "主動式管理，投資美國成長型企業。",
  "00993A.TW": "主動式管理，聚焦台灣優質企業。",
  "00995A.TW": "主動式管理，投資台灣卓越企業。",
  "00994A.TW": "主動式管理，篩選台股優質標的。",
  "00996A.TW": "主動式管理，追求台灣豐收型投資。",
  "00405A.TW": "主動式管理，聚焦台灣龍耀型成長企業。",
  "00402A.TW": "主動式管理，投資美國科技產業龍頭。",
  "00406A.TW": "主動式管理，追求台灣收益型投資機會。",
  "00998A.TW": "主動式管理，聚焦金融產業高股息標的。",
  "00401A.TW": "主動式管理，追求台灣鑫收型投資機會。",
  "00987A.TW": "主動式管理，篩選台灣優勢成長企業。",
  "00404A.TW": "主動式管理，追蹤動能因子前50強企業。",
  "00983A.TW": "主動式管理，投資ARK創新主題相關企業。",
  "00989A.TW": "主動式管理，投資美國科技產業。",
  "00986A.TW": "主動式管理，聚焦台灣龍頭成長型企業。",
};
const TW_STOCK_NAMES = {
  "2330": "台積電", "2317": "鴻海", "2454": "聯發科", "2308": "台達電",
  "2881": "富邦金", "2882": "國泰金", "2891": "中信金", "2886": "兆豐金",
  "2884": "玉山金", "2412": "中華電", "3711": "日月光投控", "1301": "台塑",
  "1303": "南亞", "2303": "聯電", "2002": "中鋼", "1216": "統一",
  "2912": "統一超", "5880": "合庫金", "2892": "第一金", "5871": "中租-KY",
  "2887": "台新金", "2885": "元大金", "3045": "台灣大", "2357": "華碩",
  "2382": "廣達", "2395": "研華", "3008": "大立光", "2327": "國巨",
  "3037": "欣興", "2379": "瑞昱", "6505": "台塑化", "1326": "台化",
  "2880": "華南金", "2883": "開發金", "2301": "光寶科", "4904": "遠傳",
  "2890": "永豐金", "3034": "聯詠", "2345": "智邦", "4938": "和碩",
  "2603": "長榮", "6669": "緯穎", "3443": "創意", "3661": "世芯-KY",
  "2356": "英業達", "3231": "緯創", "2353": "宏碁", "1101": "台泥",
  "1102": "亞泥", "2207": "和泰車", "9910": "豐泰", "2105": "正新",
  "8046": "南電", "3529": "力旺", "6488": "環球晶", "2474": "可成",
  "3017": "奇鋐", "6409": "旭隼", "2347": "聯強", "6770": "力積電",
  "3702": "大聯大", "2615": "萬海", "2609": "陽明", "8464": "億豐",
  "2377": "微星", "5269": "祥碩", "3533": "嘉澤", "6239": "力成",
  "2324": "仁寶", "4958": "臻鼎-KY", "2542": "興富發", "2408": "南亞科",
  "3044": "健鼎", "2344": "華邦電", "3189": "景碩", "2049": "上銀",
  "5347": "世界", "3514": "昱晶", "6547": "高端疫苗", "2618": "長榮航",
  "2610": "華航", "9904": "寶成", "1590": "亞德客-KY", "2383": "台光電",
  "6446": "藥華藥", "3665": "貿聯-KY", "6592": "和潤企業",
  "2888": "新光金", "2801": "彰銀",
};
function getTwStockName(symbol) {
  const ticker = String(symbol || "").replace(/\.(TW|TWO)$/i, "").replace(/-(TW|TWO)$/i, "");
  return TW_STOCK_NAMES[ticker] || null;
}
const MARKET_CONFIG = {
  US: { label: "美股市場", currencySymbol: "$", cats: US_ETF_CATS, themes: US_ETF_THEMES, names: {} },
  TW: { label: "台股市場", currencySymbol: "NT$", cats: TW_ETF_CATS, themes: TW_ETF_THEMES, names: TW_ETF_NAMES },
};
const MARKET_BUTTON_LABELS = { US: "美股", TW: "台股" };
function displaySymbol(sym) {
  return sym.replace(/\.(TW|TWO)$/i, "");
}

function cleanSentence(value) {
  return String(value || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function formatMaybePct(value) {
  return value == null || Number.isNaN(Number(value)) ? null : `${(Number(value) * 100).toFixed(2)}%`;
}

function inferEtfAssetType(symbol, category, theme, description) {
  const text = `${symbol} ${category || ""} ${theme || ""} ${description || ""}`;
  if (/債|bond|treasury|公債|公司債|投資級|高收益/i.test(text)) return "債券型";
  if (/黃金|白銀|原油|商品|gold|silver|oil|commodity/i.test(text)) return "商品型";
  if (/L\.|R\.|正2|反1|槓桿|反向|leveraged|inverse|TQQQ|SQQQ|UPRO|SPXU/i.test(text)) return "槓桿或反向型";
  if (/高股息|股息|配息|dividend|income/i.test(text)) return "收益型股票";
  return "股票型";
}

function buildBeginnerEtfDescription({ symbol, name, baseDesc, category, theme, family, holdings, sectors, expRatio, yld, beta, isTw }) {
  const marketLabel = isTw ? "台灣掛牌" : "美國掛牌";
  const shortSymbol = displaySymbol(symbol);
  const assetType = inferEtfAssetType(symbol, category, theme, baseDesc);
  const titleName = cleanSentence(name || shortSymbol);
  const seed = cleanSentence(baseDesc);
  const issuer = cleanSentence(family);
  const holdingsText = (holdings || [])
    .slice(0, 5)
    .map((h) => cleanSentence(h.holdingName || h.symbol))
    .filter(Boolean)
    .join("、");
  const sectorText = (sectors || [])
    .slice(0, 3)
    .map((s) => cleanSentence(s.sector || s.name))
    .filter(Boolean)
    .join("、");
  const costText = formatMaybePct(expRatio);
  const yieldText = formatMaybePct(yld);
  const betaText = beta == null || Number.isNaN(Number(beta)) ? null : Number(beta).toFixed(2);
  const isLeveraged = assetType === "槓桿或反向型";

  const parts = [
    `${shortSymbol}（${titleName}）是一檔${marketLabel}的${assetType} ETF，${seed || "主要透過一籃子證券，讓投資人用單一標的參與特定市場、產業或投資策略。"}`,
    `它的重點在於把多個標的放在同一個基金裡，讓投資人不用逐一買進每家公司或資產，也能取得一個較完整的市場部位。`,
  ];
  if (issuer) parts.push(`發行或管理機構為 ${issuer}，相關的公開說明書、持股明細、配息與費用資料，仍以發行商最新公告為準。`);
  if (category || theme) parts.push(`在 ETFowl 中，它目前歸類為「${category || "未分類"}」${theme ? `，主題標記為「${theme}」` : ""}，可用來觀察這個市場區塊的資金流向、產業輪動與中長期表現。`);
  if (holdingsText) parts.push(`目前可取得的主要成分包含 ${holdingsText}，這些持股或核心資產的表現，通常會直接影響 ETF 的價格走勢。`);
  if (sectorText) parts.push(`產業配置較集中在 ${sectorText} 等區塊；如果單一產業占比偏高，報酬來源會比較清楚，但價格波動也可能比全市場型 ETF 更明顯。`);
  parts.push(`費用率${costText ? `約為 ${costText}` : "需以最新公開資料為準"}，殖利率${yieldText ? `約為 ${yieldText}` : "會隨配息與價格變動"}，Beta(3Y)${betaText ? `約為 ${betaText}` : "若資料不足則不宜過度解讀"}。這些數字可用來快速掌握持有成本、配息特性與相對波動。`);
  if (isLeveraged) parts.push(`這類槓桿或反向 ETF 通常追求單日指數倍數報酬，較偏向短線交易或避險工具；持有時間拉長後，可能因每日再平衡而和原指數累積報酬出現明顯差距。`);
  parts.push(`整體來看，評估這檔 ETF 時可以同時看它追蹤的市場、前十大持股集中度、產業分布、費用率與近期價格趨勢。若持股高度集中，單一公司或少數產業的變化就會放大到基金表現；若分散度較高，走勢通常更接近整體市場或指數。`);

  let text = parts.join(" ");
  while (text.length < 200) {
    text += " 這檔 ETF 的觀察重點不只在單日漲跌，也包括它背後的資產組合、追蹤邏輯、成本結構與主要風險來源。";
  }
  return text;
}

const YAHOO_SUFFIXES = new Set(["SW", "PA", "L", "MI", "DE", "AS", "BR", "CO", "ST", "OL", "TO", "V", "T", "HK", "KS", "KQ", "TW", "TWO", "SI", "AX", "SZ", "SS"]);

const RETURN_PERIODS = [
  { id: "1d", label: "日", days: 1 },
  { id: "1w", label: "週", days: 7 },
  { id: "1m", label: "月", days: 30 },
  { id: "1y", label: "年", days: 365 },
  { id: "2y", label: "兩年", days: 365 * 2 },
  { id: "3y", label: "三年", days: 365 * 3 },
  { id: "5y", label: "五年", days: 365 * 5 },
];

const RETURN_PERIOD_MAP = Object.fromEntries(RETURN_PERIODS.map((p) => [p.id, p]));
const HOLDING_TREND_PERIODS = [
  { id: "1w", label: "週", days: 7 },
  { id: "1m", label: "月", days: 30 },
  { id: "1y", label: "年", days: 365 },
  { id: "2y", label: "兩年", days: 365 * 2 },
  { id: "3y", label: "三年", days: 365 * 3 },
];
const DAILY_SPARK_POINTS = 3;
const MAX_HOLDING_TRENDS = 150;
const HOLDING_SEARCH_PAGE_SIZE = 10;
const TAIPEI_DATE_TIME = new Intl.DateTimeFormat("zh-TW", {
  timeZone: "Asia/Taipei",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const TAIPEI_DATE_KEY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Taipei",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function formatTaipeiTime(value) {
  if (!value) return "—";
  return TAIPEI_DATE_TIME.format(new Date(value));
}

function taipeiDateKey(value) {
  if (!value) return "";
  return TAIPEI_DATE_KEY.format(new Date(value));
}

// ETF 風險屬性分類（單一公式,清單與個股頁共用 → 完全一致）
// 規則：積極分類 +2、保守分類 −2、槓桿/反向 額外 +1；Beta>1.15 +1 / <0.85 −1；殖利率>3% −1
// Beta/殖利率為 null 時自動退化為純類別判定。
const RISK_META = {
  "積極": { color: "#d97706", icon: "🚀", note: "成長／上攻取向,波動較大" },
  "均衡": { color: "#2563eb", icon: "⚖️", note: "核心配置,攻守兼具" },
  "保守": { color: "#16a34a", icon: "🛡️", note: "穩健／收益取向,波動較低" },
};
const RISK_AGGR_CATS = ["產業板塊", "科技/半導體", "槓桿/反向", "主動型", "商品/另類", "商品"];
const RISK_CONS_CATS = ["債券/現金", "債券", "高股息", "收益/股息"];
// 台股主動式 ETF 發行商官方揭露頁(Yahoo 無持股,導去官方每日揭露)
const TW_ACTIVE_DISCLOSURE = {
  "00981A.TW": "https://www.ezmoney.com.tw/ETF/Fund/Info?fundCode=49YTW",
  "00403A.TW": "https://www.ezmoney.com.tw/ETF/Fund/Info?fundCode=63YTW",
  "00988A.TW": "https://www.ezmoney.com.tw/ETF/Fund/Info?fundCode=61YTW",
  "00982A.TW": "https://www.capitalfund.com.tw/etf/product/detail/399",
  "00992A.TW": "https://www.capitalfund.com.tw/etf/product/detail/500",
  "00997A.TW": "https://www.capitalfund.com.tw/etf/product/detail/502",
  "00991A.TW": "https://www.fhtrust.com.tw/ETF/etf_detail/ETF23",
  "00990A.TW": "https://www.yuantaetfs.com/product/detail/00990A/Basic_information",
  "00400A.TW": "https://www.cathaysite.com.tw/ETF/detail/EEA",
  "00980A.TW": "https://www.nomurafunds.com.tw/ETFWEB/product-description?fundNo=00980A",
  "00985A.TW": "https://www.nomurafunds.com.tw/ETFWEB/product-description?fundNo=00985A",
  "00999A.TW": "https://www.nomurafunds.com.tw/ETFWEB/product-description?fundNo=00999A",
  "00993A.TW": "https://etf.allianzgi.com.tw/etf-info/E0002",
  "00984A.TW": "https://etf.allianzgi.com.tw/etf-info/E0001",
  "00995A.TW": "https://www.ctbcinvestments.com/Etf/00653201/Info",
  "00994A.TW": "https://www.fsitc.com.tw/FundDetail.aspx?ID=182",
  "00996A.TW": "https://www.megafunds.com.tw/MEGA/etf/etf_product.aspx?id=23",
  "00405A.TW": "https://www.fuh-hwa.com.tw/fund/etf/00405A",
  "00402A.TW": "https://etf.allianzgi.com.tw/etf-info/E0003",
  "00406A.TW": "https://www.ctbcinvestments.com/Etf/00653202/Info",
  "00998A.TW": "https://www.fhtrust.com.tw/ETF/etf_detail/ETF24",
  "00401A.TW": "https://www.jpmorganam.com.tw/etf/fund/00401A",
  "00987A.TW": "https://www.cathaysite.com.tw/ETF/detail/EEB",
  "00404A.TW": "https://www.ab-invest.tw/etf/00404A",
  "00983A.TW": "https://www.ctbcinvestments.com/Etf/00653204/Info",
  "00989A.TW": "https://www.ezmoney.com.tw/ETF/Fund/Info?fundCode=62YTW",
  "00986A.TW": "https://www.taishinam.com.tw/etf/00986A",
};

// 該 ETF 在 ETFowl 站內主頁的深連結(?symbol=XXX,新頁載入時由 mount effect 讀取開 Detail)
function etfHomeUrl(symbol) {
  if (!symbol) return null;
  let s = String(symbol).toUpperCase();
  if (!/\.(TW|TWO)$/.test(s)) s += ".TW";
  const base = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";
  return `${base}?symbol=${encodeURIComponent(s)}`;
}
// 股數變動格式化:桌機顯示完整整數,手機(compact)對 |n|≥1萬改「萬股」簡記,避免長數字折行。
function fmtSharesDelta(n, compact) {
  if (n == null) return "";
  const sign = n > 0 ? "+" : "";
  if (compact && Math.abs(n) >= 10000) return `${sign}${(Math.round((n / 10000) * 10) / 10).toLocaleString()}萬股`;
  return `${sign}${Number(n).toLocaleString()} 股`;
}
// ETF 代號超連結:開新頁到該 ETF 的 ETFowl 主頁。stopPropagation 避免觸發外層可點列。
function EtfLink({ symbol, C, style, children }) {
  const url = etfHomeUrl(symbol);
  const label = children ?? displaySymbol(symbol);
  if (!url) return <span style={style}>{label}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={`在 ETFowl 開啟 ${displaySymbol(symbol)} 主頁`}
      onClick={(e) => e.stopPropagation()}
      style={{ textDecoration: "none", cursor: "pointer", borderBottom: `1px dotted ${(C && C.t3) || "currentColor"}`, ...style }}>{label}</a>
  );
}

function computeRiskProfile(category, beta, yld) {
  let score = 0;
  if (RISK_AGGR_CATS.includes(category)) score += 2;
  if (RISK_CONS_CATS.includes(category)) score -= 2;
  if (category === "槓桿/反向") score += 1;
  if (beta != null) { if (beta > 1.15) score += 1; else if (beta < 0.85) score -= 1; }
  if ((yld ?? 0) > 0.03) score -= 1;
  if (score >= 2) return "積極";
  if (score <= -2) return "保守";
  return "均衡";
}

function getPeriodReturn(points, currentPrice, period) {
  if (!points.length || !currentPrice) return null;

  if (period.id === "1d") {
    const prev = points[points.length - 2]?.close ?? points[points.length - 1]?.close;
    return prev ? ((currentPrice - prev) / prev) * 100 : null;
  }

  const target = Date.now() - period.days * 24 * 60 * 60 * 1000;
  let base = points[0];
  for (const point of points) {
    if (point.time <= target) base = point;
    else break;
  }

  return base?.close ? ((currentPrice - base.close) / base.close) * 100 : null;
}

function getPeriodReturnFromTime(points, currentPrice, period, asOfTime) {
  if (!points.length || !currentPrice) return null;

  const target = asOfTime - period.days * 24 * 60 * 60 * 1000;
  let base = points[0];
  for (const point of points) {
    if (point.time <= target) base = point;
    else break;
  }

  return base?.close ? ((currentPrice - base.close) / base.close) * 100 : null;
}

function normalizeHoldingSymbol(symbol) {
  return String(symbol || "")
    .trim()
    .toUpperCase()
    .replace(".", "-");
}

function toYahooSymbol(symbol) {
  const normalized = normalizeHoldingSymbol(symbol);
  if (!normalized || normalized === "--") return "";
  const parts = normalized.split("-");
  const suffix = parts[parts.length - 1];
  if (parts.length > 1 && YAHOO_SUFFIXES.has(suffix)) {
    return `${parts.slice(0, -1).join("-")}.${suffix}`;
  }
  return normalized;
}

function getHoldingQuoteUrl(symbol) {
  const yahooSymbol = toYahooSymbol(symbol);
  if (!yahooSymbol || !/^[A-Z0-9.-]{1,16}$/.test(yahooSymbol)) return null;
  return `https://finance.yahoo.com/quote/${encodeURIComponent(yahooSymbol)}`;
}

function openQuotePage(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function parseHoldingTrend(chart) {
  if (!chart) return null;
  const closes = chart.indicators?.quote?.[0]?.close || [];
  const timestamps = chart.timestamp || [];
  const points = timestamps
    .map((t, i) => ({ time: t * 1000, close: closes[i] }))
    .filter((p) => p.close != null);
  const currentPrice = chart.meta?.regularMarketPrice || points[points.length - 1]?.close;
  const asOfTime = (chart.meta?.regularMarketTime ? chart.meta.regularMarketTime * 1000 : null) || points[points.length - 1]?.time;
  if (!points.length || !currentPrice) return null;
  const returns = Object.fromEntries(
    HOLDING_TREND_PERIODS.map((period) => [period.id, getPeriodReturnFromTime(points, currentPrice, period, asOfTime)])
  );
  returns["1d"] = getPeriodReturn(points, currentPrice, { id: "1d" });

  return {
    price: currentPrice,
    currency: chart.meta?.currency,
    lastCloseTime: asOfTime,
    closes: points.map((p) => p.close).slice(-26),
    returns,
  };
}

// ── Parse chart data into a usable quote object ──
function parseChart(chart) {
  if (!chart) return null;
  const m = chart.meta;
  const closes = chart.indicators?.quote?.[0]?.close || [];
  const volumes = chart.indicators?.quote?.[0]?.volume || [];
  const timestamps = chart.timestamp || [];
  const points = timestamps
    .map((t, i) => ({ time: t * 1000, close: closes[i], volume: volumes[i] }))
    .filter((p) => p.close != null);
  const validCloses = points.map((p) => p.close);
  const metaPrevClose = m.previousClose || m.chartPreviousClose;
  const prevClose = metaPrevClose || validCloses[validCloses.length - 2] || validCloses[0] || m.regularMarketPrice;
  const price = m.regularMarketPrice;
  const change = price - prevClose;
  const changePct = prevClose ? (change / prevClose) * 100 : 0;
  const lastCloseTime = (m.regularMarketTime ? m.regularMarketTime * 1000 : null) || points[points.length - 1]?.time || null;
  const returns = Object.fromEntries(
    RETURN_PERIODS.map((period) => [period.id, getPeriodReturn(points, price, period)])
  );
  returns["1d"] = changePct;

  return {
    symbol: m.symbol,
    name: m.longName || m.shortName || m.symbol,
    price,
    navPrice: getRawNumber(m.navPrice, m.nav, m.netAssetValue),
    prevClose,
    change,
    changePct,
    returns,
    dayHigh: m.regularMarketDayHigh,
    dayLow: m.regularMarketDayLow,
    high52: m.fiftyTwoWeekHigh,
    low52: m.fiftyTwoWeekLow,
    volume: m.regularMarketVolume,
    currency: m.currency,
    exchange: m.fullExchangeName || m.exchangeName,
    lastCloseTime,
    points,
    closes: validCloses.slice(-60),
    volumes: points.map((p) => p.volume).filter((v) => v != null).slice(-60),
  };
}

// 單日漲跌超過 40% 視為分割/合併異常，不顯示
const ABNORMAL_RETURN_THRESHOLD = 40;
function isAbnormalReturn(value) { return Number.isFinite(value) && Math.abs(value) > ABNORMAL_RETURN_THRESHOLD; }

function getReturnPct(quote, periodId = "1d") {
  const value = quote?.returns?.[periodId];
  if (value == null || isAbnormalReturn(value)) return 0;
  return value;
}

function hasUsablePrice(quote) {
  return Number.isFinite(quote?.price) && quote.price > 0;
}

function formatReturnPct(value) {
  if (value == null || isAbnormalReturn(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function getRawNumber(...values) {
  for (const value of values) {
    if (Number.isFinite(value)) return value;
    if (Number.isFinite(value?.raw)) return value.raw;
    const parsed = typeof value?.fmt === "string" ? Number(value.fmt.replace(/[$,%\s,]/g, "")) : NaN;
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function getEtfNavPrice(detail, quote) {
  return getRawNumber(
    quote?.navPrice,
    quote?.nav,
    detail?.summaryDetail?.navPrice,
    detail?.summaryDetail?.nav,
    detail?.price?.navPrice,
    detail?.price?.nav,
    detail?.defaultKeyStatistics?.navPrice
  );
}

function getPremiumDiscountPct(price, navPrice) {
  if (!Number.isFinite(price) || !Number.isFinite(navPrice) || navPrice <= 0) return null;
  return ((price - navPrice) / navPrice) * 100;
}

function getEtfNavInfo(detail, quote) {
  const official = detail?.officialNav;
  const officialNavPrice = getRawNumber(official?.navPrice);
  const yahooNavPrice = getEtfNavPrice(detail, quote);
  const navPrice = officialNavPrice ?? yahooNavPrice;
  const marketPrice = getRawNumber(official?.marketPrice, quote?.price);
  const premiumDiscountPct =
    getRawNumber(official?.premiumDiscountPct) ?? getPremiumDiscountPct(marketPrice, navPrice);
  return {
    navPrice,
    premiumDiscountPct,
    sourceLabel: officialNavPrice != null ? official?.sourceLabel || "Official NAV" : yahooNavPrice != null ? "Yahoo Finance" : null,
    asOf: official?.time || official?.date || official?.updatedAt || null,
    refUrl: official?.refUrl || detail?.officialNavRefUrl || null,
  };
}

function formatPremiumDiscount(value) {
  if (value == null) return "—";
  if (Math.abs(value) < 0.005) return "平價 0.00%";
  return `${value > 0 ? "溢價 +" : "折價 "}${value.toFixed(2)}%`;
}

function getMarketPremiumInfo(symbol, quote, premiumBySymbol = {}) {
  const stored = premiumBySymbol?.[symbol] || {};
  const navPrice = getRawNumber(stored.navPrice, quote?.navPrice, quote?.nav);
  const marketPrice = getRawNumber(quote?.price, stored.marketPrice);
  const calculatedPremium = getPremiumDiscountPct(marketPrice, navPrice);
  const premiumDiscountPct = calculatedPremium ?? getRawNumber(stored.premiumDiscountPct);
  return {
    navPrice,
    marketPrice,
    premiumDiscountPct,
    sourceLabel: stored.sourceLabel || (navPrice != null ? "NAV" : null),
    asOf: stored.asOf || null,
  };
}

function formatPremiumShort(value) {
  if (value == null) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function sampleSparkValues(values, max = 120) {
  if (values.length <= max) return values;
  const sampled = [];
  for (let i = 0; i < max; i++) {
    sampled.push(values[Math.round((i / (max - 1)) * (values.length - 1))]);
  }
  return sampled;
}

function getSparkCloses(quote, periodId) {
  const points = quote?.points || [];
  if (points.length < 2) return quote?.closes || [];
  const period = RETURN_PERIOD_MAP[periodId];
  if (!period) return sampleSparkValues(points.map((p) => p.close));
  if (period.id === "1d") return points.slice(-DAILY_SPARK_POINTS).map((p) => p.close);
  const end = quote?.lastCloseTime || points[points.length - 1]?.time || Date.now();
  const start = end - period.days * 24 * 60 * 60 * 1000;
  const windowPoints = points.filter((p) => p.time >= start);
  const selected = windowPoints.length >= 2 ? windowPoints : points.slice(-2);
  return sampleSparkValues(selected.map((p) => p.close));
}

function getSparkRangeLabel(period) {
  return period?.id === "1d" ? "近3日" : period?.label || "目前區間";
}

// ── Mini sparkline ──
function Spark({ data, w = 72, h = 24, color }) {
  if (!data || data.length < 2) return <div style={{ width: w, height: h }} />;
  const mn = Math.min(...data), mx = Math.max(...data), rng = mx - mn || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - 2 - ((v - mn) / rng) * (h - 4)}`).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block", flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── Color helpers ──
const THEMES = {
  dark: {
    bg: "#060b16",
    panel: "#0b1220",
    card: "#101a2c",
    border: "#1c2942",
    accent: "#5aacff",
    accentDim: "rgba(90,172,255,0.14)",
    warm: "#fbbf24",
    red: "#f43f5e",
    green: "#34d399",
    up: "#f43f5e",
    down: "#34d399",
    t1: "#eaf1fb",
    t2: "#a9bce0",
    t3: "#6f83ad",
    tooltip: "#16233b",
    shadow: "rgba(0,0,0,0.38)",
    chartLine: "#5aacff",
    chartGrid: "#1c2942",
    chartLabelBg: "#050a14",
    chartLabelText: "#eaf1fb",
    inputBg: "#101a2c",
    cardShadow: "none",
    headerBg: "rgba(9,16,30,0.72)",
    headerBorder: "#1f3f7455",
    segTrack: "rgba(120,140,180,0.18)",
    segActive: "#22314c",
    segShadow: "0 1px 3px rgba(0,0,0,0.4), 0 1px 1px rgba(0,0,0,0.25)",
    // ── Azure Aurora tokens ──
    cyan: "#54e0da",
    indigo: "#6f8dff",
    sky: "#8ec9ff",
    glass: "rgba(140,185,255,0.06)",
    glassBrd: "rgba(140,185,255,0.14)",
    aurora1: "#2f8bff",
    aurora2: "#18c6d8",
    aurora3: "#6f8dff",
    auroraOpacity: 0.5,
    grainOpacity: 0.045,
    heroInk: "#8ec9ff",
  },
  light: {
    bg: "#F5F9FF",
    panel: "#EAF1FB",
    card: "#ffffff",
    border: "#D5E3F5",
    accent: "#2A6BE0",
    accentDim: "rgba(42,107,224,0.10)",
    warm: "#B45309",
    red: "#C81E4A",
    green: "#1F7A4C",
    up: "#C81E4A",
    down: "#1F7A4C",
    t1: "#0F2340",
    t2: "#42506A",
    t3: "#5E6C85",
    tooltip: "#ffffff",
    shadow: "rgba(16,40,80,0.12)",
    chartLine: "#2A6BE0",
    chartGrid: "#D5E3F5",
    chartLabelBg: "#ffffff",
    chartLabelText: "#0F2340",
    inputBg: "#F5F9FF",
    cardShadow: "0 1px 4px rgba(16,40,80,0.08)",
    headerBg: "rgba(240,246,255,0.78)",
    headerBorder: "#C6DAF3",
    segTrack: "rgba(90,130,200,0.12)",
    segActive: "#ffffff",
    segShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.04)",
    // ── Azure Aurora tokens (light) ──
    cyan: "#0E9AAE",
    indigo: "#4F63E6",
    sky: "#2A6BE0",
    glass: "rgba(42,107,224,0.05)",
    glassBrd: "rgba(42,107,224,0.16)",
    aurora1: "#a9ccff",
    aurora2: "#b6ecf2",
    aurora3: "#c8d0ff",
    auroraOpacity: 0.55,
    grainOpacity: 0.03,
    heroInk: "#2A6BE0",
  },
};

function getInitialTheme() {
  try { return localStorage.getItem("etfowl:theme") || "dark"; } catch { return "dark"; }
}

// Placeholder — replaced by component-level `C` via useTheme
let C = THEMES.dark;

// 台股紅漲綠跌，美股相反（綠漲紅跌）；market/symbol 判斷市場，value 為漲跌幅或報酬率
const isTwSymbol = (symbol) => typeof symbol === "string" && (symbol.endsWith(".TW") || symbol.endsWith(".TWO"));
const dirColor = (value, isTw) => {
  if (value == null) return C.t3;
  const positive = value >= 0;
  return isTw ? (positive ? C.up : C.down) : (positive ? C.down : C.up);
};

// 讓 onClick 卡片可用鍵盤 Enter/Space 觸發，搭配 role="button" tabIndex={0}
const onActivateKey = (fn) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn();
  }
};

function buildOhlcPoints(chart) {
  const q = chart?.indicators?.quote?.[0] || {};
  const timestamps = chart?.timestamp || [];
  const points = [];
  for (let i = 0; i < timestamps.length; i++) {
    const close = q.close?.[i];
    if (close == null) continue;
    const prevClose = points[points.length - 1]?.close ?? close;
    const open = q.open?.[i] ?? prevClose;
    const high = q.high?.[i] ?? Math.max(open, close);
    const low = q.low?.[i] ?? Math.min(open, close);
    points.push({
      time: timestamps[i] * 1000,
      open,
      high,
      low,
      close,
      volume: q.volume?.[i],
      isSyntheticOhlc: q.open?.[i] == null || q.high?.[i] == null || q.low?.[i] == null,
    });
  }
  return points;
}

function movingAverage(points, period) {
  return points.map((point, i) => {
    if (i + 1 < period) return null;
    const slice = points.slice(i + 1 - period, i + 1);
    const value = slice.reduce((sum, p) => sum + p.close, 0) / period;
    return { ...point, value, index: i };
  });
}

function aggregateOhlc(dailyPoints, mode) {
  if (!dailyPoints.length || mode === "daily") return dailyPoints;
  const groups = new Map();
  for (const p of dailyPoints) {
    const d = new Date(p.time);
    let key;
    if (mode === "weekly") {
      const day = d.getDay() || 7;
      const mon = new Date(d);
      mon.setDate(d.getDate() - day + 1);
      key = `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`;
    } else {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  }
  return [...groups.values()].map((bars) => ({
    time: bars[0].time,
    open: bars[0].open,
    high: Math.max(...bars.map((b) => b.high)),
    low: Math.min(...bars.map((b) => b.low)),
    close: bars[bars.length - 1].close,
    volume: bars.reduce((s, b) => s + (b.volume || 0), 0),
  }));
}

function TechnicalChart({ chart: initialChart, symbol }) {
  const isTw = isTwSymbol(symbol);
  const [timeframe, setTimeframe] = useState("daily");
  const [range, setRange] = useState("1y");
  const [zoom, setZoom] = useState(1);
  const [viewEndIndex, setViewEndIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoverPrice, setHoverPrice] = useState(null);
  const [maximized, setMaximized] = useState(false);
  const [chartData, setChartData] = useState(initialChart);
  const [tfLoading, setTfLoading] = useState(false);
  const [maEnabled, setMaEnabled] = useState({ 5: true, 10: true, 20: true, 60: true });
  const pinchRef = useRef(null);
  const panRef = useRef(null);
  const techChartSvgRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [techChartYScale, setTechChartYScale] = useState(1);

  const tfRangeOptions = {
    daily:   [["3m","3M"],["6m","6M"],["1y","1Y"],["2y","2Y"]],
    weekly:  [["6m","6M"],["1y","1Y"],["2y","2Y"],["5y","5Y"]],
    monthly: [["1y","1Y"],["2y","2Y"],["5y","5Y"],["10y","MAX"]],
  };
  const rangeDaysMap = { "3m": 92, "6m": 183, "1y": 365, "2y": 730, "5y": 1826, "10y": 3652 };
  const maColors = { 5: "#a78bfa", 10: "#f472b6", 20: "#f59e0b", 60: "#38bdf8" };

  useEffect(() => {
    setChartData(initialChart);
    setZoom(1);
    setViewEndIndex(null);
    setHoverIndex(null);
    setHoverPrice(null);
    setTechChartYScale(1);
  }, [initialChart]);

  // Fetch more daily data when range exceeds 1Y
  useEffect(() => {
    if (rangeDaysMap[range] <= 365) { setChartData(initialChart); setTfLoading(false); return; }
    let cancelled = false;
    setTfLoading(true);
    fetchChartSingle(symbol, range, "1d").then((data) => {
      if (cancelled) return;
      setChartData(data);
      setTfLoading(false);
    });
    return () => { cancelled = true; };
  }, [symbol, range]);

  const handleTimeframeChange = (tf) => {
    const validRanges = tfRangeOptions[tf].map(([id]) => id);
    if (!validRanges.includes(range)) setRange(validRanges[1] || validRanges[0]);
    setTimeframe(tf);
    setZoom(1);
    setViewEndIndex(null);
    setHoverIndex(null);
    setHoverPrice(null);
  };

  // Build daily points, filter by range, then aggregate to timeframe
  const dailyPoints = useMemo(() => buildOhlcPoints(chartData), [chartData]);
  const rangeFilteredPoints = useMemo(() => {
    if (!dailyPoints.length) return [];
    const cutoff = Date.now() - (rangeDaysMap[range] || 365) * 24 * 60 * 60 * 1000;
    return dailyPoints.filter((p) => p.time >= cutoff);
  }, [dailyPoints, range]);
  const allPoints = useMemo(() => aggregateOhlc(rangeFilteredPoints, timeframe), [rangeFilteredPoints, timeframe]);
  const chartView = useMemo(() => {
    if (!allPoints.length) return { points: [], start: 0, end: -1, rightSlots: 0 };
    const visibleCount = Math.min(allPoints.length, Math.max(20, Math.round(allPoints.length / zoom)));
    const maxEnd = allPoints.length - 1;
    // viewEndIndex 可超過 maxEnd → 超出的部分為右側留白空槽(可把最新一根拖離右緣、移到中間看)
    const maxRight = Math.round(visibleCount * 0.7);
    const defaultRight = Math.max(3, Math.round(visibleCount * 0.06)); // 預設就留一點右側空間,最新一根不貼右緣
    const raw = viewEndIndex == null ? maxEnd + defaultRight : Math.max(visibleCount - 1, Math.min(maxEnd + maxRight, viewEndIndex));
    const end = Math.min(raw, maxEnd);
    const rightSlots = Math.max(0, Math.round(raw - maxEnd));
    const start = Math.max(0, end - visibleCount + 1);
    return { points: allPoints.slice(start, end + 1), start, end, rightSlots };
  }, [allPoints, zoom, viewEndIndex]);
  const points = chartView.points;

  if (points.length < 2) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        {/* Timeframe buttons even when no data */}
        <div style={{ display: "flex", gap: 3, justifyContent: "center", marginBottom: 16 }}>
          {[["daily","日"],["weekly","週"],["monthly","月"]].map(([tf, label]) => (
            <button key={tf} className={`etf-btn ${timeframe === tf ? "is-active" : ""}`} onClick={() => handleTimeframeChange(tf)} style={{ padding: "5px 12px", background: timeframe === tf ? C.accentDim : C.card, color: timeframe === tf ? C.accent : C.t2, border: `1px solid ${timeframe === tf ? C.accent : C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}>{label}</button>
          ))}
        </div>
        <div style={{ color: C.t3, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>
          {timeframe !== "daily" ? `${({ weekly: "週線", monthly: "月線" })[timeframe]}資料不足（僅 ${allPoints.length} 根），請切回日線` : "無圖表資料"}
        </div>
      </div>
    );
  }

  const w = 700;
  const priceH = 300;
  const volH = 72;
  const pad = { l: 46, r: 54, t: 18, b: 24 };
  const chartW = w - pad.l - pad.r;
  const highs = points.map((p) => p.high);
  const lows = points.map((p) => p.low);
  const rawMaxP = Math.max(...highs);
  const rawMinP = Math.min(...lows);
  const rawPR = rawMaxP - rawMinP || 1;
  const midP = (rawMaxP + rawMinP) / 2;
  const scaledPR = rawPR / techChartYScale;
  const maxPrice = midP + scaledPR / 2;
  const minPrice = midP - scaledPR / 2;
  const priceRange = maxPrice - minPrice || 1;
  const maxVol = Math.max(...points.map((p) => p.volume || 0)) || 1;
  const rightSlots = chartView.rightSlots || 0; // 右側留白空槽數(拖曳過最新一根形成)
  const slotCount = points.length + rightSlots;
  const candleGap = chartW / slotCount;
  const candleW = Math.max(2, Math.min(9, candleGap * 0.58));
  const x = (i) => pad.l + i * candleGap + candleGap / 2;
  const y = (price) => pad.t + ((maxPrice - price) / priceRange) * priceH;
  const volY = (volume) => pad.t + priceH + 18 + volH - ((volume || 0) / maxVol) * volH;
  const maPath = (period) => movingAverage(points, period)
    .filter(Boolean)
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.index).toFixed(1)} ${y(p.value).toFixed(1)}`)
    .join(" ");
  const priceLines = [0, 0.25, 0.5, 0.75, 1].map((ratio) => maxPrice - priceRange * ratio);
  const last = points[points.length - 1];
  const activeIndex = hoverIndex == null ? points.length - 1 : Math.max(0, Math.min(points.length - 1, hoverIndex));
  const activePoint = points[activeIndex] || last;
  const cursorPrice = hoverPrice ?? activePoint.close;
  const cursorY = Math.max(pad.t, Math.min(pad.t + priceH, y(cursorPrice)));
  const cursorX = x(activeIndex);
  const latestY = y(last.close);
  const latestColor = dirColor(last.close - last.open, isTw);
  const dateLabelW = 120;
  const dateLabelX = Math.max(pad.l, Math.min(w - pad.r - dateLabelW, cursorX - dateLabelW / 2));
  const first = points[0];
  const chartReturn = first.close ? ((last.close - first.close) / first.close) * 100 : 0;
  const activeReturn = activePoint.open ? ((activePoint.close - activePoint.open) / activePoint.open) * 100 : null;
  const fmtDate = new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" });
  const timeLabel = (value) => fmtDate.format(new Date(value)).replace(/-/g, "/");
  const fullTimeLabel = timeLabel;
  const cursorDateLabel = (value) => { const base = timeLabel(value); const wd = new Intl.DateTimeFormat("zh-TW", { timeZone: "Asia/Taipei", weekday: "short" }).format(new Date(value)); return `${base}（${wd}）`; };
  const formatVolume = (value) => value ? (value >= 1e9 ? `${(value / 1e9).toFixed(2)}B` : value >= 1e6 ? `${(value / 1e6).toFixed(1)}M` : value.toLocaleString()) : "—";
  const clampValue = (value, min, max) => Math.max(min, Math.min(max, value));
  const applyZoomAtRatio = (nextZoom, ratio) => {
    if (!allPoints.length || !points.length) return;
    const safeZoom = clampValue(nextZoom, 1, 8);
    const safeRatio = clampValue(ratio, 0, 1);
    const visibleIndex = clampValue(Math.round(safeRatio * Math.max(points.length - 1, 1)), 0, points.length - 1);
    const anchorIndex = chartView.start + visibleIndex;
    const nextVisibleCount = Math.min(allPoints.length, Math.max(20, Math.round(allPoints.length / safeZoom)));
    const maxStart = Math.max(0, allPoints.length - nextVisibleCount);
    const nextStart = clampValue(anchorIndex - Math.round(safeRatio * Math.max(nextVisibleCount - 1, 1)), 0, maxStart);
    const nextEnd = nextStart + nextVisibleCount - 1;
    setZoom(safeZoom);
    setViewEndIndex(nextEnd >= allPoints.length - 1 ? null : nextEnd);
    setHoverIndex(clampValue(anchorIndex - nextStart, 0, nextVisibleCount - 1));
    setHoverPrice(null);
  };
  const handlePointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewX = ((e.clientX - rect.left) / rect.width) * w;
    const viewY = ((e.clientY - rect.top) / rect.height) * (pad.t + priceH + volH + 48);
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((viewX - pad.l - candleGap / 2) / candleGap)));
    setHoverIndex(idx);
    if (viewY >= pad.t && viewY <= pad.t + priceH) {
      const price = maxPrice - ((viewY - pad.t) / priceH) * priceRange;
      setHoverPrice(price);
    } else {
      setHoverPrice(points[idx]?.close ?? null);
    }
  };
  const handlePointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const viewX = ((e.clientX - rect.left) / rect.width) * w;
    if (viewX > w - pad.r) {
      panRef.current = { pointerId: e.pointerId, type: "yStretch", startY: e.clientY, startScale: techChartYScale };
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return;
    }
    if (pinchRef.current) return;
    panRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseEnd: chartView.end + (chartView.rightSlots || 0), // 含右側留白的有效終點
      isPanning: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handleChartPointerMove = (e) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== e.pointerId || pinchRef.current) {
      handlePointerMove(e);
      return;
    }

    if (pan.type === "yStretch") {
      const dy = pan.startY - e.clientY;
      const scale = pan.startScale * Math.pow(1.008, dy);
      setTechChartYScale(Math.max(0.15, Math.min(20, scale)));
      return;
    }

    const dx = e.clientX - pan.startX;
    const dy = e.clientY - pan.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (!pan.isPanning) {
      if (absX < 8) {
        handlePointerMove(e);
        return;
      }
      if (absY > absX) {
        handlePointerMove(e);
        return;
      }
      pan.isPanning = true;
      setIsPanning(true);
    }

    e.preventDefault();
    applyPanDelta(dx, pan.baseEnd);
  };
  const handlePointerEnd = (e) => {
    if (!panRef.current || panRef.current.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    panRef.current = null;
    setIsPanning(false);
  };
  const applyPanDelta = (dx, baseEnd) => {
    const visibleCount = points.length;
    const minEnd = visibleCount - 1;
    const maxEnd = allPoints.length - 1;
    const maxRight = Math.round(visibleCount * 0.7); // 最多可把最新一根往左推到約中間
    const barShift = Math.round(dx / Math.max(candleGap, 1));
    const nextEnd = clampValue(baseEnd - barShift, minEnd, maxEnd + maxRight);
    setViewEndIndex(nextEnd === maxEnd ? null : nextEnd); // 剛好貼齊最新且無留白 → null(跟隨新資料)
    setHoverIndex(null);
    setHoverPrice(null);
  };
  const handleWheelZoom = (e) => {
    if (!allPoints.length) return;
    const rect = (e.currentTarget || techChartSvgRef.current).getBoundingClientRect();
    const viewX = ((e.clientX - rect.left) / rect.width) * w;
    if (viewX > w - pad.r) {
      const dir = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setTechChartYScale((prev) => Math.max(0.15, Math.min(20, prev * dir)));
      return;
    }
    const ratio = clampValue((viewX - pad.l) / chartW, 0, 1);
    const nextZoom = clampValue(e.deltaY < 0 ? zoom * 1.18 : zoom / 1.18, 1, 8);
    applyZoomAtRatio(nextZoom, ratio);
  };
  useEffect(() => {
    const el = techChartSvgRef.current;
    if (!el) return;
    const handler = (e) => { e.preventDefault(); handleWheelZoom(e); };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  });
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };
  const getTouchRatio = (touches, target) => {
    const rect = target.getBoundingClientRect();
    const midX = (touches[0].clientX + touches[1].clientX) / 2;
    const viewX = ((midX - rect.left) / rect.width) * w;
    return clampValue((viewX - pad.l) / chartW, 0, 1);
  };
  const handleTouchStart = (e) => {
    if (e.touches.length === 1 && !pinchRef.current) {
      const touch = e.touches[0];
      panRef.current = {
        pointerId: "touch",
        startX: touch.clientX,
        startY: touch.clientY,
        baseEnd: chartView.end + (chartView.rightSlots || 0),
        isPanning: false,
      };
      return;
    }
    if (e.touches.length === 2) {
      e.preventDefault();
      panRef.current = null;
      setIsPanning(false);
      pinchRef.current = {
        distance: Math.max(1, getTouchDistance(e.touches)),
        zoom,
      };
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current && allPoints.length) {
      e.preventDefault();
      const distance = Math.max(1, getTouchDistance(e.touches));
      const ratio = getTouchRatio(e.touches, e.currentTarget);
      applyZoomAtRatio(pinchRef.current.zoom * (distance / pinchRef.current.distance), ratio);
      return;
    }
    if (e.touches.length !== 1 || !panRef.current || panRef.current.pointerId !== "touch" || pinchRef.current) return;
    const touch = e.touches[0];
    const pan = panRef.current;
    const dx = touch.clientX - pan.startX;
    const dy = touch.clientY - pan.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (!pan.isPanning) {
      if (absX < 10) return;
      if (absY > absX) {
        panRef.current = null;
        setIsPanning(false);
        return;
      }
      pan.isPanning = true;
      setIsPanning(true);
    }
    e.preventDefault();
    applyPanDelta(dx, pan.baseEnd);
  };
  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) {
      panRef.current = null;
      setIsPanning(false);
    }
  };

  const chartBody = (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>技術線圖 · {{ daily: "日線", weekly: "週線", monthly: "月線" }[timeframe]}</div>
          <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 2 }}>
            {hoverIndex == null ? "LATEST" : fullTimeLabel(activePoint.time)} · O/H/L/C {activePoint.open?.toFixed(2)} / {activePoint.high?.toFixed(2)} / {activePoint.low?.toFixed(2)} / {activePoint.close?.toFixed(2)}
            <span style={{ color: dirColor(chartReturn, isTw), marginLeft: 8 }}>{formatReturnPct(chartReturn)}</span>
            <span style={{ marginLeft: 8 }}>Vol {formatVolume(activePoint.volume)}</span>
          </div>
        </div>
        <button
          onClick={() => setMaximized(true)}
          style={{
            padding: "7px 12px",
            background: C.accent,
            color: C.bg,
            border: "none",
            borderRadius: 7,
            fontSize: 11,
            fontWeight: 800,
            fontFamily: "'Fira Code', monospace",
            cursor: "pointer",
          }}
        >
          MAX
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10, padding: "8px 10px", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 7, fontFamily: "'Fira Code', monospace", fontSize: 11, minHeight: 38 }}>
        <span style={{ color: C.t1 }}>{fullTimeLabel(activePoint.time)}</span>
        <span style={{ color: C.t2 }}>O {activePoint.open?.toFixed(2)}</span>
        <span style={{ color: C.t2 }}>H {activePoint.high?.toFixed(2)}</span>
        <span style={{ color: C.t2 }}>L {activePoint.low?.toFixed(2)}</span>
        <span style={{ color: C.t2 }}>C {activePoint.close?.toFixed(2)}</span>
        <span style={{ color: C.t2 }}>Vol {formatVolume(activePoint.volume)}</span>
        <span style={{ color: dirColor(activeReturn, isTw) }}>{formatReturnPct(activeReturn)}</span>
        <span style={{ marginLeft: "auto", color: C.t3 }}>{points.length}/{allPoints.length} bars</span>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          {/* Timeframe: 日/週/月 */}
          <div style={{ display: "flex", gap: 3, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 7, padding: 3 }}>
            {[["daily","日"],["weekly","週"],["monthly","月"]].map(([tf, label]) => (
              <button
                key={tf}
                className={`etf-btn ${timeframe === tf ? "is-active" : ""}`}
                onClick={() => handleTimeframeChange(tf)}
                style={{
                  padding: "5px 12px",
                  background: timeframe === tf ? C.accentDim : "transparent",
                  color: timeframe === tf ? C.accent : C.t2,
                  border: "none",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 800,
                  fontFamily: "'Fira Code', monospace",
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* MA toggles */}
          <div style={{ display: "flex", gap: 3 }}>
            {[5, 10, 20, 60].map((period) => (
              <button
                key={period}
                onClick={() => setMaEnabled((prev) => ({ ...prev, [period]: !prev[period] }))}
                style={{
                  padding: "4px 8px",
                  background: maEnabled[period] ? maColors[period] : C.card,
                  color: maEnabled[period] ? "#fff" : C.t3,
                  border: `1px solid ${maEnabled[period] ? maColors[period] : C.border}`,
                  borderRadius: 5,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "'Fira Code', monospace",
                  cursor: "pointer",
                  opacity: maEnabled[period] ? 1 : 0.5,
                }}
              >
                MA{period}
              </button>
            ))}
          </div>

          <div style={{ flex: 1 }} />

          {/* Range buttons */}
          {tfRangeOptions[timeframe].map(([id, label]) => (
            <button
              key={id}
              className={`etf-btn ${range === id ? "is-active" : ""}`}
              onClick={() => { setRange(id); setZoom(1); setViewEndIndex(null); setHoverIndex(null); setHoverPrice(null); }}
              style={{
                padding: "6px 10px",
                background: range === id ? C.accentDim : C.card,
                color: range === id ? C.accent : C.t2,
                border: `1px solid ${range === id ? C.accent : C.border}`,
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "'Fira Code', monospace",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}

          {/* Zoom controls */}
          {[["out", "−"], ["in", "+"], ["reset", "⟳"]].map(([action, label]) => (
            <button
              key={action}
              onClick={() => {
                setHoverIndex(null);
                setHoverPrice(null);
                if (action === "in") setZoom((z) => Math.min(6, z * 1.5));
                if (action === "out") setZoom((z) => Math.max(1, z / 1.5));
                if (action === "reset") { setZoom(1); setViewEndIndex(null); }
              }}
              style={{
                padding: "6px 10px",
                background: C.card,
                color: C.t2,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "'Fira Code', monospace",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
      </div>

      <div className="tech-chart-shell" style={{ position: "relative", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, overflowX: "hidden", userSelect: "none", WebkitUserSelect: "none" }}>
        {tfLoading && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(3,6,12,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: 8 }}>
            <span style={{ color: C.accent, fontFamily: "'Fira Code', monospace", fontSize: 12, fontWeight: 700 }}>載入中…</span>
          </div>
        )}
        <svg
          ref={techChartSvgRef}
          className="tech-chart-svg"
          viewBox={`0 0 ${w} ${pad.t + priceH + volH + 48}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handleChartPointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onPointerLeave={(e) => { handlePointerEnd(e); setHoverIndex(null); setHoverPrice(null); }}
          style={{ width: "100%", minWidth: 0, display: "block", cursor: isPanning ? "grabbing" : "grab", touchAction: "pan-y" }}
        >
          <defs>
            <filter id="techLabelShadow" x="-20%" y="-60%" width="140%" height="220%">
              <feDropShadow dx="0" dy="1.5" stdDeviation="1.8" floodColor={C.shadow} floodOpacity="0.9" />
            </filter>
            <clipPath id="techPriceClip"><rect x={pad.l} y={pad.t} width={chartW} height={priceH} /></clipPath>
          </defs>
          <rect x="0" y="0" width={w} height={pad.t + priceH + volH + 48} fill={C.card} />
          {priceLines.map((price) => (
            <g key={price}>
              <line x1={pad.l} x2={w - pad.r} y1={y(price)} y2={y(price)} stroke={C.border} strokeDasharray="4 5" />
              <text x={w - pad.r + 8} y={y(price) + 4} fill={C.t3} fontSize="10" fontFamily="Fira Code">{price.toFixed(2)}</text>
            </g>
          ))}
          <line x1={pad.l} x2={w - pad.r} y1={latestY} y2={latestY} stroke={latestColor} strokeDasharray="5 5" opacity="0.45" />
          <rect x={w - pad.r + 4} y={latestY - 11} width={50} height={22} rx="4" fill={latestColor} />
          <text x={w - pad.r + 29} y={latestY + 4} fill={C.bg} fontSize="11" fontWeight="900" textAnchor="middle" fontFamily="Fira Code">{last.close.toFixed(2)}</text>
          {points.map((p, i) => {
            const up = p.close >= p.open;
            const marketUp = isTw ? up : !up; // 台股紅漲綠跌，美股相反
            const cx = x(i);
            return (
              <rect key={`v${p.time}`} x={cx - candleW / 2} y={volY(p.volume)} width={candleW} height={pad.t + priceH + 18 + volH - volY(p.volume)} fill={marketUp ? "rgba(244,63,94,0.24)" : "rgba(52,211,153,0.22)"} />
            );
          })}
          <g clipPath="url(#techPriceClip)">
            {points.map((p, i) => {
              const up = p.close >= p.open;
              const marketUp = isTw ? up : !up; // 台股紅漲綠跌，美股相反
              const color = marketUp ? C.up : C.down;
              const bodyY = Math.min(y(p.open), y(p.close));
              const bodyH = Math.max(1, Math.abs(y(p.open) - y(p.close)));
              const cx = x(i);
              return (
                <g key={p.time}>
                  <line x1={cx} x2={cx} y1={y(p.high)} y2={y(p.low)} stroke={color} strokeWidth="1.2" />
                  <rect x={cx - candleW / 2} y={bodyY} width={candleW} height={bodyH} fill={marketUp ? "rgba(244,63,94,0.78)" : "rgba(52,211,153,0.72)"} stroke={color} strokeWidth="0.8" />
                </g>
              );
            })}
            {maEnabled[5] && <path d={maPath(5)} fill="none" stroke={maColors[5]} strokeWidth="1.3" strokeLinecap="round" />}
            {maEnabled[10] && <path d={maPath(10)} fill="none" stroke={maColors[10]} strokeWidth="1.4" strokeLinecap="round" />}
            {maEnabled[20] && <path d={maPath(20)} fill="none" stroke={maColors[20]} strokeWidth="1.6" strokeLinecap="round" />}
            {maEnabled[60] && <path d={maPath(60)} fill="none" stroke={maColors[60]} strokeWidth="1.6" strokeLinecap="round" />}
          </g>
          {hoverIndex != null && (
            <g>
              <line x1={cursorX} x2={cursorX} y1={pad.t} y2={pad.t + priceH + volH + 18} stroke={C.t2} strokeDasharray="3 4" opacity="0.75" />
              <line x1={pad.l} x2={w - pad.r} y1={cursorY} y2={cursorY} stroke={C.t2} strokeDasharray="3 4" opacity="0.75" />
              <circle cx={cursorX} cy={y(activePoint.close)} r="3.5" fill={dirColor(activePoint.close - activePoint.open, isTw)} stroke={C.card} strokeWidth="1.5" />
              {Math.abs(cursorY - latestY) > 14 && (
                <>
                  <rect x={w - pad.r + 4} y={cursorY - 11} width={50} height={22} rx="4" fill={C.chartLabelBg} stroke={C.border} filter="url(#techLabelShadow)" />
                  <text x={w - pad.r + 29} y={cursorY + 4} fill={C.chartLabelText} fontSize="11" fontWeight="800" textAnchor="middle" fontFamily="Fira Code">{cursorPrice.toFixed(2)}</text>
                </>
              )}
              <rect x={dateLabelX} y={pad.t + priceH + volH + 21} width={dateLabelW} height={22} rx="4" fill={C.chartLabelBg} stroke={C.border} filter="url(#techLabelShadow)" />
              <text x={dateLabelX + dateLabelW / 2} y={pad.t + priceH + volH + 36} fill={C.chartLabelText} fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="Fira Code">{cursorDateLabel(activePoint.time)}</text>
            </g>
          )}
          <line x1={pad.l} x2={w - pad.r} y1={pad.t + priceH + 18} y2={pad.t + priceH + 18} stroke={C.border} />
          {[0, Math.floor(points.length / 2), points.length - 1].map((idx) => (
            <text key={idx} x={x(idx)} y={pad.t + priceH + volH + 42} fill={C.t3} fontSize="10" textAnchor="middle" fontFamily="Fira Code">{timeLabel(points[idx].time)}</text>
          ))}
          {(() => { let offset = 0; return [5,10,20,60].filter(p => maEnabled[p]).map(p => { const xPos = pad.l + offset; offset += `MA${p}`.length * 7.5 + 8; return <text key={p} x={xPos} y="12" fill={maColors[p]} fontSize="10" fontFamily="Fira Code">MA{p}</text>; }); })()}
          <text x={w - pad.r} y="12" fill={C.t3} fontSize="10" textAnchor="end" fontFamily="Fira Code">Volume</text>
          <rect x={w - pad.r} y={0} width={pad.r} height={pad.t + priceH} fill="transparent" style={{ cursor: "ns-resize" }} />
          {techChartYScale !== 1 && <text x={w - 2} y={pad.t + priceH + volH + 42} textAnchor="end" fontSize="8" fill={C.t3} fontFamily="Fira Code" opacity="0.6">Y:{techChartYScale.toFixed(1)}x</text>}
        </svg>
      </div>
    </>
  );

  return (
    <div>
      {chartBody}
      {maximized && (
        <div
          className="tech-max-overlay"
          onClick={() => setMaximized(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(3,6,12,0.96)",
            padding: 18,
            display: "flex",
          }}
        >
          <div
            className="tech-max-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 1380,
              margin: "0 auto",
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: 18,
              overflow: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <button
                onClick={() => setMaximized(false)}
                style={{ background: C.card, border: `1px solid ${C.border}`, color: C.t2, borderRadius: 7, padding: "8px 12px", cursor: "pointer", fontFamily: "'Fira Code', monospace", fontWeight: 800 }}
              >
                CLOSE
              </button>
            </div>
            {chartBody}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail Modal ──
function Detail({ symbol, quote, onClose, currencySymbol = "$", activeThemes = {}, etfName, isSaved = false, onToggleWatchlist, canUseWatchlist = false, etfMetrics = {}, riskCategory, onPickCategory, onGoToActiveHoldings }) {
  const [detail, setDetail] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [holdingsData, setHoldingsData] = useState(null);
  const [activeHoldings, setActiveHoldings] = useState(null);
  const [holdingTrends, setHoldingTrends] = useState({});
  const [holdingTrendProgress, setHoldingTrendProgress] = useState({ loaded: 0, total: 0 });
  const [twNameMap, setTwNameMap] = useState({});
  const [newsData, setNewsData] = useState({ items: [] });
  const [newsLoading, setNewsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTab("overview");
    setDetail(null);
    setChartData(null);
    setHoldingsData(null);
    setActiveHoldings(null);
    setTwNameMap({});
    // 主動式 ETF:抓自建每日持股(發行商揭露,Yahoo 無)
    if (TW_ACTIVE_DISCLOSURE[symbol]) {
      fetch(`${API}/etf-holdings/${encodeURIComponent(symbol)}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (!cancelled && d && d.holdings && d.holdings.length) setActiveHoldings(d); })
        .catch(() => {});
    }
    setNewsData({ items: [] });
    setNewsLoading(true);
    setHoldingTrends({});
    setHoldingTrendProgress({ loaded: 0, total: 0 });
    // Load detail/holdings/chart first (fast), news separately (slow for TW)
    Promise.all([
      fetchDetail(symbol),
      fetchHoldings(symbol),
      fetchChartSingle(symbol, "1y", "1d"),
    ]).then(([d, h, c]) => {
      if (cancelled) return;
      setDetail(d);
      setHoldingsData(h);
      setChartData(c);
      setLoading(false);
    });
    // News loads independently — doesn't block the UI
    fetchNews(symbol).then((n) => {
      if (cancelled) return;
      setNewsData(n || { items: [] });
      setNewsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  // Fetch Chinese names for TW ETF holdings from TWSE
  useEffect(() => {
    const isTw = symbol.endsWith(".TW") || symbol.endsWith(".TWO");
    const hList = holdingsData?.holdings || detail?.topHoldings?.holdings;
    if (!isTw || !hList || !hList.length) return;
    const codes = hList
      .map((h) => String(h.symbol || "").replace(/\.TW$/i, "").replace(/-TW$/i, "").replace(/\.TWO$/i, "").replace(/-TWO$/i, ""))
      .filter((c) => c && /^\d{4,}$/.test(c));
    if (!codes.length) return;
    // Only fetch codes not already in static TW_STOCK_NAMES
    const missing = codes.filter((c) => !TW_STOCK_NAMES[c]);
    if (!missing.length) return;
    fetchTwStockNames(missing).then((names) => {
      if (Object.keys(names).length) setTwNameMap((prev) => ({ ...prev, ...names }));
    });
  }, [symbol, holdingsData, detail]);

  const stats = detail?.defaultKeyStatistics || {};
  const perf = detail?.fundPerformance?.trailingReturns || {};
  const rawYahooHoldings = holdingsData?.holdings || detail?.topHoldings?.holdings || EMPTY_ARRAY;
  const isActiveHoldingsView = rawYahooHoldings.length === 0 && activeHoldings?.holdings?.length > 0;
  const holdings = useMemo(() => {
    if (!isActiveHoldingsView) return rawYahooHoldings;
    return [...activeHoldings.holdings].sort((a, b) => (b.weight || 0) - (a.weight || 0)).map((h) => {
      const code = (h.stock_code || "").trim();
      let sym = code;
      if (/^\d{4}[A-Z]?$/.test(code)) {
        sym = `${code}.TW`;
      } else if (code.includes(" ")) {
        const [ticker, exch] = code.split(" ");
        sym = exch === "TT" ? `${ticker}.TW` : ticker;
      }
      return { symbol: sym, holdingName: h.stock_name || code, holdingPercent: { raw: h.weight || 0 }, stockCode: code };
    });
  }, [isActiveHoldingsView, rawYahooHoldings, activeHoldings]);
  const sectors = holdingsData?.sectors || detail?.topHoldings?.sectorWeightings || EMPTY_ARRAY;
  const fundProf = detail?.fundProfile || {};
  const expRatio = stats?.annualReportExpenseRatio?.raw;
  const expRatioFromIssuer = detail?.expenseRatioSource === "issuer";
  const totalAssets = stats?.totalAssets?.raw;
  const yld = stats?.yield?.raw;
  const beta = stats?.beta3Year?.raw;
  const inception = stats?.fundInceptionDate?.fmt;
  const category = stats?.category;
  const family = stats?.fundFamily;
  const navInfo = getEtfNavInfo(detail, quote);
  const navPrice = navInfo.navPrice;
  const premiumDiscountPct = navInfo.premiumDiscountPct;

  // 配息：歷史記錄（除息日 + 每股金額）、近一年總配息、殖利率、下次除息日（預告）
  const dividends = Array.isArray(detail?.dividends) ? detail.dividends : EMPTY_ARRAY;
  const dividendsDesc = [...dividends].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const oneYearAgo = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
  const annualDividend = dividends.filter((d) => d.date > oneYearAgo).reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const refPrice = quote?.price ?? navPrice;
  const dividendYield = yld != null ? yld : (annualDividend > 0 && refPrice ? annualDividend / refPrice : null);
  // 下次除息日：優先 Yahoo 預告，否則用歷史配息間隔中位數推估（標示為預估）
  let nextExDate = detail?.summaryDetail?.exDividendDate?.fmt || null;
  let nextExEstimated = false;
  if (!nextExDate && dividends.length >= 2) {
    const asc = [...dividends].sort((a, b) => a.date.localeCompare(b.date));
    const gaps = [];
    for (let i = Math.max(1, asc.length - 4); i < asc.length; i++) {
      gaps.push((new Date(asc[i].date) - new Date(asc[i - 1].date)) / 86400000);
    }
    if (gaps.length) {
      gaps.sort((a, b) => a - b);
      const medianGap = gaps[Math.floor(gaps.length / 2)];
      const proj = new Date(new Date(asc[asc.length - 1].date).getTime() + medianGap * 86400000);
      if (proj > new Date()) { nextExDate = proj.toISOString().slice(0, 10); nextExEstimated = true; }
    }
  }

  const yearCloses = chartData?.indicators?.quote?.[0]?.close?.filter((c) => c != null) || [];
  const isTwSym = symbol.endsWith(".TW") || symbol.endsWith(".TWO");
  // Compute 52W High/Low from chart data when Yahoo meta doesn't have it
  const computed52H = quote?.high52 || (yearCloses.length ? Math.max(...yearCloses) : null);
  const computed52L = quote?.low52 || (yearCloses.length ? Math.min(...yearCloses) : null);

  // ── 適合誰 + 先看三指標(積極/保守導向)──
  const oneYearReturn = (() => {
    const r = perf?.oneYear?.raw;
    if (r != null) return r;
    if (yearCloses.length >= 2 && yearCloses[0]) return yearCloses[yearCloses.length - 1] / yearCloses[0] - 1;
    return null;
  })();
  const maxDrawdown = (() => {
    if (yearCloses.length < 2) return null;
    let peak = yearCloses[0], mdd = 0;
    for (const c of yearCloses) { if (c > peak) peak = c; const dd = (c - peak) / peak; if (dd < mdd) mdd = dd; }
    return mdd; // 負值
  })();
  const persona = (() => {
    // 與清單完全一致:用 app 中文類別(riskCategory)+ 同一份整批 metrics 的 Beta/殖利率
    const m = etfMetrics[symbol];
    const pType = computeRiskProfile(riskCategory, m?.beta, m?.yield);
    return { type: pType, ...RISK_META[pType] };
  })();
  const pctTxt = (x, sign) => x == null ? "—" : `${sign && x >= 0 ? "+" : ""}${(x * 100).toFixed(2)}%`;
  const volTxt = quote?.volume != null ? (quote.volume >= 1e6 ? `${(quote.volume / 1e6).toFixed(1)}M` : `${(quote.volume / 1e3).toFixed(0)}K`) : "—";
  const topMetrics = persona.type === "保守"
    ? [
        { lab: "殖利率", val: dividendYield != null ? pctTxt(dividendYield) : "—", hint: "收益穩定度" },
        { lab: "內扣費用", val: expRatio != null ? pctTxt(expRatio) : "—", hint: "持有成本,越低越好" },
        { lab: "近一年最大回撤", val: maxDrawdown != null ? pctTxt(maxDrawdown, true) : "—", hint: "下檔風險,越接近 0 越穩" },
      ]
    : persona.type === "積極"
    ? [
        { lab: "近一年報酬", val: pctTxt(oneYearReturn, true), hint: "上攻能力" },
        { lab: "Beta(3Y)", val: beta != null ? beta.toFixed(2) : "—", hint: "相對大盤波動,>1 彈性大" },
        { lab: "成交量", val: volTxt, hint: "流動性／進出效率" },
      ]
    : [
        { lab: "近一年報酬", val: pctTxt(oneYearReturn, true), hint: "成長表現" },
        { lab: "殖利率", val: dividendYield != null ? pctTxt(dividendYield) : "—", hint: "配息收益" },
        { lab: "內扣費用", val: expRatio != null ? pctTxt(expRatio) : "—", hint: "持有成本" },
      ];

  const etfDesc = isTwSym ? TW_ETF_DESC[symbol] : US_ETF_DESC[symbol];
  const storedFundIntro = cleanSentence(detail?.fundIntro?.text || detail?.fundIntro?.summary || "");
  const beginnerEtfDesc = storedFundIntro || buildBeginnerEtfDescription({
    symbol,
    name: etfName || quote?.name,
    baseDesc: etfDesc,
    category,
    theme: activeThemes[symbol],
    family,
    holdings,
    sectors,
    expRatio,
    yld,
    beta,
    isTw: isTwSym,
  });

  const perfItems = [
    ["YTD", stats?.ytdReturn?.raw],
    ["1年", perf?.oneYear?.raw],
    ["3年(年化)", stats?.threeYearAverageReturn?.raw],
    ["5年(年化)", stats?.fiveYearAverageReturn?.raw],
  ];
  const hasPerfData = perfItems.some(([, v]) => v != null);
  const marketReturnItems = RETURN_PERIODS.map((period) => [
    period.label,
    quote?.returns?.[period.id],
  ]);
  const holdingSymbols = useMemo(() => {
    const seen = new Set();
    return holdings
      .map((h) => toYahooSymbol(h.symbol))
      .filter((s) => s && /^[A-Z0-9.-]{1,16}$/.test(s))
      .filter((s) => {
        if (seen.has(s)) return false;
        seen.add(s);
        return true;
      })
      .slice(0, MAX_HOLDING_TRENDS);
  }, [holdings]);

  useEffect(() => {
    if (tab !== "holdings" || !holdingSymbols.length) {
      setHoldingTrends({});
      setHoldingTrendProgress({ loaded: 0, total: 0 });
      return;
    }

    let cancelled = false;
    setHoldingTrends({});
    setHoldingTrendProgress({ loaded: 0, total: holdingSymbols.length });

    fetchHoldingTrendCharts(holdingSymbols, (loaded, total, rawResults) => {
      if (cancelled) return;
      const parsed = {};
      for (const [sym, chart] of Object.entries(rawResults)) {
        const trend = parseHoldingTrend(chart);
        if (trend) parsed[sym] = trend;
      }
      setHoldingTrends(parsed);
      setHoldingTrendProgress({ loaded, total });
    }).then((rawResults) => {
      if (cancelled) return;
      const parsed = {};
      for (const [sym, chart] of Object.entries(rawResults)) {
        const trend = parseHoldingTrend(chart);
        if (trend) parsed[sym] = trend;
      }
      setHoldingTrends(parsed);
      setHoldingTrendProgress({ loaded: holdingSymbols.length, total: holdingSymbols.length });
    });

    return () => {
      cancelled = true;
    };
  }, [tab, holdingSymbols]);

  const tabBtn = (id, label) => (
    <button
      className={`seg-btn ${tab === id ? "is-active" : ""}`}
      onClick={() => setTab(id)}
      style={{
        padding: "7px 15px",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
  const newsItems = newsData?.items || EMPTY_ARRAY;
  const formatNewsTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("zh-TW", {
      timeZone: "Asia/Taipei",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  };

  return (
    <div
      className="detail-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(10px)",
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        className="detail-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          width: "100%",
          maxWidth: 760,
          maxHeight: "88vh",
          overflow: "auto",
        }}
      >
        {/* 麵包屑 + 返回(解決一直按瀏覽器返回) */}
        <div className="detail-crumb" style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", padding: "12px 26px", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.t3, fontFamily: "'Fira Code', monospace", background: C.headerBg }}>
          <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.card, border: `1px solid ${C.border}`, color: C.accent, borderRadius: 7, padding: "5px 11px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Fira Code', monospace" }}>← 返回</button>
          <span style={{ color: C.border }}>|</span>
          <button onClick={onClose} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.t3, fontSize: 12, fontFamily: "'Fira Code', monospace" }}>首頁</button>
          <span>›</span>
          <button onClick={onClose} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.t3, fontSize: 12, fontFamily: "'Fira Code', monospace" }}>{/\.(TW|TWO)$/.test(symbol) ? "台股" : "美股"}</button>
          {riskCategory && (<><span>›</span><button onClick={() => onPickCategory ? onPickCategory(riskCategory) : onClose()} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: onPickCategory ? C.accent : C.t3, fontSize: 12, fontFamily: "'Fira Code', monospace" }}>{riskCategory}</button></>)}
          <span>›</span>
          <span style={{ color: C.t1, fontWeight: 700 }}>{symbol.replace(/\.(TWO|TW)$/, "")} {etfName || quote?.name || ""}</span>
        </div>
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: C.t2, fontFamily: "'Fira Code', monospace", fontSize: 13 }}>
            <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            載入 {symbol} 中…
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="detail-header" style={{ padding: "22px 26px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif" }}>{displaySymbol(symbol)}</span>
                  {category && <span style={{ fontSize: 10, padding: "3px 9px", background: C.accentDim, color: C.accent, borderRadius: 4, fontFamily: "'Fira Code', monospace", fontWeight: 600 }}>{category}</span>}
                  {activeThemes[symbol] && <span style={{ fontSize: 10, padding: "3px 9px", background: C.card, color: C.warm, borderRadius: 4, border: `1px solid ${C.border}`, fontFamily: "'Fira Code', monospace", fontWeight: 600 }}>{activeThemes[symbol]}</span>}
                </div>
                <div style={{ fontSize: 13, color: C.t2, fontFamily: "'Bitter', serif" }}>{etfName || quote?.name}</div>
                {family && <div style={{ fontSize: 11, color: C.t3, marginTop: 2, fontFamily: "'Fira Code', monospace" }}>{family}{inception ? ` · 成立 ${inception}` : ""}</div>}
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <button
                  onClick={() => onToggleWatchlist?.(symbol)}
                  title={canUseWatchlist ? (isSaved ? "移出自選" : "加入自選") : "登入後可加入自選"}
                  style={{ background: isSaved ? C.warm : C.card, border: `1px solid ${isSaved ? C.warm : C.border}`, color: isSaved ? C.bg : C.t2, minWidth: 72, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 800, fontFamily: "'Fira Code', monospace" }}
                >
                  {isSaved ? "★ 自選" : "☆ 自選"}
                </button>
                <button onClick={onClose} aria-label="關閉" style={{ background: C.card, border: `1px solid ${C.border}`, color: C.t3, width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
            </div>

            {/* Price + 1Y Chart */}
            <div className="detail-price" style={{ padding: "14px 26px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div>
                <span style={{ fontSize: 34, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>{currencySymbol}{quote?.price?.toFixed(2)}</span>
                <span style={{ fontSize: 15, fontWeight: 700, marginLeft: 10, color: dirColor(quote?.changePct, isTwSym), fontFamily: "'Fira Code', monospace" }}>
                  {quote?.changePct >= 0 ? "+" : ""}{quote?.changePct?.toFixed(2)}%
                </span>
              </div>
              {yearCloses.length > 5 && (
                <Spark data={yearCloses} w={180} h={40} color={dirColor(yearCloses[yearCloses.length - 1] - yearCloses[0], isTwSym)} />
              )}
              <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>近一年走勢</span>
            </div>

            {/* Tabs */}
            <div className="detail-tabs" style={{ padding: "0 26px 14px", display: "flex" }}>
              <div className="seg-track" style={{ display: "inline-flex", overflowX: "auto" }}>
                {tabBtn("overview", "總覽")}
                {tabBtn("technical", "技術圖")}
                {tabBtn("holdings", "成分股")}
                {tabBtn("sectors", "產業配置")}
                {tabBtn("dividend", "配息")}
                {tabBtn("news", "最新消息")}
              </div>
            </div>

            <div className="detail-body" style={{ padding: "18px 26px 26px" }}>
              {tab === "overview" && (
                <>
                  {/* 適合誰 + 先看三指標 */}
                  <div style={{ background: `${persona.color}12`, border: `1px solid ${persona.color}33`, borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                      <span style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace" }}>適合誰</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: persona.color, background: C.card, border: `1px solid ${persona.color}55`, borderRadius: 7, padding: "3px 10px" }}>{persona.icon} {persona.type}型</span>
                      <span style={{ fontSize: 12, color: C.t2 }}>{persona.note}</span>
                    </div>
                    <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 6 }}>先看這三個指標</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                      {topMetrics.map((m) => (
                        <div key={m.lab} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                          <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>{m.lab}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{m.val}</div>
                          <div style={{ fontSize: 9.5, color: C.t3, marginTop: 3 }}>{m.hint}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Key Stats Grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
                    {[
                      ["基金規模", totalAssets ? (totalAssets >= 1e9 ? `${currencySymbol}${(totalAssets / 1e9).toFixed(1)}B` : `${currencySymbol}${(totalAssets / 1e6).toFixed(0)}M`) : null],
                      ["NAV", navPrice != null ? `${currencySymbol}${navPrice.toFixed(2)}` : null, "ETF 每單位淨值，也就是基金持有資產換算到每單位的估計價值。"],
                      ["溢價率", premiumDiscountPct != null ? formatPremiumDiscount(premiumDiscountPct) : null, "ETF 市價相對 NAV 的差距。正值代表市價高於淨值（溢價），負值代表市價低於淨值（折價）。"],
                      ["內扣費用", expRatio != null ? `${(expRatio * 100).toFixed(2)}%` : null, "ETF 每年從基金資產中扣除的管理成本，已反映在價格/淨值裡；數字越低，長期成本通常越低。"],
                      ["殖利率", yld != null ? `${(yld * 100).toFixed(2)}%` : null, "近似一年配息相對目前價格的比例。它不是保證收益，配息和價格都會變動。"],
                      ["Beta(3Y)", beta != null ? beta.toFixed(2) : null, "過去 3 年相對大盤的波動程度。1 接近大盤，>1 波動較大，<1 波動較小，負值代表方向常相反。"],
                      ["52W High", computed52H ? `${currencySymbol}${computed52H.toFixed(2)}` : null],
                      ["52W Low", computed52L ? `${currencySymbol}${computed52L.toFixed(2)}` : null],
                      ["收盤時間", quote?.lastCloseTime ? `${formatTaipeiTime(quote.lastCloseTime)} 台北` : null],
                    ].filter(([, v]) => v != null).map(([l, v, tip]) => (
                      <div
                        key={l}
                        className="stat-card"
                        data-tip={tip || undefined}
                        tabIndex={tip ? 0 : undefined}
                        style={{ background: C.card, borderRadius: 8, padding: "12px 14px", border: `1px solid ${C.border}`, cursor: tip ? "help" : "default" }}
                      >
                        <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", letterSpacing: 0.8, marginBottom: 4 }}>{l}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Performance */}
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 10, fontFamily: "'Bitter', serif" }}>市場報酬</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(82px, 1fr))", gap: 8, marginBottom: 20 }}>
                    {marketReturnItems.map(([l, v]) => (
                      <div key={l} style={{ background: C.card, borderRadius: 8, padding: "10px 12px", textAlign: "center", border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>{l}</div>
                        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Bitter', serif", color: dirColor(v, isTwSym) }}>
                          {formatReturnPct(v)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasPerfData && (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 10, fontFamily: "'Bitter', serif" }}>歷史績效</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                        {perfItems.map(([l, v]) => (
                          <div key={l} style={{ flex: "1 1 100px", background: C.card, borderRadius: 8, padding: "10px 14px", textAlign: "center", border: `1px solid ${C.border}` }}>
                            <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>{l}</div>
                            <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Bitter', serif", color: dirColor(v, isTwSym) }}>
                              {v != null ? `${(v * 100).toFixed(1)}%` : "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {beginnerEtfDesc && (
                    <div style={{ background: C.card, borderRadius: 8, padding: "14px 16px", border: `1px solid ${C.border}`, marginBottom: 16 }}>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", letterSpacing: 0.8, marginBottom: 6 }}>基金簡介</div>
                      <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.6 }}>{beginnerEtfDesc}</div>
                      {detail?.fundIntro?.sourceName && (
                        <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 10 }}>
                          來源：{detail.fundIntro.sourceUrl ? (
                            <a href={detail.fundIntro.sourceUrl} target="_blank" rel="noreferrer" style={{ color: C.accent, textDecoration: "none" }}>{detail.fundIntro.sourceName}</a>
                          ) : detail.fundIntro.sourceName}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div style={{ background: C.card, borderRadius: 8, padding: "12px 14px", border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>交易所</div>
                      <div style={{ fontSize: 13, color: C.t1, fontFamily: "'Bitter', serif" }}>{quote?.exchange || "—"}</div>
                    </div>
                    <div style={{ background: C.card, borderRadius: 8, padding: "12px 14px", border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>日均量</div>
                      <div style={{ fontSize: 13, color: C.t1, fontFamily: "'Bitter', serif" }}>{quote?.volume?.toLocaleString() || "—"}</div>
                    </div>
                  </div>
                </>
              )}

              {tab === "technical" && (
                <TechnicalChart chart={chartData} symbol={symbol} />
              )}

              {tab === "holdings" && (
                holdings.length > 0 ? (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace" }}>
                          {isActiveHoldingsView ? `每日揭露持股 · ${activeHoldings.count} 檔 · ${activeHoldings.date}` : `${holdingsData?.isFallback ? "TOP " : ""}${holdings.length} HOLDINGS`}
                        </div>
                        {!isActiveHoldingsView && (
                          <div style={{ fontSize: 10, color: holdingsData?.isFallback ? C.warm : C.accent, fontFamily: "'Fira Code', monospace", marginTop: 3 }}>
                            {holdingsData?.isFallback ? "Yahoo fallback：通常只提供主要持股，不代表完整成分股" : `官方來源：${holdingsData?.sourceName || "Issuer"}`}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 3 }}>
                          股價趨勢 {Object.keys(holdingTrends).length}/{holdingTrendProgress.total || holdingSymbols.length} loaded
                          {holdings.length > MAX_HOLDING_TRENDS ? ` · 先載入前 ${MAX_HOLDING_TRENDS} 檔` : ""}
                          {" · 日漲跌=最近收盤對前一交易日 · 線色=月報酬"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {TW_ACTIVE_DISCLOSURE[symbol] && onGoToActiveHoldings && (
                          <button onClick={() => onGoToActiveHoldings(symbol)} style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 5, padding: "5px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'Fira Code', monospace", whiteSpace: "nowrap" }}>
                            持股異動 →
                          </button>
                        )}
                        {holdingsData?.sourceUrl && (
                          <a
                            href={holdingsData.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: C.accent, fontSize: 10, fontFamily: "'Fira Code', monospace", textDecoration: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 8px" }}
                          >
                            SOURCE
                          </a>
                        )}
                      </div>
                    </div>
                    {holdings.map((h, i) => {
                      const pct = (h.holdingPercent?.raw || 0) * 100;
                      const hSymbol = normalizeHoldingSymbol(h.symbol);
                      const yahooSymbol = toYahooSymbol(h.symbol);
                      const quoteUrl = getHoldingQuoteUrl(h.symbol);
                      const trend = holdingTrends[yahooSymbol];
                      const isTwEtf = symbol.endsWith(".TW") || symbol.endsWith(".TWO");
                      const hTicker = String(h.symbol || "").replace(/\.(TW|TWO)$/i, "").replace(/-(TW|TWO)$/i, "");
                      const hName = (isTwEtf ? (getTwStockName(h.symbol) || twNameMap[hTicker]) : null) || h.holdingName;
                      return (
                        <div
                          className="holding-row"
                          key={i}
                          role={quoteUrl ? "button" : undefined}
                          tabIndex={quoteUrl ? 0 : undefined}
                          title={quoteUrl ? `開啟 ${yahooSymbol || hSymbol} 股價` : undefined}
                          onClick={() => openQuotePage(quoteUrl)}
                          onKeyDown={(e) => {
                            if (quoteUrl && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              openQuotePage(quoteUrl);
                            }
                          }}
                          style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}33`, overflowX: "auto", cursor: quoteUrl ? "pointer" : "default" }}
                        >
                          <div style={{ width: 24, fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace", textAlign: "right", flexShrink: 0 }}>{i + 1}</div>
                          <div style={{ flex: "1 1 150px", minWidth: 150 }}>
                            {quoteUrl ? (
                              <a
                                href={quoteUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openQuotePage(quoteUrl);
                                }}
                                style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.t1, fontFamily: "'Bitter', serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: "none" }}
                                title={`開啟 ${hSymbol} 股價`}
                              >
                                {hName}
                              </a>
                            ) : (
                              <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, fontFamily: "'Bitter', serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{hName}</div>
                            )}
                            {quoteUrl ? (
                              <a
                                href={quoteUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openQuotePage(quoteUrl);
                                }}
                                style={{ fontSize: 11, color: C.accent, fontFamily: "'Fira Code', monospace", textDecoration: "none" }}
                              >
                                {hSymbol} ↗
                              </a>
                            ) : (
                              <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace" }}>{hSymbol || h.symbol || "—"}</div>
                            )}
                            {yahooSymbol && yahooSymbol !== hSymbol && (
                              <div style={{ fontSize: 9, color: C.t3, fontFamily: "'Fira Code', monospace" }}>Yahoo {yahooSymbol}</div>
                            )}
                          </div>
                          <div className="holding-metrics" style={{ width: 442, flexShrink: 0, display: "grid", gridTemplateColumns: "64px 76px repeat(5, 1fr)", gap: 6, alignItems: "center" }}>
                            <Spark data={trend?.closes} w={58} h={22} color={dirColor(trend?.returns?.["1m"] || 0, isTwSym)} />
                            <div style={{ textAlign: "right" }} title={trend?.lastCloseTime ? `日漲跌：最近收盤對前一交易日收盤 · ${formatTaipeiTime(trend.lastCloseTime)} 台北` : "日漲跌"}>
                              <div style={{ fontSize: 9, color: C.t3, fontFamily: "'Fira Code', monospace" }}>日漲跌</div>
                              <div style={{ fontSize: 11, fontWeight: 800, color: dirColor(trend?.returns?.["1d"], isTwSym), fontFamily: "'Fira Code', monospace" }}>
                                {trend ? formatReturnPct(trend.returns?.["1d"]) : "…"}
                              </div>
                            </div>
                            {HOLDING_TREND_PERIODS.map((period) => {
                              const value = trend?.returns?.[period.id];
                              return (
                                <div key={period.id} style={{ textAlign: "right" }}>
                                  <div style={{ fontSize: 9, color: C.t3, fontFamily: "'Fira Code', monospace" }}>{period.label}</div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: dirColor(value, isTwSym), fontFamily: "'Fira Code', monospace" }}>
                                    {trend ? formatReturnPct(value) : "…"}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{pct.toFixed(2)}%</div>
                            <div style={{ width: 70, height: 3, background: C.border, borderRadius: 2, marginTop: 3 }}>
                              <div style={{ width: `${Math.min(pct * 6, 100)}%`, height: "100%", background: C.accent, borderRadius: 2 }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : TW_ACTIVE_DISCLOSURE[symbol] ? (
                  <div style={{ textAlign: "center", padding: "32px 20px" }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>🗂️</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 6 }}>主動式 ETF · 每日揭露持股</div>
                    <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.7, maxWidth: 420, margin: "0 auto 16px" }}>
                      此為主動式 ETF,Yahoo 未提供持股明細。完整且最新的每日持股由發行商官方揭露。
                    </div>
                    <a href={TW_ACTIVE_DISCLOSURE[symbol]} target="_blank" rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 800, color: C.bg, background: C.accent, borderRadius: 8, padding: "10px 18px", textDecoration: "none", fontFamily: "'Fira Code', monospace" }}>
                      看發行商每日揭露 ↗
                    </a>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 40, color: C.t3, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>無持股資料</div>
                )
              )}

              {tab === "sectors" && (
                sectors.length > 0 ? (
                  <div>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 14, fontFamily: "'Fira Code', monospace" }}>產業配置</div>
                    {sectors.map((s, i) => {
                      const [key, val] = Object.entries(s)[0];
                      const pct = (val?.raw || 0) * 100;
                      const labels = {
                        technology: "科技", financial_services: "金融", healthcare: "醫療保健",
                        consumer_cyclical: "非必需消費", communication_services: "通訊服務",
                        industrials: "工業", consumer_defensive: "必需消費", energy: "能源",
                        utilities: "公用事業", realestate: "房地產", basic_materials: "基礎材料",
                      };
                      return (
                        <div key={key} style={{ marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontSize: 12, color: C.t2, fontFamily: "'Bitter', serif" }}>{labels[key] || key}</span>
                            <span style={{ fontSize: 12, color: C.t1, fontFamily: "'Fira Code', monospace", fontWeight: 600 }}>{pct.toFixed(1)}%</span>
                          </div>
                          <div style={{ width: "100%", height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: "100%", height: "100%", background: `hsl(${160 + i * 20}, 70%, 50%)`, borderRadius: 3, transform: `scaleX(${pct / 100})`, transformOrigin: "left", transition: "transform 0.5s" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 40, color: C.t3, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>無產業資料</div>
                )
              )}

              {tab === "dividend" && (
                <>
                  {(() => {
                    const yearCount = dividends.filter((d) => d.date > oneYearAgo).length;
                    const freq = yearCount >= 11 ? "月配" : yearCount >= 5 ? "雙月配" : yearCount >= 3 ? "季配" : yearCount === 2 ? "半年配" : yearCount === 1 ? "年配" : "—";
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 18 }}>
                        {[
                          ["殖利率", dividendYield != null ? `${(dividendYield * 100).toFixed(2)}%` : "—", "近一年總配息 ÷ 股價"],
                          ["近一年配息", annualDividend > 0 ? `${isTwSym ? "NT$ " : "$"}${annualDividend.toFixed(2)}` : "—", "近 12 個月每股配息合計"],
                          ["配息頻率", freq, "依近一年配息次數推估"],
                          [`下次除息日${nextExEstimated ? "（預估）" : ""}`, nextExDate || "—", nextExEstimated ? "依歷史配息間隔推估，非官方公告" : "Yahoo 預告"],
                        ].map(([label, val, hint]) => (
                          <div key={label} title={hint} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                            <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>{label}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: C.warm, fontFamily: "'Bitter', serif" }}>{val}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {dividendsDesc.length > 0 ? (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.t2, marginBottom: 8 }}>配息記錄</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "2px 12px", fontFamily: "'Fira Code', monospace" }}>
                        <div style={{ fontSize: 10, color: C.t3, padding: "4px 0", borderBottom: `1px solid ${C.border}` }}>除息日</div>
                        <div style={{ fontSize: 10, color: C.t3, padding: "4px 0", borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>每股配息</div>
                        {dividendsDesc.map((d) => (
                          <Fragment key={d.date}>
                            <div style={{ fontSize: 12, color: C.t1, padding: "7px 0", borderBottom: `1px solid ${C.border}55` }}>{d.date}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.up, padding: "7px 0", borderBottom: `1px solid ${C.border}55`, textAlign: "right" }}>{isTwSym ? "NT$ " : "$"}{Number(d.amount).toFixed(3)}</div>
                          </Fragment>
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 12 }}>
                        資料來源：Yahoo Finance（除息日／每股配息，近 3 年）。實際配息以發行商公告為準。
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: "center", padding: 40, color: C.t3, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>無配息資料（可能為不配息或累積型 ETF）</div>
                  )}
                </>
              )}

              {tab === "news" && (
                newsLoading ? (
                  <div style={{ textAlign: "center", padding: 40, color: C.t3, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>
                    <img
                      src="/logo-etfowl.png"
                      alt="ETFowl"
                      style={{ width: 52, height: 52, objectFit: "contain", marginBottom: 12, animation: "logoPulse 1.3s ease-in-out infinite" }}
                    />
                    <div>正在搜集最新消息...</div>
                  </div>
                ) : newsItems.length > 0 ? (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>最新消息</div>
                        <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 3 }}>
                          {newsData?.source || "Yahoo Finance RSS"} · 與 {displaySymbol(symbol)} 相關
                        </div>
                      </div>
                      <a
                        href={newsData?.sourceUrl || (symbol.endsWith(".TW") || symbol.endsWith(".TWO")
                          ? `https://tw.stock.yahoo.com/quote/${encodeURIComponent(symbol)}/news`
                          : `https://finance.yahoo.com/quote/${encodeURIComponent(symbol)}/news/`)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: C.accent, fontSize: 10, fontFamily: "'Fira Code', monospace", textDecoration: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 8px" }}
                      >
                        SOURCE
                      </a>
                    </div>
                    <div style={{ display: "grid", gap: 10 }}>
                      {newsItems.map((item, i) => (
                        <a
                          key={`${item.link}-${i}`}
                          href={item.link}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "block",
                            background: C.card,
                            border: `1px solid ${C.border}`,
                            borderRadius: 8,
                            padding: "12px 14px",
                            textDecoration: "none",
                            color: C.t1,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 5 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, fontFamily: "'Bitter', serif" }}>{item.title}</div>
                            <div
                              title={item.isDateFallback ? "來源未提供文章日期，顯示本次抓取時間" : "文章發布時間"}
                              style={{ flexShrink: 0, fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", textAlign: "right" }}
                            >
                              {formatNewsTime(item.publishedAt)}
                            </div>
                          </div>
                          {item.summary && (
                            <div style={{ color: C.t2, fontSize: 12, lineHeight: 1.55, fontFamily: "'Bitter', serif", marginBottom: 7 }}>
                              {item.summary}
                            </div>
                          )}
                          <div style={{ color: C.accent, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>
                            {item.source || "news"} ↗
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 40, color: C.t3, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>
                    暫無相關新聞
                  </div>
                )
              )}

              {tab === "cost" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                    <div style={{ background: C.card, borderRadius: 8, padding: "14px 16px", border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>內扣費用</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: C.warm, fontFamily: "'Bitter', serif" }}>{expRatio != null ? `${(expRatio * 100).toFixed(2)}%` : "—"}</div>
                      <div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>年化內扣管理費{expRatioFromIssuer ? " · 發行商公告" : ""}</div>
                    </div>
                    <div style={{ background: C.card, borderRadius: 8, padding: "14px 16px", border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 4 }}>每股價格</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>{currencySymbol}{quote?.price?.toFixed(2)}</div>
                      <div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>每股買入價</div>
                    </div>
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 10, fontFamily: "'Bitter', serif" }}>購買管道比較</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8, marginBottom: 20 }}>
                    {(symbol.endsWith(".TW") ? [
                      { n: "國內券商（台股）", fee: "0.1425%", p: "直接台幣交易、免匯差", c: "手續費可議價" },
                      { n: "定期定額", fee: "1~1000元起", p: "自動扣款、紀律投資", c: "部分券商限定標的" },
                      { n: "銀行信託", fee: "0.2%–0.5%", p: "可搭配帳戶管理", c: "費用較券商高" },
                    ] : [
                      { n: "複委託（國內券商）", fee: "0.15%–1%", p: "中文介面、免海外報稅", c: "手續費偏高" },
                      { n: "Firstrade", fee: "免手續費", p: "零佣金、中文支援", c: "需自行報稅" },
                      { n: "Interactive Brokers", fee: "$0.0035/股", p: "專業工具、極低成本", c: "介面複雜" },
                      { n: "Charles Schwab", fee: "免手續費", p: "大型老牌、可國際開戶", c: "部分功能受限" },
                    ]).map((ch) => (
                      <div key={ch.n} style={{ background: C.card, borderRadius: 8, padding: 14, border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 4 }}>{ch.n}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, fontFamily: "'Fira Code', monospace", marginBottom: 6 }}>{ch.fee}</div>
                        <div style={{ fontSize: 11, color: C.up, marginBottom: 2 }}>✓ {ch.p}</div>
                        <div style={{ fontSize: 11, color: C.down }}>✗ {ch.c}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 8, fontFamily: "'Bitter', serif" }}>持有成本說明</div>
                  <div style={{ background: C.card, borderRadius: 8, padding: 16, border: `1px solid ${C.border}`, fontSize: 12, color: C.t2, lineHeight: 2, fontFamily: "'Bitter', serif" }}>
                    {symbol.endsWith(".TW") ? (
                      <>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>內扣費用 (Expense Ratio)</span> — 已反映在淨值中，無需額外支付</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>交易稅</span> — ETF 賣出時課 0.1%（股票型）</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>手續費</span> — 買賣各收 0.1425%，多數券商可打折至 2~6 折</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>股利課稅</span> — 併入綜所稅（可抵扣 8.5%，上限 8 萬），海外所得超 100 萬另計</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>二代健保補充保費</span> — 單次配息超 2 萬元加收 2.11%</div>
                      </>
                    ) : (
                      <>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>內扣費用 (Expense Ratio)</span> — 已反映在淨值中，無需額外支付</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>股息預扣稅</span> — 美國對非居民扣繳 30%，可填 W-8BEN 適用優惠稅率</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>匯差成本</span> — TWD↔USD 約 0.05%–0.3%（依銀行/券商）</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>帳戶維護費</span> — 多數海外券商已免除</div>
                        <div>• <span style={{ color: C.t1, fontWeight: 600 }}>遺產稅風險</span> — 美國對非居民超過 $60,000 資產課遺產稅（最高 40%）</div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes logoPulse { 0%,100% { transform: scale(0.92); opacity: 0.5; } 50% { transform: scale(1.08); opacity: 1; } }
      `}</style>
    </div>
  );
}

// ── Compare Chart ──
const COMPARE_COLORS = ["#f59e0b", "#38bdf8", "#34d399", "#f472b6"];
const COMPARE_RANGES = [
  { id: "1m", label: "1M", days: 30 },
  { id: "3m", label: "3M", days: 92 },
  { id: "6m", label: "6M", days: 183 },
  { id: "1y", label: "1Y", days: 365 },
  { id: "ytd", label: "YTD", days: 0 },
];

function CompareModal({ symbols, quotes, mktCfg, onClose }) {
  const [range, setRange] = useState("3m");
  const [chartDataMap, setChartDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [cmpSort, setCmpSort] = useState({ field: "return", dir: "desc" });
  const toggleCmpSort = (field) => setCmpSort((prev) => prev.field === field ? { field, dir: prev.dir === "desc" ? "asc" : "desc" } : { field, dir: field === "symbol" || field === "name" ? "asc" : "desc" });
  const cmpSortArrow = (field) => cmpSort.field === field ? (cmpSort.dir === "desc" ? " ▼" : " ▲") : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchRange = range === "ytd" ? "1y" : range === "1m" ? "3mo" : range === "3m" ? "6mo" : range === "6m" ? "1y" : range;
    Promise.all(
      symbols.map((s) =>
        fetchChartSingle(s, fetchRange, "1d").then((chart) => [s, chart])
      )
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      for (const [s, chart] of results) {
        if (chart) map[s] = buildOhlcPoints(chart);
      }
      setChartDataMap(map);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [symbols, range]);

  // Compute normalized returns (% from start)
  const { series, dates, minPct, maxPct } = useMemo(() => {
    const rDef = COMPARE_RANGES.find((r) => r.id === range);
    let cutoff;
    if (range === "ytd") {
      const now = new Date();
      cutoff = new Date(now.getFullYear(), 0, 1).getTime();
    } else {
      cutoff = Date.now() - (rDef?.days || 92) * 86400000;
    }
    const series = [];
    let allDates = new Set();
    for (const sym of symbols) {
      const pts = (chartDataMap[sym] || []).filter((p) => p.time >= cutoff);
      if (!pts.length) continue;
      const base = pts[0].close;
      const normalized = pts.map((p) => ({
        time: p.time,
        pct: ((p.close - base) / base) * 100,
        close: p.close,
      }));
      series.push({ symbol: sym, data: normalized, base });
      normalized.forEach((d) => allDates.add(d.time));
    }
    const dates = [...allDates].sort((a, b) => a - b);
    let minPct = 0, maxPct = 0;
    for (const s of series) {
      for (const d of s.data) {
        if (d.pct < minPct) minPct = d.pct;
        if (d.pct > maxPct) maxPct = d.pct;
      }
    }
    const margin = Math.max(1, (maxPct - minPct) * 0.1);
    return { series, dates, minPct: minPct - margin, maxPct: maxPct + margin };
  }, [chartDataMap, symbols, range]);

  const w = 740, h = 340;
  const pad = { l: 56, r: 62, t: 20, b: 30 };
  const chartW = w - pad.l - pad.r;
  const chartH = h - pad.t - pad.b;
  const pctRange = maxPct - minPct || 1;
  const xScale = (time) => {
    if (!dates.length) return pad.l;
    const ratio = (time - dates[0]) / (dates[dates.length - 1] - dates[0] || 1);
    return pad.l + ratio * chartW;
  };
  const yScale = (pct) => pad.t + ((maxPct - pct) / pctRange) * chartH;
  const zeroY = yScale(0);
  const gridLines = [0];
  const step = pctRange > 20 ? 5 : pctRange > 8 ? 2 : 1;
  for (let v = step; v <= maxPct; v += step) gridLines.push(v);
  for (let v = -step; v >= minPct; v -= step) gridLines.push(v);
  const timeLabel = (t) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(t)).replace(/-/g, "/");

  // Hover logic
  const hoverTime = hoverIndex != null && dates[hoverIndex] ? dates[hoverIndex] : null;
  const handlePointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewX = ((e.clientX - rect.left) / rect.width) * w;
    const ratio = (viewX - pad.l) / chartW;
    const idx = Math.max(0, Math.min(dates.length - 1, Math.round(ratio * (dates.length - 1))));
    setHoverIndex(idx);
  };

  const getClosestPoint = (seriesData, time) => {
    if (!seriesData.length) return null;
    let best = seriesData[0];
    for (const d of seriesData) {
      if (Math.abs(d.time - time) < Math.abs(best.time - time)) best = d;
    }
    return best;
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(3,6,12,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 860, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "22px 26px", maxHeight: "90vh", overflow: "auto", overscrollBehavior: "contain" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>績效比較</div>
          <button onClick={onClose} aria-label="關閉" style={{ background: C.card, border: `1px solid ${C.border}`, color: C.t3, width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
          {symbols.map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 14, height: 3, borderRadius: 2, background: COMPARE_COLORS[i] }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: COMPARE_COLORS[i], fontFamily: "'Fira Code', monospace" }}>{displaySymbol(s)}</span>
              <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>{mktCfg.names[s] || quotes[s]?.name || ""}</span>
            </div>
          ))}
        </div>

        {/* Range buttons */}
        <div style={{ display: "flex", gap: 5, marginBottom: 14 }}>
          {COMPARE_RANGES.map((r) => (
            <button key={r.id} className={`etf-btn ${range === r.id ? "is-active" : ""}`} onClick={() => { setRange(r.id); setHoverIndex(null); }} style={{ padding: "6px 12px", background: range === r.id ? C.accentDim : C.card, color: range === r.id ? C.accent : C.t2, border: `1px solid ${range === r.id ? C.accent : C.border}`, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}>{r.label}</button>
          ))}
        </div>

        {/* Hover values */}
        {hoverTime && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10, padding: "8px 10px", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 7, fontFamily: "'Fira Code', monospace", fontSize: 11 }}>
            <span style={{ color: C.t1 }}>{new Intl.DateTimeFormat("zh-TW", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(hoverTime))}</span>
            {series.map((s, i) => {
              const pt = getClosestPoint(s.data, hoverTime);
              return pt ? <span key={s.symbol} style={{ color: COMPARE_COLORS[i] }}>{displaySymbol(s.symbol)} {pt.pct >= 0 ? "+" : ""}{pt.pct.toFixed(2)}% ({pt.close.toFixed(2)})</span> : null;
            })}
          </div>
        )}

        {/* Chart */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, overflowX: "auto", position: "relative" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(3,6,12,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5, borderRadius: 8 }}>
              <span style={{ color: C.accent, fontFamily: "'Fira Code', monospace", fontSize: 12, fontWeight: 700 }}>載入中…</span>
            </div>
          )}
          <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", minWidth: 500, display: "block", cursor: "crosshair" }}
            onPointerMove={handlePointerMove} onPointerLeave={() => setHoverIndex(null)}
          >
            <rect x="0" y="0" width={w} height={h} fill={C.card} />
            {/* Grid lines */}
            {gridLines.map((v) => (
              <g key={v}>
                <line x1={pad.l} x2={w - pad.r} y1={yScale(v)} y2={yScale(v)} stroke={v === 0 ? C.t3 : C.border} strokeDasharray={v === 0 ? "none" : "4 5"} strokeWidth={v === 0 ? 1.2 : 0.8} opacity={v === 0 ? 0.6 : 1} />
                <text x={pad.l - 6} y={yScale(v) + 4} fill={C.t3} fontSize="10" textAnchor="end" fontFamily="Fira Code">{v > 0 ? "+" : ""}{v.toFixed(step < 1 ? 1 : 0)}%</text>
              </g>
            ))}
            {/* Date labels */}
            {dates.length > 0 && [0, Math.floor(dates.length * 0.25), Math.floor(dates.length * 0.5), Math.floor(dates.length * 0.75), dates.length - 1].map((idx) => (
              <text key={idx} x={xScale(dates[idx])} y={h - 6} fill={C.t3} fontSize="10" textAnchor="middle" fontFamily="Fira Code">{timeLabel(dates[idx])}</text>
            ))}
            {/* Lines */}
            {series.map((s, i) => {
              if (s.data.length < 2) return null;
              const path = s.data.map((d, j) => `${j === 0 ? "M" : "L"} ${xScale(d.time).toFixed(1)} ${yScale(d.pct).toFixed(1)}`).join(" ");
              return <path key={s.symbol} d={path} fill="none" stroke={COMPARE_COLORS[i]} strokeWidth="2" strokeLinecap="round" />;
            })}
            {/* Hover crosshair */}
            {hoverTime && (
              <g>
                <line x1={xScale(hoverTime)} x2={xScale(hoverTime)} y1={pad.t} y2={h - pad.b} stroke={C.t2} strokeDasharray="3 4" opacity="0.6" />
                {series.map((s, i) => {
                  const pt = getClosestPoint(s.data, hoverTime);
                  return pt ? <circle key={s.symbol} cx={xScale(pt.time)} cy={yScale(pt.pct)} r="4" fill={COMPARE_COLORS[i]} stroke={C.card} strokeWidth="1.5" /> : null;
                })}
              </g>
            )}
            {/* End labels (collision-resolved) */}
            {(() => {
              const labelH = 22;
              const labels = series.map((s, i) => {
                const last = s.data[s.data.length - 1];
                return last ? { i, symbol: s.symbol, pct: last.pct, y: yScale(last.pct) } : null;
              }).filter(Boolean).sort((a, b) => a.y - b.y);
              // Push apart overlapping labels
              for (let pass = 0; pass < 5; pass++) {
                for (let j = 1; j < labels.length; j++) {
                  const gap = labels[j].y - labels[j - 1].y;
                  if (gap < labelH) {
                    const push = (labelH - gap) / 2 + 1;
                    labels[j - 1].y -= push;
                    labels[j].y += push;
                  }
                }
              }
              // Clamp within chart bounds
              for (const lb of labels) { lb.y = Math.max(pad.t + 10, Math.min(h - pad.b - 10, lb.y)); }
              return labels.map((lb) => (
                <g key={lb.symbol + "-label"}>
                  <rect x={w - pad.r + 4} y={lb.y - 10} width={54} height={20} rx="4" fill={COMPARE_COLORS[lb.i]} />
                  <text x={w - pad.r + 31} y={lb.y + 4} fill="#fff" fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="Fira Code">{lb.pct >= 0 ? "+" : ""}{lb.pct.toFixed(1)}%</text>
                </g>
              ));
            })()}
          </svg>
        </div>

        {/* Summary table */}
        <div style={{ marginTop: 16, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'Fira Code', monospace", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {[["symbol","ETF","left"],["name","名稱","left"],["price","現價","right"],["return","期間報酬","right"]].map(([field, label, align]) => (
                  <th key={field} onClick={() => toggleCmpSort(field)} style={{ textAlign: align, padding: "8px 6px", color: cmpSort.field === field ? C.accent : C.t3, fontWeight: 600, cursor: "pointer", userSelect: "none" }}>{label}{cmpSortArrow(field)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...series].sort((a, b) => {
                const d = cmpSort.dir === "asc" ? 1 : -1;
                const f = cmpSort.field;
                if (f === "symbol") return d * a.symbol.localeCompare(b.symbol);
                if (f === "name") return d * ((mktCfg.names[a.symbol] || quotes[a.symbol]?.name || "").localeCompare(mktCfg.names[b.symbol] || quotes[b.symbol]?.name || ""));
                if (f === "price") return d * ((quotes[a.symbol]?.price || 0) - (quotes[b.symbol]?.price || 0));
                const la = a.data[a.data.length - 1], lb = b.data[b.data.length - 1];
                return d * ((la?.pct || 0) - (lb?.pct || 0));
              }).map((s, i) => {
                const last = s.data[s.data.length - 1];
                const q = quotes[s.symbol];
                return (
                  <tr key={s.symbol} style={{ borderBottom: `1px solid ${C.border}33` }}>
                    <td style={{ padding: "8px 6px", fontWeight: 700, color: COMPARE_COLORS[symbols.indexOf(s.symbol)] }}>{displaySymbol(s.symbol)}</td>
                    <td style={{ padding: "8px 6px", color: C.t2 }}>{mktCfg.names[s.symbol] || q?.name || ""}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", color: C.t1 }}>{q?.price?.toFixed(2) || "—"}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", fontWeight: 700, color: dirColor(last?.pct, isTwSymbol(s.symbol)) }}>{last ? `${last.pct >= 0 ? "+" : ""}${last.pct.toFixed(2)}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──
export default function ETFRadar() {
  const [theme, setTheme] = useState(getInitialTheme);
  C = THEMES[theme] || THEMES.dark;
  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try { localStorage.setItem("etfowl:theme", next); } catch {}
      return next;
    });
  }, []);

  const [view, setView] = useState("dashboard");
  const [market, setMarket] = useState("TW");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("全部");
  const [sortBy, setSortBy] = useState("changePct");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (field) => { if (sortBy === field) setSortDir((d) => d === "desc" ? "asc" : "desc"); else { setSortBy(field); setSortDir(field === "name" ? "asc" : "desc"); } };
  const sortArrow = (field) => sortBy === field ? (sortDir === "desc" ? " ▼" : " ▲") : "";
  const [returnPeriod, setReturnPeriod] = useState("1d");
  const [topCount, setTopCount] = useState(5);
  const [moveFilter, setMoveFilter] = useState("all");
  const [premiumFilter, setPremiumFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [searchViewMode, setSearchViewMode] = useState("table");
  const [etfMetrics, setEtfMetrics] = useState({});
  const [holdingQuery, setHoldingQuery] = useState("");
  const [holdingSearch, setHoldingSearch] = useState(null);
  const [holdingSearching, setHoldingSearching] = useState(false);
  const [holdingSort, setHoldingSort] = useState({ key: "weight", dir: "desc" });
  const [holdingPage, setHoldingPage] = useState(1);
  const [activeEtfData, setActiveEtfData] = useState(null);
  const [activeEtfLoading, setActiveEtfLoading] = useState(false);
  const [activeEtfExpanded, setActiveEtfExpanded] = useState(null);
  const [activeLandingTab, setActiveLandingTab] = useState("changes"); // changes|consensus|sectors|picks
  const [consensusExpanded, setConsensusExpanded] = useState(null); // 展開看是哪些 ETF 持有
  const [sectorExpanded, setSectorExpanded] = useState(null); // 展開看產業鏈裡哪些 ETF 加減碼
  const [consensusData, setConsensusData] = useState(null);
  const [sectorFlowData, setSectorFlowData] = useState(null);
  const [picksData, setPicksData] = useState(null);
  const [activeEtfDetail, setActiveEtfDetail] = useState(null);
  const [activeEtfDetailTab, setActiveEtfDetailTab] = useState("holdings");
  const [detailHoldingSort, setDetailHoldingSort] = useState({ field: "weight", dir: "desc" });
  const toggleDetailHoldingSort = (field) => setDetailHoldingSort((prev) => prev.field === field ? { field, dir: prev.dir === "desc" ? "asc" : "desc" } : { field, dir: field === "stock_name" ? "asc" : "desc" });
  const detailHoldingSortArrow = (field) => detailHoldingSort.field === field ? (detailHoldingSort.dir === "desc" ? " ▼" : " ▲") : "";
  const [activeEtfHistory, setActiveEtfHistory] = useState(null);
  const [activeEtfHistoryLoading, setActiveEtfHistoryLoading] = useState(false);
  const [activeStrategy, setActiveStrategy] = useState(null); // { [symbol]: summary row }
  const [activeStrategyDetail, setActiveStrategyDetail] = useState(null); // { symbol, stocks, ... }
  const [activeStrategyLoading, setActiveStrategyLoading] = useState(false);
  const [activeEtfSelectedStock, setActiveEtfSelectedStock] = useState(null);
  const [activeStockPrices, setActiveStockPrices] = useState(null);
  const [stockChartZoom, setStockChartZoom] = useState({ start: 0, end: 1 });
  const [stockChartHover, setStockChartHover] = useState(null);
  const [stockChartYScale, setStockChartYScale] = useState(1);
  const [weightChartHover, setWeightChartHover] = useState(null);
  const stockChartRef = useRef(null);
  const stockChartDragRef = useRef(null);
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth <= 760);
  const [activeViewMode, setActiveViewMode] = useState("card");
  const [activeSearch, setActiveSearch] = useState("");
  const [activeSort, setActiveSort] = useState("total");
  const [quotes, setQuotes] = useState({});
  const [premiumBySymbol, setPremiumBySymbol] = useState({});
  const [watchlistQuotes, setWatchlistQuotes] = useState({ US: {}, TW: {} });
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [session, setSession] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(false); // 密碼重設信回站後輸入新密碼
  const [newPassword, setNewPassword] = useState("");
  const [watchlist, setWatchlist] = useState([]); // 自選 ETF（symbol 陣列，需登入）
  const [watchlistId, setWatchlistId] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState("");
  const [adminSyncing, setAdminSyncing] = useState(false);
  const [reportRegenerating, setReportRegenerating] = useState(false);
  const [syncSort, setSyncSort] = useState({ field: "time", dir: "desc" });
  const toggleSyncSort = (field) => setSyncSort((prev) => prev.field === field ? { field, dir: prev.dir === "desc" ? "asc" : "desc" } : { field, dir: field === "type" || field === "market" || field === "status" || field === "message" ? "asc" : "desc" });
  const syncSortArrow = (field) => syncSort.field === field ? (syncSort.dir === "desc" ? " ▼" : " ▲") : "";
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg, type = "info") => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 2000);
  }, []);
  const [mobileAuthOpen, setMobileAuthOpen] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState("");
  const [portfolioAnalysis, setPortfolioAnalysis] = useState(null);
  const [reportMarket, setReportMarket] = useState("TW");
  const [reportData, setReportData] = useState(null);
  const [reportDates, setReportDates] = useState([]);
  const [reportSelDate, setReportSelDate] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const toggleCompare = useCallback((sym) => {
    setCompareList((prev) => prev.includes(sym) ? prev.filter((s) => s !== sym) : prev.length >= 4 ? prev : [...prev, sym]);
  }, []);

  const [proxyError, setProxyError] = useState(false);
  const recFormRef = useRef({});
  const quotesCache = useRef({ US: null, TW: null });
  const mktCfg = MARKET_CONFIG[market];
  const activeCats = mktCfg.cats;
  const allSyms = useMemo(() => Object.values(activeCats).flat(), [activeCats]);
  const pricedSyms = useMemo(() => allSyms.filter((s) => hasUsablePrice(quotes[s])), [allSyms, quotes]);
  const catMap = useMemo(() => {
    const m = {};
    Object.entries(activeCats).forEach(([c, ss]) => ss.forEach((s) => (m[s] = c)));
    return m;
  }, [activeCats]);
  const activeThemes = mktCfg.themes;
  const currencySymbol = mktCfg.currencySymbol;

  const activePeriod = RETURN_PERIOD_MAP[returnPeriod] || RETURN_PERIODS[0];
  const closeTimeSummary = useMemo(() => {
    const rows = pricedSyms
      .map((symbol) => ({ symbol, time: quotes[symbol]?.lastCloseTime }))
      .filter((row) => row.time);
    if (!rows.length) return { latest: null, latestCount: 0, staleCount: 0, total: 0 };

    const latest = Math.max(...rows.map((row) => row.time));
    const latestDate = taipeiDateKey(latest);
    const latestCount = rows.filter((row) => taipeiDateKey(row.time) === latestDate).length;

    // 容忍落後 1 個交易日（低流動性債券 ETF 偶爾沒成交是常態）：
    // 以實際出現過的交易日為準，落後 ≥2 個交易日（早於前一個交易日）才算「未更新」
    const distinctDates = [...new Set(rows.map((row) => taipeiDateKey(row.time)))].sort().reverse();
    const prevTradingDate = distinctDates[1] || distinctDates[0];
    const staleCount = rows.filter((row) => taipeiDateKey(row.time) < prevTradingDate).length;

    return {
      latest,
      latestCount,
      staleCount,
      total: rows.length,
    };
  }, [pricedSyms, quotes]);
  const latestCloseTime = closeTimeSummary.latest;
  const closeCoverageText = latestCloseTime
    ? ` · 收盤 ${formatTaipeiTime(latestCloseTime)} 台北${closeTimeSummary.staleCount > 0 ? ` · ${closeTimeSummary.staleCount}檔未更新` : ""}`
    : lastUpdate
      ? ` · ${lastUpdate.toLocaleTimeString("zh-TW", { timeZone: "Asia/Taipei" })} 台北`
      : "";

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => setSession(data.session || null));
    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession || null);
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // 深連結:新頁載入時若帶 ?symbol=XXX(EtfLink 產生)。主動 ETF → 開「目前持股」明細頁;其他 → 一般 Detail。
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sym = new URLSearchParams(window.location.search).get("symbol");
    if (!sym) return;
    const s = sym.toUpperCase();
    if (/\.TWO?$/.test(s)) setMarket("TW");
    if (TW_ACTIVE_DISCLOSURE[s]) { setView("active"); setActiveEtfDetail(s); setActiveEtfDetailTab("delta"); }
    else setSelected(s);
  }, []);

  // 窄螢幕(手機)偵測:主動 ETF 單股進出場頁需在窄螢幕改上下堆疊
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setNarrow(window.innerWidth <= 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const switchMarket = useCallback((m) => {
    if (m === market) return;
    setMarket(m);
    setSearch("");
    setCat("全部");
    setMoveFilter("all");
    setPremiumFilter("all");
    setQuotes({});
    setPremiumBySymbol({});
    setSelected(null);
    setHoldingSearch(null);
    setHoldingQuery("");
    setCompareList([]);
    setShowCompare(false);
    setView("dashboard");
  }, [market]);

  const loadMarketData = useCallback(async (targetMarket = market, force = false) => {
    const syms = Object.values(MARKET_CONFIG[targetMarket].cats).flat();
    const cachedQuotes = !force
      ? quotesCache.current[targetMarket] || readMarketCache(targetMarket, syms)
      : null;

    if (cachedQuotes) {
      quotesCache.current[targetMarket] = cachedQuotes;
      setWatchlistQuotes((prev) => ({ ...prev, [targetMarket]: { ...prev[targetMarket], ...cachedQuotes } }));
      setQuotes(cachedQuotes);
      setLoading(false);
      setProgress(100);
    } else {
      setLoading(true);
      setProgress(0);
      setQuotes({});
    }

    setProxyError(false);

    try {
      const health = await fetch(`${API}/health`, { cache: "no-store" });
      if (!health.ok) throw new Error("not ok");
    } catch {
      setProxyError(true);
      if (!cachedQuotes) {
        setLoading(false);
        return false;
      }
      return true;
    }

    const fetcher = targetMarket === "TW" ? fetchTwseCharts : fetchAllCharts;
    const results = await fetcher(syms, (loaded, total, rawResults) => {
      setProgress(Math.round((loaded / total) * 100));
      const parsed = {};
      for (const [sym, chart] of Object.entries(rawResults)) {
        const p = parseChart(chart);
        if (p) parsed[sym] = p;
      }
      setQuotes((prev) => cachedQuotes ? { ...prev, ...parsed } : parsed);
    });
    const finalQuotes = {};
    for (const [sym, chart] of Object.entries(results)) {
      const p = parseChart(chart);
      if (p) finalQuotes[sym] = p;
    }
    quotesCache.current[targetMarket] = finalQuotes;
    setWatchlistQuotes((prev) => ({ ...prev, [targetMarket]: { ...prev[targetMarket], ...finalQuotes } }));
    writeMarketCache(targetMarket, syms, finalQuotes);
    setQuotes(finalQuotes);
    setLoading(false);
    setLastUpdate(new Date());
    return true;
  }, [market]);

  useEffect(() => {
    loadMarketData(market);
  }, [market, loadMarketData]);

  useEffect(() => {
    let cancelled = false;

    async function loadPremiumData() {
      const syms = Object.values(MARKET_CONFIG[market].cats).flat();
      if (!syms.length) {
        setPremiumBySymbol({});
        return;
      }

      try {
        const apiRes = await fetch(`${API}/navs?symbols=${encodeURIComponent(syms.join(","))}`, { cache: "no-store" });
        if (apiRes.ok) {
          const apiData = await apiRes.json();
          if (apiData?.navs && Object.keys(apiData.navs).length) {
            if (!cancelled) setPremiumBySymbol(apiData.navs);
            return;
          }
        }
      } catch (err) {
        console.warn("[premium] API load failed:", err?.message || err);
      }

      if (!isSupabaseConfigured || !supabase) {
        if (!cancelled) setPremiumBySymbol({});
        return;
      }

      try {
        const next = {};
        for (let i = 0; i < syms.length; i += 100) {
          const chunk = syms.slice(i, i + 100);
          const { data, error } = await supabase
            .from("etf_details")
            .select("symbol,data")
            .in("symbol", chunk);
          if (error) throw error;
          for (const row of data || []) {
            const navInfo = getEtfNavInfo(row.data || {}, null);
            if (navInfo.navPrice == null && navInfo.premiumDiscountPct == null) continue;
            next[row.symbol] = {
              navPrice: navInfo.navPrice,
              premiumDiscountPct: navInfo.premiumDiscountPct,
              marketPrice: getRawNumber(row.data?.officialNav?.marketPrice),
              sourceLabel: navInfo.sourceLabel,
              asOf: navInfo.asOf,
            };
          }
        }
        if (!cancelled) setPremiumBySymbol(next);
      } catch (err) {
        console.warn("[premium] load failed:", err?.message || err);
        if (!cancelled) setPremiumBySymbol({});
      }
    }

    loadPremiumData();
    return () => { cancelled = true; };
  }, [market]);

  const cats = ["全部", ...Object.keys(activeCats)];

  const filtered = useMemo(() => {
    return pricedSyms.filter((s) => {
      const mc = cat === "全部" || catMap[s] === cat;
      const ms = !search || s.toLowerCase().includes(search.toLowerCase()) || displaySymbol(s).toLowerCase().includes(search.toLowerCase()) || (quotes[s]?.name || "").toLowerCase().includes(search.toLowerCase());
      const ret = getReturnPct(quotes[s], returnPeriod);
      const mm = moveFilter === "all" || (moveFilter === "up" && ret > 0) || (moveFilter === "down" && ret < 0);
      const premium = getMarketPremiumInfo(s, quotes[s], premiumBySymbol).premiumDiscountPct;
      const pm = premiumFilter === "all" || (premiumFilter === "premium" && premium > 0) || (premiumFilter === "discount" && premium < 0);
      const mr = riskFilter === "all" || computeRiskProfile(catMap[s], etfMetrics[s]?.beta, etfMetrics[s]?.yield) === riskFilter;
      return mc && ms && mm && pm && mr;
    }).sort((a, b) => {
      const qa = quotes[a], qb = quotes[b];
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortBy === "name") return dir * a.localeCompare(b);
      if (sortBy === "price") return dir * ((qa?.price || 0) - (qb?.price || 0));
      if (sortBy === "changePct") return dir * (getReturnPct(qa, returnPeriod) - getReturnPct(qb, returnPeriod));
      if (sortBy === "volume") return dir * ((qa?.volume || 0) - (qb?.volume || 0));
      if (sortBy === "premium") {
        const pa = getMarketPremiumInfo(a, qa, premiumBySymbol).premiumDiscountPct;
        const pb = getMarketPremiumInfo(b, qb, premiumBySymbol).premiumDiscountPct;
        return dir * ((pa ?? -Infinity) - (pb ?? -Infinity));
      }
      return 0;
    });
  }, [cat, search, sortBy, sortDir, quotes, returnPeriod, moveFilter, premiumFilter, riskFilter, etfMetrics, premiumBySymbol, pricedSyms, catMap]);

  const watchlistByMarket = useMemo(() => {
    const usSyms = new Set(Object.values(MARKET_CONFIG.US.cats).flat());
    const twSyms = new Set(Object.values(MARKET_CONFIG.TW.cats).flat());
    return {
      US: watchlist.filter((s) => usSyms.has(s) || (!twSyms.has(s) && !/^\d/.test(s))),
      TW: watchlist.filter((s) => twSyms.has(s) || /^\d/.test(s)),
    };
  }, [watchlist]);

  useEffect(() => {
    if (view !== "watchlist" || !session || !watchlist.length) return;
    let cancelled = false;
    (async () => {
      for (const m of ["US", "TW"]) {
        const syms = (watchlistByMarket[m] || []).filter((s) => !watchlistQuotes[m]?.[s]);
        if (!syms.length) continue;
        const fetcher = m === "TW" ? fetchTwseCharts : fetchAllCharts;
        try {
          const results = await fetcher(syms);
          if (cancelled) return;
          const parsed = {};
          for (const [sym, chart] of Object.entries(results)) {
            const p = parseChart(chart);
            if (p) parsed[sym] = p;
          }
          if (Object.keys(parsed).length) {
            setWatchlistQuotes((prev) => ({ ...prev, [m]: { ...prev[m], ...parsed } }));
          }
        } catch (err) {
          console.warn(`[watchlist:${m}] quote preload failed:`, err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [view, session, watchlist, watchlistByMarket, watchlistQuotes]);

  const gainers = useMemo(() =>
    pricedSyms.slice().sort((a, b) => getReturnPct(quotes[b], returnPeriod) - getReturnPct(quotes[a], returnPeriod)).slice(0, topCount),
    [quotes, returnPeriod, pricedSyms, topCount]
  );
  const losers = useMemo(() =>
    pricedSyms.slice().sort((a, b) => getReturnPct(quotes[a], returnPeriod) - getReturnPct(quotes[b], returnPeriod)).slice(0, topCount),
    [quotes, returnPeriod, pricedSyms, topCount]
  );

  const premiumRows = useMemo(() => (
    pricedSyms
      .map((s) => ({ symbol: s, premium: getMarketPremiumInfo(s, quotes[s], premiumBySymbol).premiumDiscountPct }))
      .filter((row) => Number.isFinite(row.premium))
  ), [pricedSyms, quotes, premiumBySymbol]);
  const premiumCount = premiumRows.filter((row) => row.premium > 0).length;
  const discountCount = premiumRows.filter((row) => row.premium < 0).length;
  const highPremiums = useMemo(() =>
    premiumRows.filter((row) => row.premium > 0).sort((a, b) => b.premium - a.premium).slice(0, topCount),
    [premiumRows, topCount]
  );
  const deepDiscounts = useMemo(() =>
    premiumRows.filter((row) => row.premium < 0).sort((a, b) => a.premium - b.premium).slice(0, topCount),
    [premiumRows, topCount]
  );

  const upCount = pricedSyms.filter((s) => getReturnPct(quotes[s], returnPeriod) > 0).length;
  const downCount = pricedSyms.filter((s) => getReturnPct(quotes[s], returnPeriod) < 0).length;

  const holdingRows = useMemo(() => {
    const rows = (holdingSearch?.matches || []).map((m) => {
      const q = quotes[m.etfSymbol];
      return {
        ...m,
        name: mktCfg.names[m.etfSymbol] || q?.name || catMap[m.etfSymbol] || "ETF",
        ret: getReturnPct(q, returnPeriod),
      };
    });
    const dir = holdingSort.dir === "asc" ? 1 : -1;
    return rows.slice().sort((a, b) => {
      const value = (row) => {
        if (holdingSort.key === "etf") return displaySymbol(row.etfSymbol);
        if (holdingSort.key === "name") return row.name || "";
        if (holdingSort.key === "source") return row.sourceName || "";
        if (holdingSort.key === "return") return Number.isFinite(row.ret) ? row.ret : -Infinity;
        return row.weight ?? -Infinity;
      };
      const av = value(a);
      const bv = value(b);
      if (typeof av === "string" || typeof bv === "string") return String(av).localeCompare(String(bv)) * dir;
      return (av - bv) * dir;
    });
  }, [holdingSearch, quotes, returnPeriod, mktCfg.names, catMap, holdingSort]);

  const holdingTotalPages = Math.max(1, Math.ceil(holdingRows.length / HOLDING_SEARCH_PAGE_SIZE));
  const holdingPageSafe = Math.min(holdingPage, holdingTotalPages);
  const holdingPageRows = holdingRows.slice((holdingPageSafe - 1) * HOLDING_SEARCH_PAGE_SIZE, holdingPageSafe * HOLDING_SEARCH_PAGE_SIZE);

  const setHoldingSortKey = useCallback((key) => {
    setHoldingSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
    setHoldingPage(1);
  }, []);

  const holdingSortMark = (key) => holdingSort.key === key ? (holdingSort.dir === "desc" ? "▼" : "▲") : "↕";
  const holdingSortHeader = (key, label, align = "left", title) => (
    <button
      type="button"
      onClick={() => setHoldingSortKey(key)}
      title={title || `依${label}排序`}
      style={{
        width: "100%",
        background: "transparent",
        border: 0,
        color: holdingSort.key === key ? C.t1 : C.t3,
        fontSize: 10,
        fontFamily: "'Fira Code', monospace",
        textAlign: align,
        cursor: "pointer",
        padding: 0,
      }}
    >
      {label} {holdingSortMark(key)}
    </button>
  );

  const runHoldingSearch = useCallback(async () => {
    const q = holdingQuery.trim();
    if (!q) return;
    setHoldingSearching(true);
    setHoldingPage(1);
    const result = await searchEtfsByHolding(q, pricedSyms);
    setHoldingSearch(result);
    setHoldingSearching(false);
  }, [holdingQuery, pricedSyms]);

  const handleAuthSubmit = useCallback(async (mode) => {
    if (!supabase) {
      setAuthMessage("資料庫尚未設定。");
      return;
    }
    if (!authEmail.trim()) {
      setAuthMessage("請輸入電子信箱。");
      return;
    }
    if (authPassword.length < 6) {
      setAuthMessage("密碼至少需要 6 碼。");
      return;
    }
    setAuthBusy(true);
    setAuthMessage("");
    const email = authEmail.trim();
    try {
      if (mode === "signup") {
        // 開了「Confirm email」時 signUp 不會立刻給 session → 顯示「已寄驗證信」
        const { data, error } = await supabase.auth.signUp({ email, password: authPassword, options: { emailRedirectTo: window.location.origin } });
        if (error) setAuthMessage(error.message);
        else if (!data.session) setAuthMessage("驗證信已寄出，請至信箱點連結完成註冊。");
        else { setAuthMessage("帳號已建立並登入。"); setAuthPassword(""); }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password: authPassword });
        if (error) setAuthMessage(error.message);
        else { setAuthMessage(""); setAuthPassword(""); }
      }
    } catch (err) {
      setAuthMessage(err?.message || "驗證請求失敗。");
    } finally {
      setAuthBusy(false);
    }
  }, [authEmail, authPassword]);

  const handleOAuthSignIn = useCallback(async (provider) => {
    if (!supabase) { setAuthMessage("資料庫尚未設定。"); return; }
    setAuthBusy(true);
    setAuthMessage("");
    // 成功會整頁導向 provider;回跳後 supabase-js 自動建立 session(onAuthStateChange 接手)
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: window.location.origin } });
    if (error) { setAuthMessage(error.message); setAuthBusy(false); }
  }, []);

  const handlePasswordReset = useCallback(async () => {
    if (!supabase) { setAuthMessage("資料庫尚未設定。"); return; }
    if (!authEmail.trim()) { setAuthMessage("請先輸入電子信箱再重設密碼。"); return; }
    setAuthBusy(true);
    setAuthMessage("");
    const { error } = await supabase.auth.resetPasswordForEmail(authEmail.trim(), { redirectTo: window.location.origin });
    setAuthMessage(error ? error.message : "密碼重設信已寄出，請收信。");
    setAuthBusy(false);
  }, [authEmail]);

  const handleUpdatePassword = useCallback(async () => {
    if (!supabase) return;
    if (newPassword.length < 6) { setAuthMessage("新密碼至少需要 6 碼。"); return; }
    setAuthBusy(true);
    setAuthMessage("");
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) setAuthMessage(error.message);
    else {
      setAuthMessage("密碼已更新。");
      setNewPassword("");
      setRecoveryMode(false);
      // 清掉網址列殘留的 recovery hash
      if (window.history.replaceState) window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    setAuthBusy(false);
  }, [newPassword]);

  const handleSignOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setAuthMessage("");
  }, []);

  const isAdmin = ADMIN_EMAILS.includes(String(session?.user?.email || "").toLowerCase());

  // 帳戶下拉:點外面或按 Esc 自動關閉
  useEffect(() => {
    if (!mobileAuthOpen) return;
    const onPointerDown = (e) => {
      if (e.target.closest(".mobile-account-toggle, .auth-controls")) return;
      setMobileAuthOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === "Escape") setMobileAuthOpen(false); };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileAuthOpen]);

  // 流量追蹤：每個 session 進站記一次 pageview
  useEffect(() => {
    try {
      // 持久訪客 ID（localStorage，跨分頁/跨天為同一個）；用於不重複訪客去重
      let vid = localStorage.getItem("etfowl:vid");
      if (!vid) {
        vid = (window.crypto?.randomUUID?.() || (Math.random().toString(36).slice(2) + Date.now().toString(36)));
        localStorage.setItem("etfowl:vid", vid);
      }
      // session ID（sessionStorage，單分頁）仍保留供參考
      let sid = sessionStorage.getItem("etfowl:sid");
      if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("etfowl:sid", sid); }
      fetch(`${API}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitorId: vid, sessionId: sid, path: "/", userId: session?.user?.id || null }),
      }).catch(() => {});
    } catch {}
    // 只在首次掛載記錄
  }, []); // eslint-disable-line

  // 載入每檔 Beta/殖利率，供風險屬性「完全一致」判定（清單與個股頁共用公式）
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/etf-metrics`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled && d && typeof d === "object") setEtfMetrics(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadAdminStats = useCallback(async () => {
    if (!session) return;
    setAdminLoading(true); setAdminError("");
    try {
      const res = await fetch(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `讀取失敗 (${res.status})`);
      }
      setAdminStats(await res.json());
    } catch (err) { setAdminError(err.message); }
    finally { setAdminLoading(false); }
  }, [session]);

  const triggerAdminSync = useCallback(async (mkt) => {
    if (!session) return;
    setAdminSyncing(true); setAdminError("");
    try {
      const res = await fetch(`${API}/admin/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ market: mkt, range: "1mo" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || `更新失敗 (${res.status})`);
    } catch (err) { setAdminError(err.message); }
    finally { setAdminSyncing(false); loadAdminStats(); }
  }, [session, loadAdminStats]);

  const regenerateReport = useCallback(async (mkt) => {
    if (!session) return;
    setReportRegenerating(true); setAdminError("");
    try {
      const res = await fetch(`${API}/admin/regenerate-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ market: mkt }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || `重新生成失敗 (${res.status})`);
    } catch (err) { setAdminError(err.message); }
    finally { setReportRegenerating(false); }
  }, [session]);

  // 進入管理頁時載入數據
  useEffect(() => {
    if (view === "admin" && isAdmin) loadAdminStats();
  }, [view, isAdmin]); // eslint-disable-line

  const loadReport = useCallback(async (mkt, date) => {
    if (!session) return;
    setReportLoading(true); setReportError("");
    try {
      const qs = `market=${mkt}${date ? `&date=${date}` : ""}`;
      const res = await fetch(`${API}/report?${qs}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `讀取失敗 (${res.status})`); }
      const data = await res.json();
      setReportData(data.report || null);
      setReportDates(data.availableDates || []);
      setReportSelDate(data.report?.report_date || "");
    } catch (err) { setReportError(err.message); setReportData(null); }
    finally { setReportLoading(false); }
  }, [session]);

  // 進入報告頁 / 切換市場時載入
  useEffect(() => {
    if (view === "report" && session) loadReport(reportMarket, "");
  }, [view, session, reportMarket]); // eslint-disable-line

  // 進入主動 ETF 專區時載入
  useEffect(() => {
    if (view !== "active" || activeEtfData) return;
    setActiveEtfLoading(true);
    fetch(`${API}/active-etf-summary`)
      .then((r) => r.json())
      .then((d) => setActiveEtfData(d))
      .catch(() => {})
      .finally(() => setActiveEtfLoading(false));
  }, [view]); // eslint-disable-line

  // 主動專區跨檔分頁(共識股/產業流向/精選訊號),各只抓一次
  useEffect(() => {
    if (view !== "active") return;
    const load = (tab, cur, setter, path) => {
      if (activeLandingTab !== tab || cur) return;
      fetch(`${API}${path}`).then((r) => r.json()).then(setter).catch(() => setter({ empty: true }));
    };
    load("consensus", consensusData, setConsensusData, "/consensus-stocks");
    load("sectors", sectorFlowData, setSectorFlowData, "/sector-flow");
    load("picks", picksData, setPicksData, "/accumulation-picks");
  }, [view, activeLandingTab]); // eslint-disable-line

  // 策略總表(勝率/報酬),供列表卡片徽章用
  useEffect(() => {
    if (view !== "active" || activeStrategy) return;
    fetch(`${API}/active-etf-strategy`)
      .then((r) => r.json())
      .then((d) => {
        const map = {};
        for (const r of (d.etfs || [])) map[r.symbol] = r;
        setActiveStrategy(map);
      })
      .catch(() => setActiveStrategy({}));
  }, [view]); // eslint-disable-line

  // 開「策略/損益」tab 時載入該檔明細
  useEffect(() => {
    if (!activeEtfDetail || activeEtfDetailTab !== "strategy") return;
    if (activeStrategyDetail && activeStrategyDetail.symbol === activeEtfDetail) return;
    setActiveStrategyLoading(true);
    fetch(`${API}/active-etf-strategy/${encodeURIComponent(activeEtfDetail)}`)
      .then((r) => r.json())
      .then((d) => setActiveStrategyDetail(d))
      .catch(() => setActiveStrategyDetail({ symbol: activeEtfDetail, empty: true }))
      .finally(() => setActiveStrategyLoading(false));
  }, [activeEtfDetail, activeEtfDetailTab]); // eslint-disable-line

  useEffect(() => {
    if (!activeEtfDetail) return;
    if (activeEtfHistory && activeEtfHistory.symbol === activeEtfDetail) return;
    setActiveEtfHistoryLoading(true);
    setActiveEtfSelectedStock(null);
    fetch(`${API}/etf-holdings-history/${encodeURIComponent(activeEtfDetail)}`)
      .then((r) => r.json())
      .then((d) => { setActiveEtfHistory(d); if (d.stocks && d.stocks.length) setActiveEtfSelectedStock(d.stocks[0].stock_code); })
      .catch(() => {})
      .finally(() => setActiveEtfHistoryLoading(false));
  }, [activeEtfDetail]); // eslint-disable-line

  useEffect(() => {
    if (!activeEtfSelectedStock || activeEtfDetailTab !== "stock") { setActiveStockPrices(null); return; }
    setStockChartZoom({ start: 0, end: 1 });
    setStockChartHover(null);
    setStockChartYScale(1);
    let cancelled = false;
    const code = activeEtfSelectedStock;
    let yahooSym = code;
    const EXCHANGE_SUFFIX = { TT: ".TW", JP: ".T", HK: ".HK", LN: ".L", GR: ".DE", GY: ".DE", FP: ".PA", NA: ".AS", SM: ".MC", IM: ".MI", SJ: ".JO", AU: ".AX", SP: ".SI", KS: ".KS" };
    if (/^\d{4}[A-Z]?$/.test(code)) yahooSym = `${code}.TW`;
    else if (code.includes(" ")) { const [t, ex] = code.split(" "); yahooSym = EXCHANGE_SUFFIX[ex] ? `${t}${EXCHANGE_SUFFIX[ex]}` : t; }
    setActiveStockPrices({ loading: true });
    fetch(`${API}/batch-chart?symbols=${encodeURIComponent(yahooSym)}&range=1y&interval=1d`)
      .then((r) => r.ok ? r.json() : {})
      .then((data) => {
        if (cancelled) return;
        const chart = data[yahooSym];
        const prices = chart ? (chart.timestamp || []).map((t, i) => ({
          date: new Date(t * 1000).toISOString().slice(0, 10),
          close: chart.indicators?.quote?.[0]?.close?.[i],
        })).filter((p) => p.close != null) : [];
        setActiveStockPrices({ loading: false, yahooSymbol: yahooSym, prices });
      })
      .catch(() => { if (!cancelled) setActiveStockPrices(null); });
    return () => { cancelled = true; };
  }, [activeEtfSelectedStock, activeEtfDetailTab]); // eslint-disable-line

  useEffect(() => {
    const el = stockChartRef.current;
    if (!el) return;
    const W = 700, PAD_R = 50;
    const wheelHandler = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const relX = e.clientX - rect.left;
      const rightEdge = rect.width * (1 - PAD_R / W);
      if (relX > rightEdge) {
        const dir = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        setStockChartYScale((prev) => Math.max(0.15, Math.min(20, prev * dir)));
        return;
      }
      const zoomDir = e.deltaY < 0 ? -1 : 1;
      setStockChartZoom((prev) => {
        const span = prev.end - prev.start;
        const delta = span * 0.12 * zoomDir;
        const mid = (prev.start + prev.end) / 2;
        let ns = mid - (span + delta) / 2;
        let ne = mid + (span + delta) / 2;
        if (ne - ns < 0.05) { ns = mid - 0.025; ne = mid + 0.025; }
        if (ns < 0) { ne -= ns; ns = 0; }
        if (ne > 1) { ns -= (ne - 1); ne = 1; }
        return { start: Math.max(0, ns), end: Math.min(1, ne) };
      });
    };
    const mousedownHandler = (e) => {
      if (e.button !== 0) return;
      const rect = el.getBoundingClientRect();
      const relX = e.clientX - rect.left;
      const rightEdge = rect.width * (1 - PAD_R / W);
      if (relX > rightEdge) {
        stockChartDragRef.current = { type: "yStretch", startY: e.clientY, startScale: stockChartYScale };
      } else {
        stockChartDragRef.current = { type: "pan", startX: e.clientX, startZoom: { ...stockChartZoom } };
      }
      e.preventDefault();
    };
    const mousemoveHandler = (e) => {
      const drag = stockChartDragRef.current;
      if (!drag) return;
      if (drag.type === "pan") {
        const rect = el.getBoundingClientRect();
        const chartPx = rect.width * ((W - 56 - PAD_R) / W);
        const span = drag.startZoom.end - drag.startZoom.start;
        const shift = -((e.clientX - drag.startX) / chartPx) * span;
        let ns = drag.startZoom.start + shift;
        let ne = drag.startZoom.end + shift;
        if (ns < 0) { ne -= ns; ns = 0; }
        if (ne > 1) { ns -= (ne - 1); ne = 1; }
        setStockChartZoom({ start: Math.max(0, ns), end: Math.min(1, ne) });
      } else if (drag.type === "yStretch") {
        const dy = drag.startY - e.clientY;
        const scale = drag.startScale * Math.pow(1.008, dy);
        setStockChartYScale(Math.max(0.15, Math.min(20, scale)));
      }
    };
    const mouseupHandler = () => { stockChartDragRef.current = null; };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    el.addEventListener("mousedown", mousedownHandler);
    window.addEventListener("mousemove", mousemoveHandler);
    window.addEventListener("mouseup", mouseupHandler);
    return () => {
      el.removeEventListener("wheel", wheelHandler);
      el.removeEventListener("mousedown", mousedownHandler);
      window.removeEventListener("mousemove", mousemoveHandler);
      window.removeEventListener("mouseup", mouseupHandler);
    };
  });

  // 登入後載入自選清單；登出則清空
  useEffect(() => {
    if (!session || !supabase) { setWatchlist([]); setWatchlistId(null); return; }
    let cancelled = false;
    (async () => {
      const { data: existing, error: listError } = await supabase
        .from("watchlists")
        .select("id")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: true })
        .limit(1);
      if (cancelled) return;
      if (listError) {
        console.warn("[watchlists] load failed:", listError.message);
        setAuthMessage(`自選清單讀取失敗：${listError.message}`);
        return;
      }

      let listId = existing?.[0]?.id || null;
      if (!listId) {
        const { data: created, error: createError } = await supabase
          .from("watchlists")
          .insert({ user_id: session.user.id, name: "我的自選" })
          .select("id")
          .single();
        if (cancelled) return;
        if (createError) {
          console.warn("[watchlists] create failed:", createError.message);
          setAuthMessage(`自選清單建立失敗：${createError.message}`);
          return;
        }
        listId = created?.id || null;
      }

      setWatchlistId(listId);
      if (!listId) { setWatchlist([]); return; }

      const { data, error } = await supabase
        .from("watchlist_items")
        .select("symbol")
        .eq("watchlist_id", listId)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        console.warn("[watchlist_items] load failed:", error.message);
        setAuthMessage(`自選 ETF 讀取失敗：${error.message}`);
        return;
      }
      setWatchlist((data || []).map((r) => String(r.symbol).toUpperCase()));
    })();
    return () => { cancelled = true; };
  }, [session]);

  const ensureDefaultWatchlist = useCallback(async () => {
    if (!supabase || !session) return null;
    const { data: existing, error: listError } = await supabase
      .from("watchlists")
      .select("id")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: true })
      .limit(1);

    if (listError) {
      console.warn("[watchlists] load failed:", listError.message);
      setAuthMessage(`自選清單讀取失敗：${listError.message}`);
      return null;
    }
    if (existing?.[0]?.id) return existing[0].id;

    const { data: created, error: createError } = await supabase
      .from("watchlists")
      .insert({ user_id: session.user.id, name: "我的自選" })
      .select("id")
      .single();

    if (createError) {
      console.warn("[watchlists] create failed:", createError.message);
      setAuthMessage(`自選清單建立失敗：${createError.message}`);
      return null;
    }
    return created?.id || null;
  }, [session]);

  const isWatchlisted = useCallback(
    (symbol) => watchlist.includes(String(symbol || "").toUpperCase()),
    [watchlist]
  );

  const toggleWatchlist = useCallback(async (symbol, marketHint = market) => {
    if (!supabase || !session) {
      setMobileAuthOpen(true);
      setAuthMessage("請先登入，才能使用自選 ETF。");
      return;
    }
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    const listId = watchlistId || await ensureDefaultWatchlist();
    if (!listId) {
      setAuthMessage("自選清單尚未建立，請稍後再試。");
      return;
    }
    if (!watchlistId) setWatchlistId(listId);
    if (watchlist.includes(sym)) {
      setWatchlist((prev) => prev.filter((s) => s !== sym)); // 樂觀更新
      const { error } = await supabase.from("watchlist_items").delete().eq("watchlist_id", listId).eq("symbol", sym);
      if (error) {
        console.warn("[watchlist_items] remove failed:", error.message);
        showToast(`移出自選失敗：${error.message}`, "error");
        setWatchlist((prev) => [...prev, sym]);
      } else {
        showToast(`${displaySymbol(sym)} 已移出自選`);
      }
    } else {
      setWatchlist((prev) => [sym, ...prev]); // 樂觀更新
      const rowMarket = marketHint || (/^\d/.test(sym) || /\.TWO?$/i.test(sym) ? "TW" : "US");
      const { error } = await supabase.from("watchlist_items").insert({ watchlist_id: listId, symbol: sym, market: rowMarket });
      if (error && error.code !== "23505") {
        console.warn("[watchlist_items] add failed:", error.message);
        showToast(`加入自選失敗：${error.message}`, "error");
        setWatchlist((prev) => prev.filter((s) => s !== sym));
      } else {
        showToast(`${displaySymbol(sym)} 已加入自選 ★`);
      }
    }
  }, [session, watchlist, market, watchlistId, ensureDefaultWatchlist]);

  const navBtn = (id, label, className = "nav-button", styleOverrides = {}) => (
    <button
      className={`etf-btn ${className} ${view === id ? "is-active" : ""}`}
      onClick={() => setView(id)}
      style={{
        padding: "7px 18px",
        fontSize: 12,
        fontWeight: 700,
        fontFamily: "'Fira Code', monospace",
        background: view === id ? C.accentDim : C.card,
        color: view === id ? C.accent : C.t2,
        border: `1px solid ${view === id ? C.accent : C.border}`,
        borderRadius: 8,
        cursor: "pointer",
        letterSpacing: 0.3,
        ...styleOverrides,
      }}
    >
      {label}
    </button>
  );

  // 需登入才能使用的功能，未登入時顯示提示面板
  const renderLoginRequired = (title, desc) => (
    <div style={{ animation: "fadeUp 0.3s ease-out", maxWidth: 520, margin: "40px auto 0", textAlign: "center" }}>
      <div style={{ background: `linear-gradient(135deg, ${C.card} 0%, ${C.panel} 100%)`, border: `1px solid ${C.border}`, borderRadius: 14, padding: "40px 32px" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.8, marginBottom: 22 }}>{desc}</div>
        {SHOW_AUTH && (
        <button
          onClick={() => setMobileAuthOpen(true)}
          style={{ padding: "10px 28px", fontSize: 13, fontWeight: 700, background: C.accent, color: C.bg, border: "none", borderRadius: 8, cursor: "pointer" }}
        >
          登入 / 註冊
        </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100vh", minWidth: 0, color: C.t1 }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { background: ${C.bg}; transition: background 0.25s ease; }
        /* 全站放大字級／介面，方便閱讀 */
        body { zoom: 1.15; }
        @supports not (zoom: 1) { body { font-size: 115%; } }
        ::-webkit-scrollbar { width: 5px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        .holding-metrics::-webkit-scrollbar { height: 3px; }
        .holding-metrics { scrollbar-width: thin; scrollbar-color: ${C.border} transparent; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        @keyframes toastIn { from { opacity: 0; transform: translateY(-8px) scale(0.96); } to { opacity: 1; transform: none; } }
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
        @keyframes auroraDrift { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(42px,-30px) scale(1.12); } }
        @keyframes blinkDot { 50% { opacity: 0.35; } }
        .aurora-field { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
        .aurora-field span { position: absolute; border-radius: 50%; filter: blur(95px); mix-blend-mode: screen; animation: auroraDrift 22s ease-in-out infinite; }
        .app-shell { position: relative; z-index: 1; }
        .hero-eyebrow .dot { animation: blinkDot 1.8s infinite; }
        .hero-panel .live i { animation: blinkDot 1.4s infinite; }
        .abc-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .abc-card:hover { transform: translateY(-5px); }
        .hero-headline { font-family: 'Fraunces', 'Bitter', serif; }
        @media (max-width: 860px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 22px !important; }
          .hero-headline { font-size: 44px !important; }
        }
        @media (max-width: 480px) {
          .hero-headline { font-size: 38px !important; }
        }
        input:focus, select:focus { outline: none; border-color: ${C.accent} !important; }
        input:focus-visible, select:focus-visible, button:focus-visible, a:focus-visible { outline: 2px solid ${C.accent}66; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; } }
        td, .results-grid, .holding-search-header { font-variant-numeric: tabular-nums; }
        /* ── 按鈕互動（Apple 風）── */
        .etf-btn {
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", system-ui, sans-serif !important;
          letter-spacing: 0 !important;
          white-space: nowrap !important;
          transition: transform 0.18s cubic-bezier(0.2,0.7,0.3,1), border-color 0.15s ease, background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
        }
        .etf-btn:hover { border-color: ${C.accent}99 !important; }
        .etf-btn:active { transform: scale(0.96); }
        .etf-btn:focus-visible { outline: 2px solid ${C.accent}66; outline-offset: 2px; }

        /* ── iOS Segmented Control ── */
        .seg-track {
          display: inline-flex;
          gap: 2px;
          padding: 2px !important;
          background: ${C.segTrack} !important;
          border: none !important;
          border-radius: 10px !important;
        }
        .seg-btn {
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", system-ui, sans-serif !important;
          font-weight: 600 !important;
          letter-spacing: 0 !important;
          background: transparent !important;
          border: none !important;
          border-radius: 7px !important;
          color: ${C.t2} !important;
          box-shadow: none !important;
          cursor: pointer;
          white-space: nowrap !important;
          flex: 0 0 auto;
          text-align: center;
          transition: transform 0.18s cubic-bezier(0.2,0.7,0.3,1), background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .seg-btn:active { transform: scale(0.94); }
        .seg-btn:focus-visible { outline: 2px solid ${C.accent}66; outline-offset: 2px; }
        .kbd-card:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
        .seg-btn.is-active {
          background: ${C.segActive} !important;
          color: ${C.accent} !important;
          box-shadow: ${C.segShadow} !important;
        }
        .stat-card[data-tip] { position: relative; }
        .stat-card[data-tip]::after {
          content: attr(data-tip);
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: calc(100% + 8px);
          z-index: 20;
          padding: 9px 11px;
          background: ${C.tooltip};
          border: 1px solid ${C.border};
          border-radius: 7px;
          color: ${C.t1};
          font: 12px/1.55 'Bitter', serif;
          box-shadow: 0 12px 30px ${C.shadow};
          opacity: 0;
          pointer-events: none;
          transform: translateY(4px);
          transition: opacity 0.15s ease, transform 0.15s ease;
        }
        .stat-card[data-tip]::before {
          content: "";
          position: absolute;
          left: 22px;
          bottom: calc(100% + 2px);
          border: 6px solid transparent;
          border-top-color: ${C.tooltip};
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease;
        }
        .stat-card[data-tip]:hover::after,
        .stat-card[data-tip]:focus-visible::after,
        .stat-card[data-tip]:hover::before,
        .stat-card[data-tip]:focus-visible::before {
          opacity: 1;
          transform: translateY(0);
        }
        .mobile-account-toggle { display: none; }
        .market-switch,
        .primary-nav,
        .secondary-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .market-switch {
          padding: 2px;
          gap: 2px;
          background: ${C.segTrack};
          border: none;
          border-radius: 10px;
        }
        .seg-btn--warm.is-active { color: ${C.warm} !important; }
        .primary-nav {
          padding: 2px;
          gap: 2px;
          background: ${C.segTrack};
          border: none;
          border-radius: 10px;
        }
        .secondary-nav {
          padding-left: 4px;
        }
        .header-divider {
          width: 1px;
          align-self: stretch;
          min-height: 28px;
          background: ${C.border};
          opacity: 0.9;
        }
        /* 761px 以上一律把帳號/登入收進下拉：header-inner 有 max-width:1200，寬螢幕並不會多出空間，
           固定寬度的登入表單（2 個 input + 4 個按鈕，全部 flex-shrink:0）在任何視窗寬度都可能與 nav 重疊，
           所以不能只在「中間寬度」套用，必須整段桌機寬度都收合。 */
        @media (min-width: 761px) {
          .app-header-inner { gap: 8px !important; }
          .primary-nav { gap: 4px !important; }
          .secondary-nav { gap: 4px !important; }
          .header-divider { display: none !important; }
          .market-button { padding: 6px 9px !important; min-width: 44px !important; }
          .primary-nav .seg-btn { padding: 7px 11px !important; }
          .mobile-account-toggle {
            display: inline-flex !important;
            align-items: center;
            white-space: nowrap;
            flex-shrink: 0;
            padding: 8px 10px;
            font-size: 12px;
            font-weight: 700;
            font-family: 'Fira Code', monospace;
            background: ${C.accent};
            color: ${C.bg};
            border: 1px solid ${C.accent};
            border-radius: 8px;
            cursor: pointer;
          }
          .auth-controls {
            display: none !important;
            position: absolute !important;
            top: calc(100% + 8px) !important;
            right: 16px !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
            width: 240px !important;
            padding: 12px !important;
            background: ${C.card} !important;
            border: 1px solid ${C.border} !important;
            border-radius: 12px !important;
            box-shadow: 0 10px 30px ${C.shadow} !important;
            z-index: 200 !important;
          }
          .auth-controls.auth-open { display: flex !important; }
          .auth-input { width: 100% !important; min-width: 0 !important; box-sizing: border-box; flex-shrink: 1 !important; }
          .auth-controls button { width: 100% !important; white-space: nowrap !important; }
        }
        @media (max-width: 760px) {
          html, body { width: 100%; overflow-x: hidden; }
          button, input, select { min-height: 40px; }
          input, select { font-size: 16px !important; }
          .app-header {
            padding: 12px 12px !important;
          }
          .app-header-inner {
            flex-direction: column !important;
            align-items: stretch !important;
            flex-wrap: wrap !important;
            gap: 10px !important;
          }
          .brand {
            width: 100%;
            justify-content: space-between;
            gap: 8px !important;
          }
          .brand-logo {
            width: 38px !important;
            height: 38px !important;
          }
          .hero-slogan {
            padding: 12px 10px 10px !important;
            margin-bottom: 12px !important;
          }
          .hero-slogan div {
            font-size: 12px !important;
          }
          .header-actions {
            width: 100%;
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 7px !important;
            align-items: stretch !important;
          }
          .header-divider { display: none !important; }
          .header-spacer { display: none !important; }
          .market-switch {
            grid-column: 1 / -1;
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 3px !important;
            padding: 3px !important;
            border-radius: 14px !important;
            background: ${C.bg} !important;
          }
          .primary-nav {
            grid-column: 1 / -1;
            min-width: 0;
            display: flex !important;
            flex-wrap: nowrap !important;
            gap: 6px !important;
            padding: 0 !important;
            background: transparent !important;
            border: 0 !important;
            overflow-x: auto;
            scrollbar-width: none;
          }
          .primary-nav::-webkit-scrollbar { display: none; }
          .secondary-nav {
            grid-column: 1;
            display: flex !important;
            gap: 6px !important;
            padding-left: 0 !important;
          }
          .market-button {
            width: 100%;
            min-height: 42px !important;
            padding: 6px 8px !important;
            font-size: 13px !important;
            border-radius: 11px !important;
          }
          .nav-button {
            width: auto !important;
            min-width: 72px;
            flex: 0 0 auto;
            min-height: 38px !important;
            padding: 7px 12px !important;
            font-size: 12px !important;
            border-radius: 10px !important;
            background: ${C.card} !important;
            color: ${C.t2} !important;
            border-color: ${C.border} !important;
            box-shadow: none !important;
          }
          .nav-button.is-active {
            background: ${C.accentDim} !important;
            color: ${C.accent} !important;
            border-color: ${C.accent} !important;
          }
          .secondary-nav-button {
            width: 58px !important;
            min-height: 38px !important;
            padding: 7px 8px !important;
            font-size: 12px !important;
            border-radius: 10px !important;
          }
          .theme-toggle {
            width: 42px !important;
            min-height: 38px !important;
            padding: 0 !important;
            border-radius: 10px !important;
          }
          .auth-controls {
            grid-column: 1 / -1;
            display: none !important;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 6px !important;
            width: 100%;
            padding: 8px;
            background: ${C.bg};
            border: 1px solid ${C.border};
            border-radius: 10px;
          }
          .auth-controls.auth-open {
            display: grid !important;
          }
          .auth-input {
            width: 100% !important;
            min-width: 0;
          }
          .auth-controls button {
            width: 100%;
          }
          .auth-message {
            grid-column: 1 / -1;
            text-align: left !important;
            padding: 0 2px;
          }
          .mobile-account-toggle {
            display: block !important;
            grid-column: 2;
            width: auto;
            min-width: 68px;
            min-height: 38px !important;
            padding: 7px 10px !important;
            background: ${C.card};
            color: ${C.t2};
            border: 1px solid ${C.border};
            border-radius: 10px;
            font: 800 11px 'Fira Code', monospace;
            cursor: pointer;
          }
          .app-main {
            padding: 12px 10px 80px !important;
            max-width: none !important;
          }
          .stat-row {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px !important;
            margin-bottom: 16px !important;
          }
          .summary-tile:first-child {
            grid-column: 1 / -1;
          }
          .summary-tile {
            min-width: 0;
            padding: 11px 10px !important;
          }
          .summary-tile div:nth-child(2) {
            font-size: 22px !important;
          }
          .period-toolbar,
          .category-pills {
            width: 100%;
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto;
            justify-content: flex-start !important;
            padding-bottom: 4px;
            scrollbar-width: none;
          }
          .period-toolbar::-webkit-scrollbar,
          .category-pills::-webkit-scrollbar { display: none; }
          .period-toolbar button,
          .category-pills button {
            flex: 0 0 auto;
          }
          .top-count-control {
            width: 100%;
          }
          .movers-grid,
          .results-grid {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
          .category-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .search-controls {
            display: grid !important;
            grid-template-columns: 1fr 1fr;
            gap: 8px !important;
          }
          .search-controls input {
            grid-column: 1 / -1;
            width: 100%;
          }
          .reverse-search-form {
            display: grid !important;
            grid-template-columns: 1fr;
          }
          .reverse-search-form button {
            width: 100%;
          }
          .holding-search-header {
            display: none !important;
          }
          .holding-search-row {
            grid-template-columns: 62px minmax(0, 1fr) 72px !important;
            gap: 8px !important;
            padding: 10px 0 !important;
          }
          .holding-search-row > div:nth-child(3) {
            grid-column: 2 / -1;
            grid-row: 2;
          }
          .detail-overlay {
            align-items: stretch !important;
            padding: 0 !important;
          }
          .detail-panel {
            max-width: none !important;
            max-height: none !important;
            min-height: 100vh;
            border-radius: 0 !important;
            border-left: 0 !important;
            border-right: 0 !important;
          }
          .detail-header,
          .detail-price,
          .detail-crumb,
          .detail-body {
            padding-left: 14px !important;
            padding-right: 14px !important;
          }
          .detail-tabs {
            padding-left: 14px !important;
            padding-right: 14px !important;
            overflow-x: auto;
            flex-wrap: nowrap !important;
            scrollbar-width: none;
          }
          .detail-tabs::-webkit-scrollbar { display: none; }
          .detail-tabs button {
            flex: 0 0 auto;
          }
          .tech-chart-shell {
            padding: 8px !important;
          }
          .tech-chart-svg {
            min-width: 520px !important;
          }
          .tech-max-overlay {
            padding: 0 !important;
          }
          .tech-max-panel {
            border-radius: 0 !important;
            padding: 10px !important;
          }
          .holding-row {
            display: grid !important;
            grid-template-columns: 24px minmax(0, 1fr);
            align-items: start !important;
            gap: 8px !important;
            overflow-x: visible !important;
          }
          .holding-row > div:last-child {
            grid-column: 2;
            justify-self: end;
          }
          .holding-metrics {
            grid-column: 1 / -1;
            width: 100% !important;
            overflow-x: auto;
            grid-template-columns: 64px 76px repeat(5, 72px) !important;
            padding-bottom: 4px;
          }
          .compare-bar {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto;
            gap: 8px !important;
            padding: 8px 10px calc(8px + env(safe-area-inset-bottom)) !important;
            align-items: center !important;
            scrollbar-width: none;
          }
          .compare-bar::-webkit-scrollbar {
            display: none;
          }
          .compare-spacer {
            display: none !important;
          }
          .compare-bar > div:not(.compare-spacer) {
            flex: 0 0 auto;
            max-width: 96px;
            overflow: hidden;
          }
          .compare-bar > button {
            flex: 0 0 auto;
            min-height: 34px !important;
            padding: 6px 10px !important;
          }
        }
        @media (max-width: 420px) {
          .category-grid {
            grid-template-columns: 1fr !important;
          }
          .summary-tile div:nth-child(3) {
            font-size: 10px !important;
          }
        }
      `}</style>

      {/* ── Azure Aurora background ── */}
      <div className="aurora-field" aria-hidden="true">
        <span style={{ width: 660, height: 660, top: -170, left: -130, opacity: C.auroraOpacity, background: `radial-gradient(circle, ${C.aurora1}, transparent 65%)` }} />
        <span style={{ width: 560, height: 560, top: 110, right: -150, opacity: C.auroraOpacity, background: `radial-gradient(circle, ${C.aurora2}, transparent 65%)`, animationDelay: "-6s" }} />
        <span style={{ width: 520, height: 520, top: 620, left: "34%", opacity: C.auroraOpacity * 0.62, background: `radial-gradient(circle, ${C.aurora3}, transparent 66%)`, animationDelay: "-12s" }} />
      </div>
      <div className="aurora-field" aria-hidden="true" style={{ zIndex: 0, opacity: C.grainOpacity, mixBlendMode: theme === "dark" ? "screen" : "multiply", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      {/* Header */}
      <header className="app-header" style={{
        padding: "12px 24px",
        borderBottom: `1px solid ${C.headerBorder}`,
        background: C.headerBg,
        backdropFilter: "blur(16px) saturate(1.3)",
        WebkitBackdropFilter: "blur(16px) saturate(1.3)",
        boxShadow: `0 2px 12px ${C.shadow}`,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}>
        <div className="app-header-inner" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap", gap: 10, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <div
          onClick={() => { setView("dashboard"); setSearch(""); setCat("全部"); setMoveFilter("all"); }}
          className="brand"
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flexShrink: 0 }}
          title="回到首頁"
        >
          <div style={{ width: 54, height: 54, borderRadius: 15, display: "grid", placeItems: "center", flexShrink: 0,
            background: `radial-gradient(120% 120% at 30% 20%, ${C.accent}22, ${C.glass})`,
            border: `1px solid ${C.glassBrd}`, boxShadow: `0 8px 24px ${C.accent}22` }}>
            <img
              className="brand-logo"
              src="/logo-etfowl.png"
              alt="ETFowl"
              style={{ width: 42, height: 42, display: "block", flexShrink: 0, objectFit: "contain", filter: "drop-shadow(0 2px 5px rgba(0,0,0,.3))" }}
            />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 25, fontWeight: 900, fontFamily: "'Fraunces', 'Bitter', serif", color: C.t1, letterSpacing: -0.3, lineHeight: 1 }}>
                ETF<span style={{ color: C.accent }}>owl</span>
              </span>
            </div>
            <div style={{ fontSize: 9.5, color: C.t3, fontFamily: "'Fira Code', monospace", letterSpacing: 2.5, marginTop: 3, whiteSpace: "nowrap" }}>
              {mktCfg.label}{closeCoverageText}
            </div>
          </div>
        </div>
        <div className="header-actions" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flex: 1 }}>
          <div className="market-switch" aria-label="市場切換">
            {["US", "TW"].map((m) => (
              <button
                className={`market-button seg-btn seg-btn--warm ${market === m ? "is-active" : ""}`}
                key={m}
                onClick={() => switchMarket(m)}
                style={{
                  padding: "6px 13px",
                  minWidth: 52,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {MARKET_BUTTON_LABELS[m] || m}
              </button>
            ))}
          </div>
          <div className="header-divider" />
          <nav className="primary-nav seg-track" aria-label="主要頁面">
            {navBtn("dashboard", "總覽", "seg-btn")}
            {navBtn("search", "搜尋", "seg-btn")}
            {navBtn("watchlist", "自選", "seg-btn")}
            {navBtn("recommend", "推薦", "seg-btn")}
            {navBtn("report", "報告", "seg-btn")}
          </nav>
          <div className="secondary-nav">
            {navBtn("learn", "學習", "secondary-nav-button", {
              background: view === "learn" ? C.accentDim : C.card,
              color: view === "learn" ? C.accent : C.t2,
              borderColor: view === "learn" ? C.accent : C.border,
              padding: "7px 16px",
            })}
            {isAdmin && navBtn("admin", "管理", "secondary-nav-button", {
              background: view === "admin" ? C.accentDim : C.card,
              color: view === "admin" ? C.accent : C.t2,
              borderColor: view === "admin" ? C.accent : C.border,
              padding: "7px 16px",
            })}
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            title={theme === "dark" ? "切換淺色模式" : "切換深色模式"}
            style={{
              padding: "7px 10px",
              fontSize: 14,
              background: C.card,
              color: C.t2,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              cursor: "pointer",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {theme === "dark" ? "☀️" : "\u{1F319}"}
          </button>
          </div>
          <div className="header-spacer" style={{ flex: 1 }} />
          {SHOW_AUTH && (<>
          <button
            type="button"
            className="mobile-account-toggle"
            aria-expanded={mobileAuthOpen}
            onClick={() => setMobileAuthOpen((open) => !open)}
          >
            {session ? "帳戶" : "登入 / 註冊"}
          </button>
          <div className={`auth-controls ${mobileAuthOpen ? "auth-open" : ""}`} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap", flexShrink: 0 }}>
            {!isSupabaseConfigured ? (
              <span style={{ color: C.t3, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>資料庫未設定</span>
            ) : session ? (
              <>
                <span style={{ color: C.t2, fontSize: 10, fontFamily: "'Fira Code', monospace", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {session.user?.email}
                </span>
                <button
                  onClick={() => { setMobileAuthOpen(false); handleSignOut(); }}
                  style={{ padding: "7px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: C.card, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer" }}
                >
                  登出
                </button>
              </>
            ) : (
              <>
                <input
                  className="auth-input"
                  type="email"
                  placeholder="電子信箱"
                  aria-label="電子信箱"
                  autoComplete="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  style={{ width: 140, flexShrink: 0, padding: "7px 9px", background: C.card, color: C.t1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, fontFamily: "'Fira Code', monospace" }}
                />
                <input
                  className="auth-input"
                  type="password"
                  placeholder="密碼"
                  aria-label="密碼"
                  autoComplete="current-password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAuthSubmit("login"); }}
                  style={{ width: 110, flexShrink: 0, padding: "7px 9px", background: C.card, color: C.t1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, fontFamily: "'Fira Code', monospace" }}
                />
                <button
                  onClick={() => handleAuthSubmit("login")}
                  disabled={authBusy}
                  style={{ padding: "7px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: C.accent, color: C.bg, border: `1px solid ${C.accent}`, borderRadius: 8, cursor: authBusy ? "wait" : "pointer", opacity: authBusy ? 0.65 : 1 }}
                >
                  登入
                </button>
                <button
                  onClick={() => handleAuthSubmit("signup")}
                  disabled={authBusy}
                  style={{ padding: "7px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: C.card, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 8, cursor: authBusy ? "wait" : "pointer", opacity: authBusy ? 0.65 : 1 }}
                >
                  註冊
                </button>
                <button
                  onClick={() => handleOAuthSignIn("google")}
                  disabled={authBusy}
                  title="使用 Google 登入"
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: "#fff", color: "#3c4043", border: `1px solid ${C.border}`, borderRadius: 8, cursor: authBusy ? "wait" : "pointer", opacity: authBusy ? 0.65 : 1 }}
                >
                  <GoogleG /> Google
                </button>
                <button
                  onClick={handlePasswordReset}
                  disabled={authBusy}
                  style={{ padding: "7px 4px", fontSize: 10, fontFamily: "'Fira Code', monospace", background: "none", color: C.t3, border: "none", textDecoration: "underline", cursor: authBusy ? "wait" : "pointer", whiteSpace: "nowrap" }}
                >
                  忘記密碼
                </button>
              </>
            )}
          </div>
          </>)}
        </div>
        {SHOW_AUTH && authMessage && (
          <div className="auth-message" style={{ width: "100%", textAlign: "right", color: authMessage.includes("已") || authMessage.includes("建立") ? C.up : C.warm, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>
            {authMessage}
          </div>
        )}
        </div>
      </header>

      {/* 密碼重設:點信中連結回站後輸入新密碼 */}
      {SHOW_AUTH && recoveryMode && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>設定新密碼</div>
            <div style={{ fontSize: 12, color: C.t3 }}>請輸入新的登入密碼(至少 6 碼)。</div>
            <input
              type="password"
              placeholder="新密碼"
              aria-label="新密碼"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdatePassword(); }}
              style={{ padding: "10px 12px", background: C.bg, color: C.t1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, fontFamily: "'Fira Code', monospace" }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleUpdatePassword} disabled={authBusy}
                style={{ flex: 1, padding: "10px 0", background: C.accent, color: C.bg, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: authBusy ? "wait" : "pointer", opacity: authBusy ? 0.65 : 1 }}>更新密碼</button>
              <button onClick={() => { setRecoveryMode(false); setNewPassword(""); setAuthMessage(""); }} disabled={authBusy}
                style={{ padding: "10px 14px", background: C.panel, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>取消</button>
            </div>
            {authMessage && <div style={{ fontSize: 12, color: authMessage.includes("已") ? C.up : C.warm, textAlign: "center" }}>{authMessage}</div>}
          </div>
        </div>
      )}

      {/* Proxy error */}
      {proxyError && (
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.down, fontFamily: "'Bitter', serif", marginBottom: 12 }}>
            無法連接 API 伺服器
          </div>
          <div style={{ fontSize: 13, color: C.t2, fontFamily: "'Fira Code', monospace", lineHeight: 2, maxWidth: 500, margin: "0 auto" }}>
            {isUsingWorkerApi ? (
              <>
                線上版會連到 Cloudflare Worker API：<br/>
                <span style={{ color: C.accent }}>{WORKER_API_FALLBACK}</span><br/><br/>
                請確認 Worker 已部署，且 <span style={{ color: C.accent }}>/api/health</span> 可以回應。
              </>
            ) : (
              <>
                本機開發請確認已啟動 Proxy Server：<br/>
                <span style={{ color: C.accent }}>node server.js</span><br/><br/>
                或用一個指令同時啟動：<br/>
                <span style={{ color: C.accent }}>npm start</span><br/><br/>
                Proxy Server 需要在 port 3001 運行
              </>
            )}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              padding: "10px 24px",
              background: C.accent,
              color: C.bg,
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "'Fira Code', monospace",
              cursor: "pointer",
            }}
          >
            重新連接
          </button>
        </div>
      )}

      {/* Loading bar */}
      {loading && !proxyError && (
        <div style={{ padding: "14px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 12, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 6, animation: "pulse 1.5s infinite" }}>
            載入盤後資料中… {pricedSyms.length}/{allSyms.length} 檔 ETF ({progress}%)
          </div>
          <div style={{ width: "100%", height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: "100%", height: "100%", background: C.accent, borderRadius: 2, transform: `scaleX(${progress / 100})`, transformOrigin: "left", transition: "transform 0.3s" }} />
          </div>
        </div>
      )}

      <main className="app-main" style={{ padding: "18px 24px", maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 1 }}>

        {/* ── DASHBOARD ── */}
        {view === "dashboard" && (
          <div style={{ animation: "fadeUp 0.3s ease-out" }}>
            {/* ── Hero band (Azure Aurora) ── */}
            <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.12fr 0.88fr", gap: 34, alignItems: "center", margin: "6px 0 34px" }}>
              <div>
                <span className="hero-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 9, fontFamily: "'Fira Code', monospace", fontSize: 12, letterSpacing: 1.5, color: C.accent, background: `${C.accent}14`, border: `1px solid ${C.accent}44`, borderRadius: 999, padding: "7px 15px", marginBottom: 22 }}>
                  <span className="dot" style={{ width: 7, height: 7, borderRadius: "50%", background: C.cyan, boxShadow: `0 0 12px ${C.cyan}`, flexShrink: 0 }} />
                  持續追蹤 {pricedSyms.length} 檔 ETF · 每日盤後 AI 解讀
                </span>
                <h1 className="hero-headline" style={{ fontWeight: 900, fontSize: 58, lineHeight: 1.04, letterSpacing: -1.4, color: C.t1, margin: 0 }}>
                  看懂市場,<br />從<span style={{ backgroundImage: `linear-gradient(115deg, ${C.sky}, ${C.cyan} 55%, ${C.indigo})`, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>一隻貓頭鷹</span><br />的<span style={{ fontStyle: "italic", fontWeight: 400 }}>視角</span>開始。
                </h1>
                <p style={{ fontSize: 17, lineHeight: 1.65, color: C.t2, maxWidth: 500, margin: "22px 0 28px" }}>
                  台股與美股 ETF 的盤後報價、溢價折價、主動式持股異動與白話 AI 分析,全部整合成一個好讀的儀表板。
                </p>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={() => { setRiskFilter("all"); setCat(market === "TW" ? "台股大盤" : "核心/大盤"); setMoveFilter("all"); setView("search"); }} style={{ fontWeight: 800, fontSize: 14.5, fontFamily: "'Outfit', sans-serif", color: theme === "dark" ? "#04162b" : "#ffffff", background: `linear-gradient(120deg, ${C.accent}, ${C.cyan})`, border: "none", borderRadius: 999, padding: "12px 24px", cursor: "pointer", boxShadow: `0 10px 28px ${C.accent}44` }}>開始觀察市場 →</button>
                  <button onClick={() => setView("learn")} style={{ fontWeight: 700, fontSize: 14.5, fontFamily: "'Outfit', sans-serif", color: C.t1, background: C.glass, border: `1px solid ${C.glassBrd}`, borderRadius: 999, padding: "12px 22px", cursor: "pointer" }}>ETF 新手教室</button>
                </div>
                <div style={{ display: "flex", gap: 30, marginTop: 30 }}>
                  {[{ b: `${pricedSyms.length}`, s: "ETF 追蹤" }, { b: "2", s: "市場 TW·US" }, { b: "18:00", s: "每日更新" }].map((t) => (
                    <div key={t.s}>
                      <div style={{ fontFamily: "'Fraunces','Bitter',serif", fontSize: 28, fontWeight: 900, color: C.t1, lineHeight: 1 }}>{t.b}</div>
                      <div style={{ fontFamily: "'Fira Code', monospace", fontSize: 10, letterSpacing: 0.8, color: C.t3, marginTop: 5 }}>{t.s}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* glass live panel */}
              <div className="hero-panel" style={{ position: "relative" }}>
                <div style={{ position: "relative", background: `linear-gradient(165deg, ${C.glass}, transparent)`, border: `1px solid ${C.glassBrd}`, borderRadius: 22, padding: 22, backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", boxShadow: `0 30px 70px ${C.shadow}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <b style={{ fontFamily: "'Fira Code', monospace", fontSize: 12, letterSpacing: 1, color: C.t3, fontWeight: 700 }}>{activePeriod.label}漲幅 TOP · {mktCfg.label}</b>
                    <span style={{ fontFamily: "'Fira Code', monospace", fontSize: 10.5, color: C.cyan, display: "flex", alignItems: "center", gap: 6 }}><i style={{ width: 6, height: 6, borderRadius: "50%", background: C.cyan, boxShadow: `0 0 8px ${C.cyan}`, display: "inline-block" }} />最新盤後</span>
                  </div>
                  {(gainers.length ? gainers.slice(0, 5) : pricedSyms.slice(0, 5)).map((s) => {
                    const q = quotes[s];
                    const ret = getReturnPct(q, returnPeriod);
                    return (
                      <div key={s} className="kbd-card" role="button" tabIndex={0} onClick={() => setSelected(s)} onKeyDown={onActivateKey(() => setSelected(s))} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 0", borderBottom: `1px solid ${C.glassBrd}`, cursor: "pointer" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, fontSize: 14, color: C.accent }}>{displaySymbol(s)}</span>
                          <div style={{ fontSize: 11, color: C.t3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mktCfg.names[s] || q?.name || ""}</div>
                        </div>
                        <Spark data={getSparkCloses(q, returnPeriod)} w={56} h={22} color={dirColor(ret, isTwSymbol(s))} />
                        <span style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, fontSize: 14, color: dirColor(ret, isTwSymbol(s)), minWidth: 60, textAlign: "right" }}>{formatReturnPct(ret)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ABC 用途入口卡:每個捷徑連到不同的真實目的地 */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: "'Fraunces','Bitter',serif", fontSize: 15, fontWeight: 600, letterSpacing: 0.5, color: C.t3, marginBottom: 14 }}>你想做什麼 · 選一個角度直接抵達</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
                {[
                  { key: "new", color: C.accent, icon: "🎓", title: "新手入門", desc: "先搞懂 ETF 怎麼運作、怎麼挑,從核心大盤清單開始。", actions: [
                    { label: "學習中心", on: () => setView("learn") },
                    { label: "核心大盤清單", on: () => { setRiskFilter("all"); setCat(market === "TW" ? "台股大盤" : "核心/大盤"); setView("search"); } },
                  ] },
                  { key: "active", color: C.cyan, icon: "🚀", title: "積極成長", desc: "追成長、抓波段與產業輪動,鎖定科技半導體與主動持股。", actions: [
                    { label: "全部積極型", on: () => { setCat("全部"); setRiskFilter("積極"); setView("search"); } },
                    { label: "科技/半導體", on: () => { setRiskFilter("all"); setCat(market === "TW" ? "科技/半導體" : "產業板塊"); setView("search"); } },
                    ...(market === "TW" ? [{ label: "主動持股異動", on: () => setView("active") }] : []),
                  ] },
                  { key: "conservative", color: C.indigo, icon: "🛡️", title: "穩健領息", desc: "求穩健、領息、低波動,搭配 AI 推薦挑選債券與高息。", actions: [
                    { label: "全部保守型", on: () => { setCat("全部"); setRiskFilter("保守"); setView("search"); } },
                    { label: "債券清單", on: () => { setRiskFilter("all"); setCat(market === "TW" ? "債券" : "債券/現金"); setView("search"); } },
                    { label: "AI 推薦", on: () => setView("recommend") },
                  ] },
                ].map((p) => (
                  <div key={p.key} className="abc-card" style={{ position: "relative", overflow: "hidden", background: `linear-gradient(160deg, ${C.glass}, transparent)`, border: `1px solid ${C.glassBrd}`, borderRadius: 20, padding: 24 }}>
                    <div style={{ position: "absolute", width: 170, height: 170, borderRadius: "50%", filter: "blur(58px)", top: -40, right: -40, opacity: 0.38, background: p.color, pointerEvents: "none" }} />
                    <div style={{ position: "relative" }}>
                      <div style={{ width: 50, height: 50, borderRadius: 14, display: "grid", placeItems: "center", fontSize: 25, marginBottom: 14, background: `${p.color}22` }}>{p.icon}</div>
                      <div style={{ fontFamily: "'Fraunces','Bitter',serif", fontSize: 22, fontWeight: 600, color: C.t1, marginBottom: 6 }}>{p.title}</div>
                      <div style={{ fontSize: 14, color: C.t2, lineHeight: 1.5, marginBottom: 16 }}>{p.desc}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {p.actions.map((a) => (
                          <button key={a.label} onClick={a.on} style={{ fontSize: 12.5, fontWeight: 700, background: C.glass, color: C.t1, borderRadius: 999, padding: "7px 13px", border: `1px solid ${C.glassBrd}`, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                            {a.label} →
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Stats Row */}
            <div className="stat-row" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
              {[
                { label: "追蹤 ETF", value: `${pricedSyms.length}`, sub: `${Object.keys(activeCats).length} 分類`, move: "all", premium: "all" },
                { label: "上漲", value: `${upCount}`, sub: `${activePeriod.label}報酬`, move: "up", premium: "all" },
                { label: "下跌", value: `${downCount}`, sub: `${activePeriod.label}報酬`, move: "down", premium: "all" },
                { label: "溢價較高", value: `${premiumCount}`, sub: `TOP ${Math.min(topCount, highPremiums.length)} 溢價率`, move: "all", premium: "premium" },
                { label: "折價較深", value: `${discountCount}`, sub: `TOP ${Math.min(topCount, deepDiscounts.length)} 折價率`, move: "all", premium: "discount" },
              ].map(({ label: l, value: v, sub: s, move: moveTarget, premium: premiumTarget }) => (
                <div
                  className="summary-tile"
                  key={l}
                  onClick={() => {
                    setMoveFilter(moveTarget);
                    setPremiumFilter(premiumTarget);
                    setSortBy(premiumTarget === "all" ? "changePct" : "premium");
                    setCat("全部");
                    setSearch("");
                    setView("search");
                  }}
                  style={{ flex: "1 1 160px", background: C.card, border: `1px solid ${(moveFilter === moveTarget && premiumFilter === premiumTarget) ? C.accent : C.border}`, borderRadius: 10, padding: "14px 18px", cursor: "pointer" }}
                  title={`查看${l} ETF`}
                >
                  <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", letterSpacing: 1, marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Bitter', serif" }}>{v}</div>
                  <div style={{ fontSize: 11, color: C.t3 }}>{s} · 點擊查看</div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>報酬區間</div>
                <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 2 }}>
                  TOP {topCount}、分類總覽與搜尋排序會同步更新 · 線圖={getSparkRangeLabel(activePeriod)}走勢
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                <div className="period-toolbar" style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {RETURN_PERIODS.map((period) => (
                    <button
                      key={period.id}
                      className={`etf-btn ${returnPeriod === period.id ? "is-active" : ""}`}
                      onClick={() => setReturnPeriod(period.id)}
                      style={{
                        padding: "6px 11px",
                        minWidth: 38,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "'Fira Code', monospace",
                        background: returnPeriod === period.id ? C.accentDim : C.card,
                        color: returnPeriod === period.id ? C.accent : C.t2,
                        border: `1px solid ${returnPeriod === period.id ? C.accent : C.border}`,
                        borderRadius: 7,
                        cursor: "pointer",
                      }}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
                <label className="top-count-control" style={{ display: "flex", alignItems: "center", gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: "5px 8px", color: C.t3, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>
                  TOP
                  <select
                    value={topCount}
                    onChange={(e) => setTopCount(Number(e.target.value))}
                    style={{ background: C.panel, color: C.t1, border: `1px solid ${C.border}`, borderRadius: 5, padding: "4px 6px", fontSize: 11, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                  >
                    {[5, 10, 15, 20, 30].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
            </div>

            {/* Movers */}
            <div className="movers-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 20 }}>
              {/* Gainers */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: dirColor(1, market === "TW"), fontFamily: "'Bitter', serif", marginBottom: 12 }}>▲ {activePeriod.label}漲幅 TOP {topCount}</div>
                {gainers.map((s) => {
                  const q = quotes[s];
                  const ret = getReturnPct(q, returnPeriod);
                  return (
                    <div key={s} className="kbd-card" role="button" tabIndex={0} onClick={() => setSelected(s)} onKeyDown={onActivateKey(() => setSelected(s))} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${C.border}22`, cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: C.accent, fontFamily: "'Fira Code', monospace" }}>{displaySymbol(s)}</span>
                          <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mktCfg.names[s] || q?.name || ""}</div>
                        </div>
                        <Spark data={getSparkCloses(q, returnPeriod)} w={50} h={18} color={dirColor(1, isTwSymbol(s))} />
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <span style={{ fontSize: 12, color: C.t3, fontFamily: "'Fira Code', monospace", marginRight: 8 }}>{currencySymbol}{q?.price?.toFixed(2)}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: dirColor(ret, isTwSymbol(s)), fontFamily: "'Fira Code', monospace" }}>{formatReturnPct(ret)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Losers */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: dirColor(-1, market === "TW"), fontFamily: "'Bitter', serif", marginBottom: 12 }}>▼ {activePeriod.label}跌幅 TOP {topCount}</div>
                {losers.map((s) => {
                  const q = quotes[s];
                  const ret = getReturnPct(q, returnPeriod);
                  return (
                    <div key={s} className="kbd-card" role="button" tabIndex={0} onClick={() => setSelected(s)} onKeyDown={onActivateKey(() => setSelected(s))} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${C.border}22`, cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: C.accent, fontFamily: "'Fira Code', monospace" }}>{displaySymbol(s)}</span>
                          <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mktCfg.names[s] || q?.name || ""}</div>
                        </div>
                        <Spark data={getSparkCloses(q, returnPeriod)} w={50} h={18} color={dirColor(ret, isTwSymbol(s))} />
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <span style={{ fontSize: 12, color: C.t3, fontFamily: "'Fira Code', monospace", marginRight: 8 }}>{currencySymbol}{q?.price?.toFixed(2)}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: dirColor(ret, isTwSymbol(s)), fontFamily: "'Fira Code', monospace" }}>{formatReturnPct(ret)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Category Grid */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>分類總覽</div>
                <div style={{ fontSize: 11.5, color: C.t2, marginTop: 3 }}>依主題快速瀏覽,看哪一類資金在流入、哪類在流出。點任一分類即進入該類清單。</div>
              </div>
              <div className="category-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                {Object.entries(activeCats).map(([c, syms]) => {
                  const loaded = syms.filter((t) => hasUsablePrice(quotes[t]));
                  if (!loaded.length) return null;
                  const avg = loaded.length ? loaded.reduce((sum, t) => sum + getReturnPct(quotes[t], returnPeriod), 0) / loaded.length : 0;
                  return (
                    <div key={c} onClick={() => { setRiskFilter("all"); setCat(c); setView("search"); }} style={{ background: C.panel, borderRadius: 8, padding: "12px 14px", cursor: "pointer", border: `1px solid ${C.border}`, transition: "border-color 0.2s" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 2 }}>{c}</div>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 6 }}>{loaded.length} ETFs</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: dirColor(avg, market === "TW"), fontFamily: "'Fira Code', monospace" }}>
                        {avg >= 0 ? "+" : ""}{avg.toFixed(2)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── SEARCH ── */}
        {view === "search" && (
          <div style={{ animation: "fadeUp 0.3s ease-out" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>個股反查 ETF</div>
                  <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 2 }}>輸入股票代號或公司名，找出目前追蹤 ETF 裡哪些持有它</div>
                </div>
              </div>
              <div className="reverse-search-form" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  type="text"
                  placeholder="例如 台積電、2330、AMD、NVIDIA"
                  aria-label="個股反查搜尋"
                  value={holdingQuery}
                  onChange={(e) => setHoldingQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runHoldingSearch(); }}
                  style={{
                    flex: "1 1 220px",
                    padding: "9px 14px",
                    background: C.panel,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    color: C.t1,
                    fontSize: 13,
                    fontFamily: "'Fira Code', monospace",
                  }}
                />
                <button
                  onClick={runHoldingSearch}
                  disabled={holdingSearching}
                  style={{
                    padding: "9px 14px",
                    background: C.accent,
                    color: C.bg,
                    border: "none",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 800,
                    fontFamily: "'Fira Code', monospace",
                    cursor: holdingSearching ? "wait" : "pointer",
                    opacity: holdingSearching ? 0.65 : 1,
                  }}
                >
                  {holdingSearching ? "掃描中…" : "搜尋持有ETF"}
                </button>
              </div>
              {holdingSearch && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace", marginBottom: 8 }}>
                    {holdingSearch.error ? `錯誤：${holdingSearch.error}` : `${holdingSearch.resolved?.name || holdingSearch.yahooSymbol || holdingSearch.query} (${holdingSearch.yahooSymbol || "未解析"}) · 掃描 ${holdingSearch.scanned} 檔 · 命中 ${holdingRows.length} 檔`}
                  </div>
                  {!holdingSearch.error && holdingRows.length > 0 && (
                    <div className="holding-search-header" style={{ display: "grid", gridTemplateColumns: "70px minmax(160px, 1fr) 170px 90px 80px", gap: 10, alignItems: "center", padding: "2px 0 6px", color: C.t3, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>
                      <div>{holdingSortHeader("etf", "ETF")}</div>
                      <div>{holdingSortHeader("name", "名稱")}</div>
                      <div>{holdingSortHeader("source", "資料來源")}</div>
                      <div>{holdingSortHeader("weight", "持股權重", "right", "此 ETF 投資組合中，該個股占整體資產的比例。")}</div>
                      <div>{holdingSortHeader("return", `${activePeriod.label}報酬`, "right", `此 ETF 在目前選取期間的價格報酬：${activePeriod.label}。`)}</div>
                    </div>
                  )}
                  {holdingPageRows.map((m) => {
                    const ret = m.ret;
                    return (
                      <div className="holding-search-row" key={`${m.etfSymbol}-${m.holdingSymbol}`} onClick={() => setSelected(m.etfSymbol)} style={{ display: "grid", gridTemplateColumns: "70px minmax(160px, 1fr) 170px 90px 80px", gap: 10, alignItems: "center", padding: "8px 0", borderTop: `1px solid ${C.border}55`, cursor: "pointer" }}>
                        <div style={{ color: C.accent, fontFamily: "'Fira Code', monospace", fontWeight: 800 }}>{displaySymbol(m.etfSymbol)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: C.t1, fontSize: 12, fontFamily: "'Bitter', serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
                        </div>
                        <div style={{ color: m.isFallback ? C.warm : C.t3, fontSize: 10, fontFamily: "'Fira Code', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={`${m.sourceName || ""}${m.asOf ? ` · ${m.asOf}` : ""}`}>{m.sourceName}{m.asOf ? ` · ${m.asOf}` : ""}</div>
                        <div style={{ color: C.t1, fontFamily: "'Fira Code', monospace", textAlign: "right", fontWeight: 700 }}>{m.weight != null ? `${(m.weight * 100).toFixed(2)}%` : "—"}</div>
                        <div style={{ color: dirColor(ret, isTwSymbol(m.etfSymbol)), fontFamily: "'Fira Code', monospace", textAlign: "right", fontWeight: 700 }}>{formatReturnPct(ret)}</div>
                      </div>
                    );
                  })}
                  {!holdingSearch.error && holdingRows.length > HOLDING_SEARCH_PAGE_SIZE && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}55` }}>
                      <div style={{ color: C.t3, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>
                        {((holdingPageSafe - 1) * HOLDING_SEARCH_PAGE_SIZE) + 1}-{Math.min(holdingPageSafe * HOLDING_SEARCH_PAGE_SIZE, holdingRows.length)} / {holdingRows.length}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => setHoldingPage((p) => Math.max(1, p - 1))}
                          disabled={holdingPageSafe <= 1}
                          style={{ padding: "5px 10px", background: C.panel, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 10, fontFamily: "'Fira Code', monospace", cursor: holdingPageSafe <= 1 ? "not-allowed" : "pointer", opacity: holdingPageSafe <= 1 ? 0.45 : 1 }}
                        >
                          上一頁
                        </button>
                        <div style={{ color: C.t3, fontSize: 10, fontFamily: "'Fira Code', monospace", alignSelf: "center" }}>
                          {holdingPageSafe}/{holdingTotalPages}
                        </div>
                        <button
                          type="button"
                          onClick={() => setHoldingPage((p) => Math.min(holdingTotalPages, p + 1))}
                          disabled={holdingPageSafe >= holdingTotalPages}
                          style={{ padding: "5px 10px", background: C.panel, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 10, fontFamily: "'Fira Code', monospace", cursor: holdingPageSafe >= holdingTotalPages ? "not-allowed" : "pointer", opacity: holdingPageSafe >= holdingTotalPages ? 0.45 : 1 }}
                        >
                          下一頁
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="search-controls" style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="搜尋代號或名稱…"
                aria-label="搜尋 ETF 代號或名稱"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  flex: "1 1 220px",
                  padding: "9px 14px",
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  color: C.t1,
                  fontSize: 13,
                  fontFamily: "'Fira Code', monospace",
                }}
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="排序方式"
                style={{
                  padding: "9px 12px",
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  color: C.t1,
                  fontSize: 12,
                  fontFamily: "'Fira Code', monospace",
                  cursor: "pointer",
                }}
              >
                <option value="changePct">漲跌幅</option>
                <option value="premium">溢價率</option>
                <option value="name">名稱</option>
                <option value="price">價格</option>
                <option value="volume">成交量</option>
              </select>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                aria-label="風險屬性篩選"
                style={{ padding: "9px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.t1, fontSize: 12, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
              >
                <option value="all">全部屬性</option>
                <option value="積極">🚀 積極</option>
                <option value="均衡">⚖️ 均衡</option>
                <option value="保守">🛡️ 保守</option>
              </select>
              <select
                value={returnPeriod}
                onChange={(e) => setReturnPeriod(e.target.value)}
                aria-label="報酬區間"
                style={{
                  padding: "9px 12px",
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  color: C.t1,
                  fontSize: 12,
                  fontFamily: "'Fira Code', monospace",
                  cursor: "pointer",
                }}
              >
                {RETURN_PERIODS.map((period) => (
                  <option key={period.id} value={period.id}>{period.label}</option>
                ))}
              </select>
            </div>

            <div className="category-pills" style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 14 }}>
              {cats.map((c) => (
                <button
                  key={c}
                  className={`etf-btn ${cat === c ? "is-active" : ""}`}
                  onClick={() => setCat(c)}
                  style={{
                    padding: "5px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "'Fira Code', monospace",
                    background: cat === c ? C.accentDim : C.card,
                    color: cat === c ? C.accent : C.t3,
                    border: `1px solid ${cat === c ? C.accent : C.border}`,
                    borderRadius: 16,
                    cursor: "pointer",
                  }}
                >
                  {c}
                </button>
              ))}
            </div>

            {(() => {
              const activeFilters = [];
              if (cat !== "全部") activeFilters.push({ label: cat, onClear: () => setCat("全部") });
              if (riskFilter !== "all") activeFilters.push({ label: `${riskFilter === "積極" ? "🚀" : riskFilter === "均衡" ? "⚖️" : "🛡️"} ${riskFilter}`, onClear: () => setRiskFilter("all") });
              if (moveFilter !== "all") activeFilters.push({ label: moveFilter === "up" ? `${activePeriod.label}上漲` : `${activePeriod.label}下跌`, onClear: () => setMoveFilter("all") });
              if (premiumFilter !== "all") activeFilters.push({ label: premiumFilter === "premium" ? "溢價" : "折價", onClear: () => setPremiumFilter("all") });
              if (!activeFilters.length) return null;
              return (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginRight: 2 }}>篩選中:</span>
                  {activeFilters.map((f, i) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: C.accentDim, color: C.accent, border: `1px solid ${C.accent}55`, borderRadius: 12, fontSize: 11, fontWeight: 600, fontFamily: "'Fira Code', monospace" }}>
                      {f.label}
                      <button onClick={f.onClear} aria-label={`清除篩選: ${f.label}`} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", opacity: 0.7, fontSize: 13, lineHeight: 1, color: "inherit" }}>×</button>
                    </span>
                  ))}
                  {activeFilters.length > 1 && (
                    <button onClick={() => { setCat("全部"); setRiskFilter("all"); setMoveFilter("all"); setPremiumFilter("all"); }} style={{ background: "none", border: "none", padding: 0, fontSize: 10, color: C.t3, cursor: "pointer", fontFamily: "'Fira Code', monospace", textDecoration: "underline", marginLeft: 2 }}>清除全部</button>
                  )}
                </div>
              );
            })()}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace" }}>
                {filtered.length} RESULTS
                {searchViewMode === "card" ? ` · 線圖=${getSparkRangeLabel(activePeriod)}走勢` : ""}
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {["card", "table"].map((m) => (
                  <button key={m} onClick={() => setSearchViewMode(m)} style={{
                    padding: "4px 8px", background: searchViewMode === m ? C.accent : "transparent",
                    color: searchViewMode === m ? "#fff" : C.t3, border: `1px solid ${searchViewMode === m ? C.accent : C.border}`,
                    borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'Fira Code', monospace",
                  }}>{m === "card" ? "▦ 卡片" : "☰ 表格"}</button>
                ))}
              </div>
            </div>

            {searchViewMode === "table" ? (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "72px 1fr 90px 80px 80px minmax(80px,1fr) 68px 58px 50px 50px", gap: "0 10px", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontWeight: 700, fontFamily: "'Fira Code', monospace" }}>
                  {[["name","代號"],["name","名稱"],["price","價格","right"],["changePct","漲跌%","right"],["volume","成交量","right"],[null,"主題"],[null,"風險"],["premium","溢價率","right"],[null,"自選","center"],[null,"比較","center"]].map(([field, label, align], i) => (
                    <div key={i} onClick={field ? () => toggleSort(field) : undefined} style={{ textAlign: align || "left", cursor: field ? "pointer" : "default", userSelect: "none", color: sortBy === field ? C.accent : C.t3 }}>{label}{field ? sortArrow(field) : ""}</div>
                  ))}
                </div>
                {filtered.map((s, i) => {
                  const q = quotes[s];
                  const chg = getReturnPct(q, returnPeriod);
                  const premiumInfo = getMarketPremiumInfo(s, q, premiumBySymbol);
                  const rp = computeRiskProfile(catMap[s], etfMetrics[s]?.beta, etfMetrics[s]?.yield);
                  const saved = isWatchlisted(s);
                  const isCompared = compareList.includes(s);
                  const compareIdx = compareList.indexOf(s);
                  return (
                    <div key={s} className="kbd-card" role="button" tabIndex={0} onClick={() => setSelected(s)} onKeyDown={onActivateKey(() => setSelected(s))} style={{
                      display: "grid", gridTemplateColumns: "72px 1fr 90px 80px 80px minmax(80px,1fr) 68px 58px 50px 50px", gap: "0 10px",
                      padding: "8px 14px", borderBottom: `1px solid ${C.border}44`, cursor: "pointer", alignItems: "center",
                      animation: `fadeUp 0.2s ease-out ${i * 0.01}s both`, transition: "background 0.15s",
                      background: isCompared ? `${COMPARE_COLORS[compareIdx]}0a` : "transparent",
                    }} onMouseEnter={(e) => { if (!isCompared) e.currentTarget.style.background = C.panel; }} onMouseLeave={(e) => { e.currentTarget.style.background = isCompared ? `${COMPARE_COLORS[compareIdx]}0a` : "transparent"; }}>
                      <div style={{ fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", fontSize: 13 }}>{displaySymbol(s)}</div>
                      <div style={{ fontSize: 11, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Fira Code', monospace" }}>{mktCfg.names[s] || q?.name || "…"}</div>
                      <div style={{ textAlign: "right", fontWeight: 800, fontFamily: "'Bitter', serif", fontSize: 13 }}>{currencySymbol}{q?.price?.toFixed(2) || "—"}</div>
                      <div style={{ textAlign: "right", fontWeight: 700, color: dirColor(chg, isTwSymbol(s)), fontFamily: "'Fira Code', monospace", fontSize: 12 }}>{formatReturnPct(chg)}</div>
                      <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 11, color: C.t3 }}>{q?.volume ? (q.volume >= 1e6 ? `${(q.volume / 1e6).toFixed(1)}M` : q.volume.toLocaleString()) : "—"}</div>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeThemes[s] || catMap[s]}</div>
                      <div><span style={{ fontSize: 9, fontWeight: 700, color: RISK_META[rp].color, background: `${RISK_META[rp].color}18`, borderRadius: 4, padding: "2px 5px" }}>{RISK_META[rp].icon} {rp}</span></div>
                      <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 11, fontWeight: 800, color: premiumInfo.premiumDiscountPct == null ? C.t3 : premiumInfo.premiumDiscountPct >= 0 ? C.warm : C.down }}>{formatPremiumShort(premiumInfo.premiumDiscountPct)}</div>
                      <div style={{ textAlign: "center" }}><button onClick={(e) => { e.stopPropagation(); toggleWatchlist(s, market); }} title={session ? (saved ? "移出自選" : "加入自選") : "登入後可加入自選"} aria-label={saved ? "移出自選" : "加入自選"} style={{ width: 36, height: 24, padding: 0, background: saved ? C.warm : "transparent", color: saved ? C.bg : C.t3, border: `1.5px solid ${saved ? C.warm : C.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer" }}>{saved ? "★" : "☆"}</button></div>
                      <div style={{ textAlign: "center" }}><button onClick={(e) => { e.stopPropagation(); toggleCompare(s); }} title={isCompared ? `取消比較 ${compareIdx + 1}` : compareList.length >= 4 ? "最多比較 4 檔" : "加入比較"} aria-label={isCompared ? "取消比較" : "加入比較"} style={{ width: 36, height: 24, padding: 0, background: isCompared ? COMPARE_COLORS[compareIdx] : "transparent", color: isCompared ? "#fff" : C.t3, border: `1.5px solid ${isCompared ? COMPARE_COLORS[compareIdx] : C.border}`, borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", opacity: !isCompared && compareList.length >= 4 ? 0.3 : 1, transition: "background 0.15s, color 0.15s, border-color 0.15s, opacity 0.15s" }}>{isCompared ? "✓" : "+"}</button></div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="results-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
              {filtered.map((s, i) => {
                const q = quotes[s];
                const chg = getReturnPct(q, returnPeriod);
                const isCompared = compareList.includes(s);
                const compareIdx = compareList.indexOf(s);
                const saved = isWatchlisted(s);
                const premiumInfo = getMarketPremiumInfo(s, q, premiumBySymbol);
                const rp = computeRiskProfile(catMap[s], etfMetrics[s]?.beta, etfMetrics[s]?.yield);
                return (
                  <div
                    key={s}
                    className="kbd-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelected(s)}
                    onKeyDown={onActivateKey(() => setSelected(s))}
                    style={{
                      background: C.card,
                      border: `1px solid ${isCompared ? COMPARE_COLORS[compareIdx] + "99" : C.border}`,
                      borderRadius: 10,
                      padding: "14px 18px",
                      minHeight: 176,
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      animation: `fadeUp 0.3s ease-out ${i * 0.02}s both`,
                      transition: "border-color 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif" }}>{displaySymbol(s)}</div>
                        <div style={{ fontSize: 10, color: C.t3, maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Fira Code', monospace" }}>
                          {mktCfg.names[s] || q?.name || "…"}
                        </div>
                        <span style={{ display: "inline-block", marginTop: 4, fontSize: 9.5, fontWeight: 700, color: RISK_META[rp].color, background: `${RISK_META[rp].color}18`, borderRadius: 5, padding: "2px 6px" }}>{RISK_META[rp].icon} {rp}</span>
                      </div>
                      <Spark data={getSparkCloses(q, returnPeriod)} w={56} h={22} color={dirColor(chg, isTwSymbol(s))} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Bitter', serif" }}>{currencySymbol}{q?.price?.toFixed(2) || "—"}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: dirColor(chg, isTwSymbol(s)), fontFamily: "'Fira Code', monospace" }}>
                        {formatReturnPct(chg)}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: C.t3, marginTop: 4, fontFamily: "'Fira Code', monospace", display: "flex", justifyContent: "space-between" }}>
                      <span>{activeThemes[s] || catMap[s]}</span>
                      <span>Vol {q?.volume ? (q.volume >= 1e6 ? `${(q.volume / 1e6).toFixed(1)}M` : q.volume.toLocaleString()) : "—"}</span>
                    </div>
                    <div style={{ fontSize: 10, color: C.t3, marginTop: 4, fontFamily: "'Fira Code', monospace", display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span>{premiumInfo.sourceLabel ? "NAV 溢價率" : "溢價率"}</span>
                      <span
                        title={premiumInfo.navPrice != null ? `NAV ${currencySymbol}${premiumInfo.navPrice.toFixed(2)}${premiumInfo.asOf ? ` · ${premiumInfo.asOf}` : ""}` : "尚無 NAV 資料"}
                        style={{ color: premiumInfo.premiumDiscountPct == null ? C.t3 : premiumInfo.premiumDiscountPct >= 0 ? C.warm : C.down, fontWeight: 800 }}
                      >
                        {formatPremiumShort(premiumInfo.premiumDiscountPct)}
                      </span>
                    </div>
                    {/* Compare toggle row */}
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 58px 58px", gap: 6, alignItems: "center", marginTop: "auto", paddingTop: 8, borderTop: `1px solid ${C.border}33`, minHeight: 36 }}>
                      <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        收盤 {q?.lastCloseTime ? `${formatTaipeiTime(q.lastCloseTime)} 台北` : "—"}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleWatchlist(s, market); }}
                        title={session ? (saved ? "移出自選" : "加入自選") : "登入後可加入自選"}
                        style={{
                          width: 58,
                          height: 28,
                          padding: 0,
                          background: saved ? C.warm : "transparent",
                          color: saved ? C.bg : C.t3,
                          border: `1.5px solid ${saved ? C.warm : C.border}`,
                          borderRadius: 5,
                          fontSize: 10,
                          fontWeight: 800,
                          fontFamily: "'Fira Code', monospace",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {saved ? "★ 自選" : "☆ 自選"}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleCompare(s); }}
                        title={isCompared ? `取消比較 ${compareIdx + 1}` : compareList.length >= 4 ? "最多比較 4 檔" : "加入比較"}
                        style={{
                          width: 58,
                          height: 28,
                          padding: 0,
                          background: isCompared ? COMPARE_COLORS[compareIdx] : "transparent",
                          color: isCompared ? "#fff" : C.t3,
                          border: `1.5px solid ${isCompared ? COMPARE_COLORS[compareIdx] : C.border}`,
                          borderRadius: 5, fontSize: 10, fontWeight: 700,
                          fontFamily: "'Fira Code', monospace", cursor: "pointer",
                          opacity: !isCompared && compareList.length >= 4 ? 0.3 : 1,
                          transition: "background 0.15s, color 0.15s, border-color 0.15s, opacity 0.15s",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isCompared ? "✓ 比較" : "+ 比較"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* ── WATCHLIST ── */}
        {view === "watchlist" && !session && renderLoginRequired(
          "自選 ETF 需要登入",
          "登入後可以把 ETF 加入自己的觀察名單，之後切換裝置也能保留。"
        )}

        {view === "watchlist" && session && (
          <div style={{ animation: "fadeUp 0.3s ease-out" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 22px", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif" }}>我的自選 ETF</div>
                  <div style={{ fontSize: 11, color: C.t3, marginTop: 4, fontFamily: "'Fira Code', monospace" }}>
                    美股 {watchlistByMarket.US.length} 檔 · 台股 {watchlistByMarket.TW.length} 檔 · 登入帳號保存
                  </div>
                </div>
                <button
                  onClick={() => setView("search")}
                  style={{ padding: "8px 14px", background: C.accent, color: C.bg, border: "none", borderRadius: 8, fontSize: 12, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                >
                  搜尋加入
                </button>
              </div>
            </div>

            {watchlist.length === 0 ? (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "36px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>☆</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 8 }}>目前沒有自選 ETF</div>
                <div style={{ fontSize: 12, color: C.t3, lineHeight: 1.7, marginBottom: 16 }}>到搜尋頁點 ETF 卡片上的「☆ 自選」，美股和台股會分開顯示在這一頁。</div>
                <button
                  onClick={() => setView("search")}
                  style={{ padding: "9px 18px", background: C.accent, color: C.bg, border: "none", borderRadius: 8, fontSize: 12, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                >
                  去搜尋 ETF
                </button>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                {["US", "TW"].map((sectionMarket) => {
                  const sectionCfg = MARKET_CONFIG[sectionMarket];
                  const sectionSyms = watchlistByMarket[sectionMarket] || [];
                  const sectionCatMap = {};
                  Object.entries(sectionCfg.cats).forEach(([c, ss]) => ss.forEach((s) => { sectionCatMap[s] = c; }));
                  return (
                    <section key={sectionMarket} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif" }}>{MARKET_BUTTON_LABELS[sectionMarket]}自選</div>
                          <div style={{ fontSize: 10, color: C.t3, marginTop: 3, fontFamily: "'Fira Code', monospace" }}>{sectionSyms.length} 檔 ETF</div>
                        </div>
                        <button
                          onClick={() => { setMarket(sectionMarket); setView("search"); }}
                          style={{ padding: "7px 12px", background: C.panel, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                        >
                          加入{MARKET_BUTTON_LABELS[sectionMarket]}
                        </button>
                      </div>
                      {sectionSyms.length === 0 ? (
                        <div style={{ padding: "20px 12px", background: C.panel, border: `1px dashed ${C.border}`, borderRadius: 10, color: C.t3, fontSize: 12, textAlign: "center" }}>
                          尚未加入{MARKET_BUTTON_LABELS[sectionMarket]} ETF
                        </div>
                      ) : (
                        <div className="results-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                          {sectionSyms.map((s, i) => {
                            const q = (market === sectionMarket ? quotes[s] : null) || watchlistQuotes[sectionMarket]?.[s];
                            const chg = q ? getReturnPct(q, returnPeriod) : 0;
                            const sectionName = sectionCfg.names[s] || q?.name || sectionCfg.themes[s] || sectionCatMap[s] || "ETF";
                            return (
                              <div
                                key={s}
                                className="kbd-card"
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  if (market !== sectionMarket) setMarket(sectionMarket);
                                  setSelected(s);
                                }}
                                onKeyDown={onActivateKey(() => {
                                  if (market !== sectionMarket) setMarket(sectionMarket);
                                  setSelected(s);
                                })}
                                style={{
                                  background: C.panel,
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 10,
                                  padding: "14px 16px",
                                  cursor: "pointer",
                                  minHeight: 142,
                                  display: "flex",
                                  flexDirection: "column",
                                  animation: `fadeUp 0.3s ease-out ${i * 0.02}s both`,
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 18, fontWeight: 900, color: C.accent, fontFamily: "'Bitter', serif" }}>{displaySymbol(s)}</div>
                                    <div style={{ fontSize: 10, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Fira Code', monospace" }}>{sectionName}</div>
                                  </div>
                                  {q ? <Spark data={getSparkCloses(q, returnPeriod)} w={56} h={22} color={dirColor(chg, sectionMarket === "TW")} /> : <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>載入中</span>}
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                  <span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Bitter', serif" }}>{q ? `${sectionCfg.currencySymbol}${q.price?.toFixed(2)}` : "—"}</span>
                                  <span style={{ fontSize: 13, fontWeight: 800, color: dirColor(chg, sectionMarket === "TW"), fontFamily: "'Fira Code', monospace" }}>{q ? formatReturnPct(chg) : "—"}</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 58px", gap: 8, alignItems: "center", marginTop: "auto", paddingTop: 8, borderTop: `1px solid ${C.border}33` }}>
                                  <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {q?.lastCloseTime ? `收盤 ${formatTaipeiTime(q.lastCloseTime)} 台北` : sectionCfg.themes[s] || sectionCatMap[s] || MARKET_BUTTON_LABELS[sectionMarket]}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleWatchlist(s, sectionMarket); }}
                                    style={{ width: 58, height: 28, padding: 0, background: C.warm, color: C.bg, border: `1px solid ${C.warm}`, borderRadius: 6, fontSize: 10, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                                  >
                                    移除
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── RECOMMENDATIONS ── */}
        {view === "recommend" && !session && renderLoginRequired(
          "推薦功能需要登入",
          "登入帳號即可使用 AI 驅動的個性化 ETF 推薦，根據你的投資檔案量身打造投資組合。"
        )}

        {view === "recommend" && session && (
          <div style={{ animation: "fadeUp 0.3s ease-out", maxWidth: 900, margin: "0 auto" }}>
            <div style={{ background: `linear-gradient(135deg, ${C.card} 0%, ${C.panel} 100%)`, border: `1px solid ${C.border}`, borderRadius: 14, padding: "32px 28px", marginBottom: 20, textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
                <img src="/logo-etfowl.png" alt="ETFowl" style={{ width: 34, height: 34, objectFit: "contain" }} />
                <span style={{ fontSize: 28, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif" }}>ETF 推薦引擎</span>
              </div>
              <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.8 }}>AI 驅動的個性化投資建議</div>
            </div>

            {/* Recommendation Form */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 16 }}>1️⃣ 填寫您的投資檔案</div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>風險承受度</span>
                  <select ref={(el) => (recFormRef.current.riskTolerance = el)} style={{ padding: "8px 12px", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, color: C.t1, fontSize: 12, fontFamily: "'Fira Code', monospace" }}>
                    <option value="">選擇</option>
                    <option value="low">低風險 (低風險、穩定現金流)</option>
                    <option value="medium">中風險 (平衡成長與穩定)</option>
                    <option value="high">高風險 (追求長期成長)</option>
                  </select>
                </label>

                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>投資目標</span>
                  <input type="text" ref={(el) => (recFormRef.current.investmentGoal = el)} placeholder="例：長期資產累積" style={{ padding: "8px 12px", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, color: C.t1, fontSize: 12 }} />
                </label>

                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>投資期限</span>
                  <input type="text" ref={(el) => (recFormRef.current.timeHorizon = el)} placeholder="例：5-10 年" style={{ padding: "8px 12px", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, color: C.t1, fontSize: 12 }} />
                </label>

                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>初期投資額</span>
                  <input type="text" ref={(el) => (recFormRef.current.budget = el)} placeholder="例：$10,000" style={{ padding: "8px 12px", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, color: C.t1, fontSize: 12 }} />
                </label>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" ref={(el) => (recFormRef.current.marketUS = el)} defaultChecked style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span style={{ fontSize: 12, color: C.t1 }}>美股</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" ref={(el) => (recFormRef.current.marketTW = el)} defaultChecked style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span style={{ fontSize: 12, color: C.t1 }}>台股</span>
                </label>
              </div>

              <button
                onClick={async () => {
                  const f = recFormRef.current;
                  const riskTolerance = f.riskTolerance?.value || "";
                  const investmentGoal = f.investmentGoal?.value || "";
                  const timeHorizon = f.timeHorizon?.value || "";
                  const budget = f.budget?.value || "";
                  const marketUS = f.marketUS?.checked ?? true;
                  const marketTW = f.marketTW?.checked ?? true;

                  if (!riskTolerance) {
                    setRecommendationError("請選擇風險承受度");
                    return;
                  }

                  setRecommendationLoading(true);
                  setRecommendationError("");

                  try {
                    const response = await fetch(API + "/recommend", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        riskTolerance,
                        investmentGoal: investmentGoal || "長期資產累積",
                        timeHorizon: timeHorizon || "5-10 years",
                        budget: budget || "$10,000",
                        preferredMarkets: [marketUS ? "US" : null, marketTW ? "TW" : null].filter(Boolean),
                        investmentStyle: "balanced",
                        constraints: []
                      })
                    });

                    if (!response.ok) {
                      const err = await response.json();
                      throw new Error(err.error || "推薦失敗");
                    }

                    let data = await response.json();
                    console.log("[recommend] API response:", data);

                    // 展開嵌套的 recommendations（server.js 的返回格式）
                    if (data.recommendations && typeof data.recommendations === 'object' && data.recommendations.recommendations) {
                      data = data.recommendations;
                    }

                    // 檢查響應格式
                    if (!Array.isArray(data.recommendations)) {
                      console.error("[recommend] Invalid response format:", data);
                      setRecommendationError("推薦格式錯誤，請稍後重試");
                      return;
                    }

                    setRecommendations(data);
                  } catch (err) {
                    setRecommendationError(err.message);
                  } finally {
                    setRecommendationLoading(false);
                  }
                }}
                style={{
                  marginTop: 16,
                  padding: "10px 24px",
                  background: C.accent,
                  color: C.bg,
                  border: "none",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "'Fira Code', monospace",
                  cursor: "pointer"
                }}
              >
                {recommendationLoading ? "正在生成推薦…" : "🚀 獲得推薦"}
              </button>

              {recommendationError && (
                <div style={{ marginTop: 12, padding: "10px 12px", background: C.panel, border: `1px solid ${C.warm}`, borderRadius: 8, color: C.warm, fontSize: 12 }}>
                  ⚠️ {recommendationError}
                </div>
              )}
            </div>

            {/* Recommendations Results */}
            {recommendations && !recommendationLoading && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px" }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 16 }}>2️⃣ 推薦結果</div>

                {recommendations.recommendations && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12, marginBottom: 16 }}>
                    {recommendations.recommendations.map((rec, idx) => (
                      <div
                        key={rec.symbol}
                        onClick={() => {
                          const targetMarket = /\.TWO?$/i.test(rec.symbol) ? "TW" : "US";
                          if (targetMarket !== market) setMarket(targetMarket);
                          setSelected(rec.symbol);
                        }}
                        style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, cursor: "pointer", transition: "border-color 0.2s, transform 0.1s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = "none"; }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{rec.symbol}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>{rec.allocation}</div>
                        </div>
                        <div style={{ fontSize: 11, color: C.t3, marginBottom: 8 }}>{rec.name}</div>
                        <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6, marginBottom: 10 }}>{rec.reason?.substring(0, 100)}...</div>
                        {rec.pros && <div style={{ fontSize: 10, color: C.green, marginBottom: 4 }}>✓ {rec.pros.join(", ")}</div>}
                        {rec.cons && <div style={{ fontSize: 10, color: C.warm, marginBottom: 8 }}>✗ {rec.cons.join(", ")}</div>}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8 }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const recMarket = /^\d/.test(rec.symbol) || /\.TWO?$/i.test(rec.symbol) ? "TW" : "US";
                              toggleWatchlist(rec.symbol, recMarket);
                            }}
                            style={{ padding: "4px 9px", background: isWatchlisted(rec.symbol) ? C.warm : "transparent", color: isWatchlisted(rec.symbol) ? C.bg : C.t3, border: `1px solid ${isWatchlisted(rec.symbol) ? C.warm : C.border}`, borderRadius: 6, fontSize: 10, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                          >
                            {isWatchlisted(rec.symbol) ? "★ 自選" : "☆ 自選"}
                          </button>
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textAlign: "right" }}>查看詳情 →</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {recommendations.portfolioStrategy && (
                  <div style={{ background: `linear-gradient(135deg, ${C.accentDim}33, transparent)`, border: `1px solid ${C.accent}33`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 6 }}>投資策略</div>
                    <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>{recommendations.portfolioStrategy}</div>
                  </div>
                )}

                {recommendations.riskAssessment && (
                  <div style={{ background: `linear-gradient(135deg, ${C.warm}22, transparent)`, border: `1px solid ${C.warm}33`, borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.warm, marginBottom: 6 }}>風險評估</div>
                    <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>{recommendations.riskAssessment}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── LEARN ── */}
        {view === "learn" && (
          <div style={{ animation: "fadeUp 0.3s ease-out", maxWidth: 820, margin: "0 auto" }}>
            {/* Hero */}
            <div style={{ background: `linear-gradient(135deg, ${C.card} 0%, ${C.panel} 100%)`, border: `1px solid ${C.border}`, borderRadius: 14, padding: "32px 28px", marginBottom: 20, textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 8 }}>ETF 新手教室</div>
              <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.8 }}>從零開始認識 ETF，掌握投資的第一步</div>
            </div>

            {/* Section 1: What is ETF */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>01</span>
                什麼是 ETF？
              </div>
              <div style={{ fontSize: 13, color: C.t2, lineHeight: 2 }}>
                <p style={{ margin: "0 0 12px" }}>
                  <strong style={{ color: C.t1 }}>ETF（Exchange Traded Fund，指數股票型基金）</strong>是一種在證券交易所掛牌交易的基金。它追蹤特定的指數（如台灣加權指數、S&P 500）、產業主題或資產類別，讓投資人可以用一筆錢同時買進一籃子的股票或債券。
                </p>
                <p style={{ margin: "0 0 12px" }}>
                  簡單來說，買一張 ETF 就等於同時持有該 ETF 追蹤指數裡的所有成分股。例如買進 <strong style={{ color: C.accent }}>0050（元大台灣50）</strong>，就等於同時投資台灣市值最大的 50 家公司。
                </p>
                <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px", marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.warm, marginBottom: 8, fontFamily: "'Fira Code', monospace" }}>ETF 的核心特色</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                    {[
                      ["分散風險", "一次買進多檔標的，不怕單一公司暴跌"],
                      ["低管理費", "被動追蹤指數，管理費用遠低於主動型基金"],
                      ["即時交易", "像股票一樣在盤中買賣，流動性高"],
                      ["透明度高", "持股明細、淨值每日公開揭露"],
                    ].map(([t, d]) => (
                      <div key={t} style={{ padding: "10px 12px", background: C.card, borderRadius: 6, border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginBottom: 4 }}>{t}</div>
                        <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.6 }}>{d}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: ETF vs Stock vs Fund */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>02</span>
                ETF vs 個股 vs 基金
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {["比較項目", "ETF", "個股", "共同基金"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 8px", color: C.t3, fontWeight: 600, fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["交易方式", "盤中即時買賣", "盤中即時買賣", "每日收盤後結算淨值"],
                      ["分散程度", "一籃子標的", "單一公司", "一籃子標的"],
                      ["管理費", "0.03% ~ 0.5%", "無", "1% ~ 2%"],
                      ["最低投資額", "1 股即可 (零股)", "1 股即可 (零股)", "通常 3,000~10,000 元"],
                      ["選股方式", "被動追蹤指數", "自行研究選擇", "基金經理人主動選股"],
                      ["透明度", "每日公布持股", "公司公開資訊", "每月/季公布"],
                      ["交易成本", "手續費 + 交易稅 0.1%", "手續費 + 交易稅 0.3%", "申購手續費 1~3%"],
                      ["適合對象", "想省心的長期投資人", "有研究能力的積極投資人", "完全不想管的委託型投資人"],
                    ].map(([item, etf, stock, fund], i) => (
                      <tr key={item} style={{ borderBottom: `1px solid ${C.border}44` }}>
                        <td style={{ padding: "10px 8px", color: C.t1, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>{item}</td>
                        <td style={{ padding: "10px 8px", color: C.accent, fontSize: 11 }}>{etf}</td>
                        <td style={{ padding: "10px 8px", color: C.t2, fontSize: 11 }}>{stock}</td>
                        <td style={{ padding: "10px 8px", color: C.t2, fontSize: 11 }}>{fund}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 14, padding: "12px 14px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.t2, lineHeight: 1.8 }}>
                <strong style={{ color: C.warm }}>結論：</strong>ETF 兼具個股的交易靈活性和基金的分散風險優勢，管理費又最低，是大多數投資新手的最佳起點。
              </div>
            </div>

            {/* Section 3: ETF Types */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>03</span>
                ETF 有哪些種類？
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, fontSize: 12 }}>
                {[
                  ["市值型", "追蹤大盤指數，如 0050、VOO、SPY", C.accent],
                  ["高股息型", "篩選高配息股，如 0056、00878、00919", C.warm],
                  ["債券型", "投資政府或公司債，如 00937B、BND、TLT", "#38bdf8"],
                  ["產業型", "聚焦特定產業，如半導體、AI、生技", "#a78bfa"],
                  ["主動型 ETF", "基金經理人主動選股，追求超越指數的報酬，如 00946、ARKK", "#e879f9"],
                  ["槓桿/反向型", "放大或反向追蹤指數，高風險進階工具", C.down],
                  ["海外市場型", "投資美股、日股、陸股等海外市場", "#f472b6"],
                ].map(([t, d, color]) => (
                  <div key={t} style={{ padding: "14px 16px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}` }}>
                    <div style={{ fontWeight: 800, color, marginBottom: 6 }}>{t}</div>
                    <div style={{ color: C.t3, lineHeight: 1.6, fontSize: 11 }}>{d}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Active vs Passive ETF */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>04</span>
                主動式 ETF vs 被動式 ETF
              </div>
              <div style={{ fontSize: 13, color: C.t2, lineHeight: 2, marginBottom: 14 }}>
                <p style={{ margin: "0 0 12px" }}>
                  傳統 ETF 是<strong style={{ color: C.t1 }}>被動式</strong>的：它忠實追蹤一個指數，不做額外判斷。近年興起的<strong style={{ color: "#e879f9" }}>主動式 ETF（Active ETF）</strong>則由基金經理人主動選股和調整持股，試圖創造超越指數的報酬。
                </p>
                <p style={{ margin: "0 0 12px" }}>
                  主動式 ETF 結合了傳統主動型基金的「專業選股」和 ETF 的「盤中交易、費用透明」優勢，在台灣與美國市場都快速成長。
                </p>
              </div>
              <div style={{ overflowX: "auto", marginBottom: 14 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {["比較項目", "被動式 ETF", "主動式 ETF"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 8px", color: C.t3, fontWeight: 600, fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["投資策略", "嚴格追蹤指數，按指數權重配置", "經理人主動選股、擇時、調整權重"],
                      ["管理費", "極低（0.03% ~ 0.3%）", "較高（0.3% ~ 1%）"],
                      ["目標", "貼近指數報酬", "打敗指數，創造超額報酬 (Alpha)"],
                      ["持股透明度", "每日完整公布", "每日或每季公布（依法規）"],
                      ["調整頻率", "隨指數定期調整（季/半年）", "經理人隨時可調整"],
                      ["交易方式", "盤中即時買賣", "盤中即時買賣（與被動式相同）"],
                      ["績效穩定性", "報酬貼近指數，波動可預期", "取決於經理人能力，波動較大"],
                    ].map(([item, passive, active]) => (
                      <tr key={item} style={{ borderBottom: `1px solid ${C.border}44` }}>
                        <td style={{ padding: "10px 8px", color: C.t1, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>{item}</td>
                        <td style={{ padding: "10px 8px", color: C.accent, fontSize: 11 }}>{passive}</td>
                        <td style={{ padding: "10px 8px", color: "#e879f9", fontSize: 11 }}>{active}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginBottom: 14 }}>
                <div style={{ padding: "14px 16px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#e879f9", marginBottom: 8 }}>台灣主動式 ETF 代表</div>
                  <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.8 }}>
                    <div><strong style={{ color: C.t1 }}>00946</strong> 群益台灣精選高息 — 主動篩選高息股</div>
                    <div><strong style={{ color: C.t1 }}>00944</strong> 野村臺灣趨勢動能高息 — 動能 + 高息</div>
                    <div><strong style={{ color: C.t1 }}>00942</strong> 台新臺灣永續高息 — ESG + 高息策略</div>
                    <div><strong style={{ color: C.t1 }}>00940</strong> 元大臺灣價值高息 — 價值選股 + 高息</div>
                  </div>
                </div>
                <div style={{ padding: "14px 16px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#e879f9", marginBottom: 8 }}>美國主動式 ETF 代表</div>
                  <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.8 }}>
                    <div><strong style={{ color: C.t1 }}>ARKK</strong> ARK Innovation — 顛覆性創新科技</div>
                    <div><strong style={{ color: C.t1 }}>AVUV</strong> Avantis US Small Cap Value — 小型價值因子</div>
                    <div><strong style={{ color: C.t1 }}>JEPI</strong> JPMorgan Equity Premium Income — 高收益掩護性買權</div>
                    <div><strong style={{ color: C.t1 }}>JEPQ</strong> JPMorgan Nasdaq Equity Premium — 科技 + 期權收益</div>
                  </div>
                </div>
              </div>
              <div style={{ padding: "12px 14px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.t2, lineHeight: 1.8 }}>
                <strong style={{ color: C.warm }}>投資提醒：</strong>研究顯示，長期而言約 80~90% 的主動型基金無法持續打敗指數。主動式 ETF 的費用更高，績效不保證優於被動式。新手建議以被動式 ETF 為核心配置，主動式 ETF 可作為衛星持倉（佔比 10~20%），用於押注特定看法。
              </div>
            </div>

            {/* Section 5: Where to Buy */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>05</span>
                去哪裡買 ETF？
              </div>
              <div style={{ fontSize: 13, color: C.t2, lineHeight: 2, marginBottom: 14 }}>
                <p style={{ margin: "0 0 14px" }}>
                  <strong style={{ color: C.t1 }}>台灣上市 ETF</strong>（如 0050、00878）透過任何一家台灣券商即可買賣，和買台股一樣操作。
                </p>
                <p style={{ margin: "0 0 14px" }}>
                  <strong style={{ color: C.t1 }}>美國 ETF</strong>（如 VOO、QQQ）可透過以下管道購買：
                </p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
                {[
                  ["國內券商複委託", "透過台灣券商下單買美股，手續費較高（約 0.15~1%），但報稅方便、有中文介面", ["元大證券", "凱基證券", "富邦證券", "國泰證券", "永豐金證券"]],
                  ["海外券商", "直接在美國券商開戶，手續費低甚至免費，但需自行處理匯款與稅務", ["Firstrade (第一證券)", "Interactive Brokers", "Charles Schwab", "TD Ameritrade"]],
                  ["台灣上市的海外 ETF", "投信發行追蹤海外指數的台灣 ETF，用台幣即可買，如 00646 (S&P 500)、00662 (NASDAQ)", ["免匯款", "免海外開戶", "用台幣交易"]],
                ].map(([t, d, items]) => (
                  <div key={t} style={{ padding: "16px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.t1, marginBottom: 8 }}>{t}</div>
                    <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.7, marginBottom: 10 }}>{d}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {items.map((item) => (
                        <span key={item} style={{ padding: "3px 8px", background: C.card, borderRadius: 4, fontSize: 10, color: C.t2, border: `1px solid ${C.border}` }}>{item}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 6: Costs */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>06</span>
                ETF 投資成本
              </div>
              <div style={{ fontSize: 13, color: C.t2, lineHeight: 2, marginBottom: 14 }}>
                投資 ETF 主要有三種成本，了解它們才能精確計算實際報酬：
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                {[
                  ["交易手續費", "買賣 ETF 時券商收取，台股一般為成交金額的 0.1425%（可談折扣，網路下單通常 5~6 折）", "買入 + 賣出時"],
                  ["證券交易稅", "賣出時政府收取的稅金。台股 ETF 為 0.1%（一般股票為 0.3%，ETF 享優惠稅率），美股 ETF 無交易稅", "賣出時"],
                  ["內扣費用（經理費 + 保管費）", "ETF 發行商每年從基金淨值中自動扣除的管理費用，直接反映在淨值上，不另外收取。台股 ETF 約 0.2~0.5%，美股 ETF 最低可到 0.03%", "每日自動扣除"],
                ].map(([t, d, when]) => (
                  <div key={t} style={{ padding: "14px 16px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}`, display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 4 }}>{t}</div>
                      <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.7 }}>{d}</div>
                    </div>
                    <span style={{ padding: "4px 8px", background: C.accentDim, borderRadius: 5, fontSize: 10, fontWeight: 700, color: C.accent, fontFamily: "'Fira Code', monospace", whiteSpace: "nowrap", flexShrink: 0 }}>{when}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, padding: "12px 14px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.warm, marginBottom: 8, fontFamily: "'Fira Code', monospace" }}>費用試算範例</div>
                <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.8 }}>
                  假設以 10 萬元買進台股 ETF，手續費 6 折：<br/>
                  <span style={{ color: C.t3 }}>• 買入手續費：</span><span style={{ color: C.t1 }}>100,000 × 0.1425% × 0.6 = 85 元</span><br/>
                  <span style={{ color: C.t3 }}>• 賣出手續費：</span><span style={{ color: C.t1 }}>100,000 × 0.1425% × 0.6 = 85 元</span><br/>
                  <span style={{ color: C.t3 }}>• 交易稅：</span><span style={{ color: C.t1 }}>100,000 × 0.1% = 100 元</span><br/>
                  <span style={{ color: C.t3 }}>• 一次買賣總成本：</span><span style={{ color: C.accent, fontWeight: 700 }}>約 270 元（0.27%）</span>
                </div>
              </div>
            </div>

            {/* Section 7: Beginner Tips */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>07</span>
                新手入門建議
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  ["1", "從市值型 ETF 開始", "0050、006208（台灣）或 VOO、VTI（美國）是最穩健的起點，長期年化報酬約 7~10%。"],
                  ["2", "定期定額，不要擇時", "每月固定金額投入，平均買進成本，避免追高殺低的情緒決策。大多數券商支援自動定期定額。"],
                  ["3", "注意內扣費用", "同類型 ETF 比較時，內扣費用是關鍵差異。長期下來 0.1% 的差距會滾成很大的金額。"],
                  ["4", "別被高配息迷惑", "配息率高不代表報酬率高，配息來自淨值，配完息淨值會等額下降。看「含息總報酬」才是真報酬。"],
                  ["5", "長期持有，複利效應", "ETF 最大的優勢在長期複利。10 年以上的投資期間，市值型 ETF 勝過大多數主動型基金。"],
                  ["6", "分散市場配置", "不要把所有資金放在單一市場。可以台股 + 美股搭配，或透過全球型 ETF（如 VT）一次配置。"],
                ].map(([n, t, d]) => (
                  <div key={n} style={{ display: "flex", gap: 14, padding: "14px 16px", background: C.panel, borderRadius: 8, border: `1px solid ${C.border}`, alignItems: "flex-start" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.accentDim, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, fontWeight: 800, color: C.accent, fontFamily: "'Fira Code', monospace" }}>{n}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 4 }}>{t}</div>
                      <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.7 }}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 8: Glossary */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 22px", marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Bitter', serif", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.accentDim, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>08</span>
                常見術語速查
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 8 }}>
                {[
                  ["NAV（淨值）", "基金每單位的實際價值，等於總資產除以發行單位數"],
                  ["折溢價", "市價低於淨值叫折價，高於叫溢價。長期大幅溢價要小心"],
                  ["追蹤誤差", "ETF 實際報酬與追蹤指數的偏差，越小越好"],
                  ["內扣費用", "每年從淨值自動扣除的管理費 + 保管費等總費用"],
                  ["配息率", "每年配發的股息佔股價的百分比"],
                  ["含息報酬", "把配息再投入計算的總報酬，才是真正的投資績效"],
                  ["填息", "配息後股價回到除息前水準，代表真正賺到股息"],
                  ["除息日", "ETF 發放股息的基準日，當天淨值會扣除配息金額"],
                  ["成分股", "ETF 持有的個別股票，依指數規則定期調整"],
                  ["再平衡", "定期調整持股比例以符合指數權重的機制"],
                ].map(([t, d]) => (
                  <div key={t} style={{ padding: "10px 12px", background: C.panel, borderRadius: 6, border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.warm, marginBottom: 3, fontFamily: "'Fira Code', monospace" }}>{t}</div>
                    <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.6 }}>{d}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div style={{ textAlign: "center", padding: "24px 0 16px" }}>
              <button
                onClick={() => setView("dashboard")}
                style={{ padding: "12px 32px", background: C.accent, color: C.bg, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 800, fontFamily: "'Fira Code', monospace", cursor: "pointer", letterSpacing: 0.5 }}
              >
                開始探索 ETF
              </button>
            </div>
          </div>
        )}

        {view === "active" && (
          <div style={{ animation: "fadeUp 0.3s ease-out", maxWidth: 960, margin: "0 auto" }}>

            {/* ── Card grid (hidden when detail is open) ── */}
            {!activeEtfDetail && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <button onClick={() => setView("dashboard")} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Fira Code', monospace", padding: 0, marginBottom: 10 }}>← 返回總覽</button>
                  <div style={{ background: `linear-gradient(135deg, ${C.card} 0%, ${C.panel} 100%)`, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 4 }}>主動 ETF 持股異動</div>
                    <div style={{ fontSize: 12, color: C.t3 }}>每日揭露 27 檔主動式 ETF 的持股變化 — 加碼、減碼、進場、出場</div>
                    {activeEtfData && <div style={{ fontSize: 11, color: C.t3, marginTop: 6, fontFamily: "'Fira Code', monospace" }}>資料日期：{activeEtfData.date}</div>}
                  </div>
                </div>

                {/* ── 分頁切換:各家異動 / 共識股 / 產業流向 / 精選訊號 ── */}
                <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
                  {[["changes", "各家異動"], ["consensus", "共識股"], ["sectors", "產業流向"], ["picks", "精選訊號"]].map(([id, label]) => (
                    <button key={id} onClick={() => setActiveLandingTab(id)} style={{
                      padding: "7px 14px", background: activeLandingTab === id ? C.accent : "transparent",
                      color: activeLandingTab === id ? "#fff" : C.t3, border: `1px solid ${activeLandingTab === id ? C.accent : C.border}`,
                      borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Fira Code', monospace",
                    }}>{label}</button>
                  ))}
                </div>

                {activeLandingTab === "changes" && (<>
                {activeEtfLoading && <div style={{ textAlign: "center", color: C.t3, padding: 40, fontSize: 13 }}>載入中…</div>}

                {activeEtfData && !activeEtfLoading && (() => {
                  const sortFns = {
                    total: (a, b) => (b.entered + b.exited + b.increased + b.decreased) - (a.entered + a.exited + a.increased + a.decreased),
                    entered: (a, b) => b.entered - a.entered,
                    exited: (a, b) => b.exited - a.exited,
                    increased: (a, b) => b.increased - a.increased,
                    decreased: (a, b) => b.decreased - a.decreased,
                    count: (a, b) => b.count - a.count,
                    name: (a, b) => (TW_ETF_NAMES[a.symbol] || "").localeCompare(TW_ETF_NAMES[b.symbol] || ""),
                  };
                  const q = activeSearch.trim().toLowerCase();
                  const filteredEtfs = (activeEtfData.etfs || [])
                    .filter((etf) => !q || displaySymbol(etf.symbol).toLowerCase().includes(q) || (TW_ETF_NAMES[etf.symbol] || "").toLowerCase().includes(q) || (TW_ETF_THEMES[etf.symbol] || "").toLowerCase().includes(q))
                    .sort(sortFns[activeSort] || sortFns.total);

                  return (
                    <>
                    {/* Controls: search + sort + view toggle */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                      <input
                        value={activeSearch} onChange={(e) => setActiveSearch(e.target.value)}
                        placeholder="搜尋代號、名稱、主題…"
                        aria-label="搜尋主動 ETF"
                        style={{ flex: "1 1 160px", minWidth: 120, padding: "6px 10px", background: C.panel, color: C.t1, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontFamily: "'Fira Code', monospace" }}
                      />
                      <select value={activeSort} onChange={(e) => setActiveSort(e.target.value)} style={{ padding: "6px 8px", background: C.panel, color: C.t1, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}>
                        <option value="total">異動數排序</option>
                        <option value="entered">新進↓</option>
                        <option value="exited">出場↓</option>
                        <option value="increased">加碼↓</option>
                        <option value="decreased">減碼↓</option>
                        <option value="count">持股數↓</option>
                        <option value="name">名稱 A→Z</option>
                      </select>
                      <div style={{ display: "flex", gap: 4 }}>
                        {["card", "table"].map((m) => (
                          <button key={m} onClick={() => setActiveViewMode(m)} style={{
                            padding: "5px 8px", background: activeViewMode === m ? C.accent : "transparent",
                            color: activeViewMode === m ? "#fff" : C.t3, border: `1px solid ${activeViewMode === m ? C.accent : C.border}`,
                            borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'Fira Code', monospace",
                          }}>{m === "card" ? "▦ 卡片" : "☰ 表格"}</button>
                        ))}
                      </div>
                      <span style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace" }}>{filteredEtfs.length} 檔</span>
                    </div>

                    {activeViewMode === "table" ? (
                      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "68px 1fr minmax(70px,auto) 48px 48px 48px 48px 54px", gap: "0 6px", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontWeight: 700, fontFamily: "'Fira Code', monospace" }}>
                          {[["name","代號"],["name","名稱"],[null,"主題"],["count","持股","right"],["entered","新進","right",C.up],["exited","出場","right",C.down],["increased","加碼","right",C.up],["decreased","減碼","right",C.down]].map(([field, label, align, clr], i) => (
                            <div key={i} onClick={field ? () => setActiveSort(activeSort === field ? (field === "name" ? "total" : field) : field) : undefined} style={{ textAlign: align || "left", cursor: field ? "pointer" : "default", userSelect: "none", color: activeSort === field ? C.accent : (clr || C.t3) }}>{label}{activeSort === field ? " ▼" : ""}</div>
                          ))}
                        </div>
                        {filteredEtfs.map((etf) => {
                          const name = TW_ETF_NAMES[etf.symbol] || etf.symbol;
                          const theme = TW_ETF_THEMES[etf.symbol] || "";
                          return (
                            <div key={etf.symbol} onClick={() => { setActiveEtfDetail(etf.symbol); setActiveEtfDetailTab("holdings"); }}
                              style={{ display: "grid", gridTemplateColumns: "68px 1fr minmax(70px,auto) 48px 48px 48px 48px 54px", gap: "0 6px", padding: "7px 14px", borderBottom: `1px solid ${C.border}44`, cursor: "pointer", alignItems: "center", transition: "background 0.15s" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = C.panel} onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                              <div style={{ fontWeight: 800, color: C.t1, fontFamily: "'Fira Code', monospace", fontSize: 12 }}>{displaySymbol(etf.symbol)}</div>
                              <div style={{ fontSize: 11, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                              <div><span style={{ fontSize: 9, color: C.accent, background: C.accentDim, borderRadius: 3, padding: "1px 5px", fontWeight: 700 }}>{theme}</span></div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 12, color: C.t2 }}>{etf.count}</div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 12, fontWeight: 800, color: etf.entered > 0 ? C.up : C.t3 }}>{etf.entered}</div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 12, fontWeight: 800, color: etf.exited > 0 ? C.down : C.t3 }}>{etf.exited}</div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 12, fontWeight: 800, color: etf.increased > 0 ? C.up : C.t3 }}>{etf.increased}</div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 12, fontWeight: 800, color: etf.decreased > 0 ? C.down : C.t3 }}>{etf.decreased}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12, marginBottom: 16 }}>
                      {filteredEtfs.map((etf) => {
                        const name = TW_ETF_NAMES[etf.symbol] || etf.symbol;
                        const theme = TW_ETF_THEMES[etf.symbol] || "";
                        const hasChange = etf.entered + etf.exited + etf.increased + etf.decreased > 0;
                        return (
                          <div key={etf.symbol} onClick={() => { setActiveEtfDetail(etf.symbol); setActiveEtfDetailTab("holdings"); }} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", cursor: "pointer", transition: "border-color 0.2s" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                            <div>
                              <span style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{displaySymbol(etf.symbol)}</span>
                              <span style={{ fontSize: 11, color: C.t3, marginLeft: 8 }}>{name}</span>
                            </div>
                            <span style={{ fontSize: 10, color: C.accent, background: C.accentDim, borderRadius: 4, padding: "2px 6px", fontWeight: 700 }}>{theme}</span>
                          </div>
                          <div style={{ fontSize: 11, color: C.t3, marginBottom: 8 }}>持股 {etf.count} 檔 · {etf.date || "—"}</div>
                          {(() => {
                            const st = activeStrategy && activeStrategy[etf.symbol];
                            if (!st || st.data_suspect) return null;
                            const rc = st.return_pct == null ? C.t3 : st.return_pct >= 0 ? C.up : C.down; // 漲紅跌綠
                            return (
                              <div style={{ display: "flex", gap: 8, marginBottom: 8, fontFamily: "'Fira Code', monospace" }}>
                                <div style={{ flex: 1, textAlign: "center", background: C.panel, borderRadius: 6, padding: "3px 0" }}>
                                  <span style={{ fontSize: 13, fontWeight: 900, color: C.t1 }}>{st.win_rate != null ? `${st.win_rate.toFixed(0)}%` : "—"}</span>
                                  <span style={{ fontSize: 9, color: C.t3, marginLeft: 4 }}>勝率</span>
                                </div>
                                <div style={{ flex: 1, textAlign: "center", background: C.panel, borderRadius: 6, padding: "3px 0" }}>
                                  <span style={{ fontSize: 13, fontWeight: 900, color: rc }}>{st.return_pct != null ? `${st.return_pct >= 0 ? "+" : ""}${st.return_pct.toFixed(0)}%` : "—"}</span>
                                  <span style={{ fontSize: 9, color: C.t3, marginLeft: 4 }}>報酬</span>
                                </div>
                              </div>
                            );
                          })()}
                          {hasChange ? (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, fontSize: 11 }}>
                              {[
                                { label: "新進", n: etf.entered, c: C.up },
                                { label: "出場", n: etf.exited, c: C.down },
                                { label: "加碼", n: etf.increased, c: C.up },
                                { label: "減碼", n: etf.decreased, c: C.down },
                              ].map((s) => (
                                <div key={s.label} style={{ textAlign: "center", background: s.n > 0 ? `${s.c}14` : C.panel, borderRadius: 6, padding: "4px 0" }}>
                                  <div style={{ fontSize: 16, fontWeight: 900, color: s.n > 0 ? s.c : C.t3 }}>{s.n}</div>
                                  <div style={{ color: C.t3, fontSize: 10 }}>{s.label}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: C.t3, textAlign: "center", padding: "6px 0", background: C.panel, borderRadius: 6 }}>今日無異動</div>
                          )}
                        </div>
                      );
                      })}
                    </div>
                    )}
                    </>
                  );
                })()}
                </>)}

                {/* ── 分頁:共識股與分歧股 ── */}
                {activeLandingTab === "consensus" && (() => {
                  const d = consensusData;
                  if (!d) return <div style={{ textAlign: "center", color: C.t3, padding: 40, fontSize: 13 }}>載入中…</div>;
                  if (d.empty || !d.consensus) return <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>尚無資料。</div>;
                  const holders = (s) => {
                    if (consensusExpanded !== s.stock_code) return null;
                    // 相容舊格式(字串陣列)與新格式({symbol, action});依動作分組
                    const list = (s.etfs || []).map((e) => typeof e === "string" ? { symbol: e, action: "flat" } : e);
                    const chip = (e, color) => (
                      <span key={e.symbol} style={{ fontSize: 10, fontFamily: "'Fira Code', monospace", color: C.t2, background: C.panel, border: `1px solid ${color}`, borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap" }}>
                        <EtfLink symbol={e.symbol} C={C} style={{ fontWeight: 700, color: color === C.border ? C.accent : color, borderBottom: "none" }}>{displaySymbol(e.symbol)}</EtfLink>{TW_ETF_NAMES[e.symbol] ? <span style={{ color: C.t3, marginLeft: 4 }}>{TW_ETF_NAMES[e.symbol]}</span> : null}
                      </span>
                    );
                    const group = (label, arr, color) => arr.length ? (
                      <div style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color, marginBottom: 3, fontFamily: "'Fira Code', monospace" }}>{label}（{arr.length}）</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{arr.map((e) => chip(e, color))}</div>
                      </div>
                    ) : null;
                    const adds = list.filter((e) => e.action === "add");
                    const news = list.filter((e) => e.action === "new");
                    const reduces = list.filter((e) => e.action === "reduce");
                    const flats = list.filter((e) => !["add", "new", "reduce"].includes(e.action));
                    return (
                      <div style={{ padding: "2px 2px 10px" }}>
                        {group("加碼", adds, C.up)}
                        {group("新進場", news, C.up)}
                        {group("減碼", reduces, C.down)}
                        {group("持平/持有", flats, C.border)}
                      </div>
                    );
                  };
                  const toggle = (s) => setConsensusExpanded(consensusExpanded === s.stock_code ? null : s.stock_code);
                  const row = (s) => (
                    <div key={s.stock_code}>
                      <div onClick={() => toggle(s)} style={{ display: "grid", gridTemplateColumns: "16px minmax(58px,auto) 1fr auto auto", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: `1px solid ${C.border}44`, fontSize: 12, cursor: "pointer" }}>
                        <span style={{ color: C.t3, fontSize: 9, transition: "transform 0.15s", transform: consensusExpanded === s.stock_code ? "rotate(90deg)" : "none" }}>▶</span>
                        <div style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.accent }}>{s.stock_code}</div>
                        <div style={{ overflow: "hidden", minWidth: 0 }}>
                          <div style={{ color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(s.stock_name || "").slice(0, 12)}</div>
                          {s.sub_sector && <div style={{ fontSize: 9, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{s.sub_sector}</div>}
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.t1, background: C.panel, borderRadius: 4, padding: "2px 7px", fontFamily: "'Fira Code', monospace", whiteSpace: "nowrap" }}>{s.heldByCount} 檔</div>
                        <div style={{ fontFamily: "'Fira Code', monospace", fontSize: 11, whiteSpace: "nowrap", textAlign: "right" }}>
                          <span style={{ color: s.addCount > 0 ? C.up : C.t3, fontWeight: 700 }}>+{s.addCount}</span>
                          <span style={{ color: C.t3 }}> / </span>
                          <span style={{ color: s.reduceCount > 0 ? C.down : C.t3, fontWeight: 700 }}>-{s.reduceCount}</span>
                        </div>
                      </div>
                      {holders(s)}
                    </div>
                  );
                  return (
                    <div style={{ animation: "fadeUp 0.3s ease-out" }}>
                      <div style={{ fontSize: 12, color: C.t3, marginBottom: 10, lineHeight: 1.7 }}>橫向比對 27 檔主動 ETF 同日持股。<b style={{ color: C.up }}>共識股</b>＝多位經理人共同持有;<b style={{ color: C.t2 }}>分歧股</b>＝單一基金的重押。{d.date && <span style={{ fontFamily: "'Fira Code', monospace" }}>資料日 {d.date}</span>}</div>
                      <div style={{ fontSize: 11, color: C.t3, marginBottom: 14, lineHeight: 1.9, background: C.panel, borderRadius: 8, padding: "8px 12px" }}>
                        <div><b style={{ color: C.t2 }}>N 檔</b>＝共有幾檔主動 ETF 持有這檔股票。</div>
                        <div><b style={{ color: C.up }}>+N</b>＝今日「加碼」的家數(紅)、<b style={{ color: C.down }}>−N</b>＝「減碼」的家數(綠);其餘為持平。</div>
                        <div style={{ opacity: 0.85 }}>加減碼以<b>股數變化</b>為準(經理人實際買賣);少數只揭權重、無股數的基金則以權重變化判定。</div>
                        <div style={{ opacity: 0.85 }}>點任一列可展開,看是<b>哪些 ETF</b> 持有。</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
                        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: C.up, fontFamily: "'Bitter', serif", marginBottom: 8 }}>共識股 · 多檔持有</div>
                          {d.consensus.map(row)}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: C.t2, fontFamily: "'Bitter', serif", marginBottom: 8 }}>分歧股 · 單一基金重押</div>
                          {(d.divergent || []).map((s) => (
                            <div key={s.stock_code}>
                              <div onClick={() => toggle(s)} style={{ display: "grid", gridTemplateColumns: "16px minmax(58px,auto) 1fr auto", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: `1px solid ${C.border}44`, fontSize: 12, cursor: "pointer" }}>
                                <span style={{ color: C.t3, fontSize: 9, transition: "transform 0.15s", transform: consensusExpanded === s.stock_code ? "rotate(90deg)" : "none" }}>▶</span>
                                <div style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.accent }}>{s.stock_code}</div>
                                <div style={{ overflow: "hidden", minWidth: 0 }}>
                          <div style={{ color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(s.stock_name || "").slice(0, 12)}</div>
                          {s.sub_sector && <div style={{ fontSize: 9, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{s.sub_sector}</div>}
                        </div>
                                <div style={{ fontFamily: "'Fira Code', monospace", fontSize: 11, color: C.t1, fontWeight: 700 }}>{(s.totalWeight * 100).toFixed(1)}%</div>
                              </div>
                              {holders(s)}
                            </div>
                          ))}
                          {!(d.divergent || []).length && <div style={{ color: C.t3, fontSize: 11, padding: 12, textAlign: "center" }}>無</div>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── 分頁:產業資金流向 ── */}
                {activeLandingTab === "sectors" && (() => {
                  const d = sectorFlowData;
                  if (!d) return <div style={{ textAlign: "center", color: C.t3, padding: 40, fontSize: 13 }}>載入中…</div>;
                  if (d.empty || !d.sectors || !d.sectors.length) return <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>尚無資料。</div>;
                  const onlyFallback = d.sectors.every((s) => s.sector === "其他" || s.sector === "海外");
                  const maxAbs = Math.max(...d.sectors.map((s) => Math.abs(s.weight_delta || 0)), 0.0001);
                  return (
                    <div style={{ animation: "fadeUp 0.3s ease-out" }}>
                      <div style={{ fontSize: 12, color: C.t3, marginBottom: 14, lineHeight: 1.8 }}>27 檔主動 ETF 的持股依<b>細分產業(產業鏈)</b>加總的每日變化。<b>Δ</b>＝該產業鏈跨全部 ETF 的<b>權重淨變化</b>(×100,<span style={{ color: C.up }}>紅為淨流入</span>、<span style={{ color: C.down }}>綠為淨流出</span>);右側 <span style={{ color: C.up }}>+N</span>/<span style={{ color: C.down }}>−N</span>＝該產業鏈今日被<b>加碼/減碼的持股筆數</b>(跨檔,以股數變化為準)。<b style={{ color: C.t2 }}>點任一列</b>可展開看是<b style={{ color: C.up }}>哪幾檔 ETF 加碼</b>／<b style={{ color: C.down }}>減碼</b>(標各檔的淨權重變化)。<span style={{ opacity: 0.7 }}>(無產業鏈分類者退回粗產業別)</span>{d.date && <span style={{ fontFamily: "'Fira Code', monospace" }}>資料日 {d.date}</span>}</div>
                      {onlyFallback && <div style={{ background: `${C.warm}18`, border: `1px solid ${C.warm}55`, color: C.warm, borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12, lineHeight: 1.6 }}>⚠ 尚未建立產業對照表,目前只有「其他/海外」。請在本機執行 <code style={{ fontFamily: "'Fira Code', monospace" }}>node fetch-sectors.mjs</code>(需先在 Supabase 跑 <code style={{ fontFamily: "'Fira Code', monospace" }}>stock_sectors.sql</code>)。</div>}
                      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
                        {d.sectors.map((s) => {
                          const col = s.weight_delta >= 0 ? C.up : C.down;
                          const barW = (Math.abs(s.weight_delta) / maxAbs) * 100;
                          const open = sectorExpanded === s.sector;
                          const hasDetail = (s.addEtfs && s.addEtfs.length) || (s.reduceEtfs && s.reduceEtfs.length);
                          return (
                            <div key={s.sector} style={{ borderBottom: `1px solid ${C.border}44` }}>
                              <div onClick={() => hasDetail && setSectorExpanded(open ? null : s.sector)} style={{ display: "grid", gridTemplateColumns: "14px minmax(84px,auto) 1fr auto auto", alignItems: "center", gap: 10, padding: "8px 4px", fontSize: 12, cursor: hasDetail ? "pointer" : "default" }}>
                                <span style={{ color: C.t3, fontSize: 9, transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "none", opacity: hasDetail ? 1 : 0 }}>▶</span>
                                <div style={{ color: C.t1, fontWeight: 700, whiteSpace: "nowrap" }}>{s.sector}</div>
                                <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                                  <div style={{ width: "100%", height: "100%", background: col, borderRadius: 4, transform: `scaleX(${Math.min(barW, 100) / 100})`, transformOrigin: "left", transition: "transform 0.4s" }} />
                                </div>
                                <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontWeight: 800, color: col, whiteSpace: "nowrap" }}>{s.weight_delta >= 0 ? "+" : ""}{(s.weight_delta * 100).toFixed(2)}</div>
                                <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 10, color: C.t3, whiteSpace: "nowrap", minWidth: 62 }}>
                                  <span style={{ color: s.addCount > 0 ? C.up : C.t3 }}>+{s.addCount}</span>/<span style={{ color: s.reduceCount > 0 ? C.down : C.t3 }}>-{s.reduceCount}</span>
                                </div>
                              </div>
                              {open && hasDetail && (
                                <div style={{ padding: "2px 4px 12px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                                  <div style={{ fontSize: 9, color: C.t3, marginBottom: 2 }}>下方為 <b>ETF 檔數</b>;chip 上 <b>+權重變化</b>、括號內 <b>加/減碼筆數</b>(與標題列 +{s.addCount}/-{s.reduceCount} 筆對應)。</div>
                                  {s.addEtfs && s.addEtfs.length > 0 && (
                                    <div>
                                      <div style={{ fontSize: 10, color: C.up, fontWeight: 700, marginBottom: 4 }}>加碼 {s.addEtfs.length} 檔 ETF</div>
                                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                        {s.addEtfs.map((e) => (
                                          <span key={e.symbol} title={`淨權重 ${(e.weight_delta * 100).toFixed(2)}｜此鏈內 +${e.addCount}/-${e.reduceCount} 筆`} style={{ fontSize: 10, color: C.up, background: `${C.up}14`, border: `1px solid ${C.up}44`, borderRadius: 4, padding: "2px 7px", fontFamily: "'Fira Code', monospace" }}><EtfLink symbol={e.symbol} C={C} style={{ color: C.up }}>{displaySymbol(e.symbol)}</EtfLink><span style={{ opacity: 0.75, marginLeft: 4 }}>{e.weight_delta >= 0 ? "+" : ""}{(e.weight_delta * 100).toFixed(2)}</span><span style={{ opacity: 0.55, marginLeft: 4 }}>({e.addCount}/{e.reduceCount}筆)</span></span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {s.reduceEtfs && s.reduceEtfs.length > 0 && (
                                    <div>
                                      <div style={{ fontSize: 10, color: C.down, fontWeight: 700, marginBottom: 4 }}>減碼 {s.reduceEtfs.length} 檔 ETF</div>
                                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                        {s.reduceEtfs.map((e) => (
                                          <span key={e.symbol} title={`淨權重 ${(e.weight_delta * 100).toFixed(2)}｜此鏈內 +${e.addCount}/-${e.reduceCount} 筆`} style={{ fontSize: 10, color: C.down, background: `${C.down}14`, border: `1px solid ${C.down}44`, borderRadius: 4, padding: "2px 7px", fontFamily: "'Fira Code', monospace" }}><EtfLink symbol={e.symbol} C={C} style={{ color: C.down }}>{displaySymbol(e.symbol)}</EtfLink><span style={{ opacity: 0.75, marginLeft: 4 }}>{(e.weight_delta * 100).toFixed(2)}</span><span style={{ opacity: 0.55, marginLeft: 4 }}>({e.addCount}/{e.reduceCount}筆)</span></span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ── 分頁:精選訊號(選股框架) ── */}
                {activeLandingTab === "picks" && (() => {
                  const d = picksData;
                  if (!d) return <div style={{ textAlign: "center", color: C.t3, padding: 40, fontSize: 13 }}>載入中…</div>;
                  if (d.empty || !d.picks || !d.picks.length) return <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>尚無資料。</div>;
                  return (
                    <div style={{ animation: "fadeUp 0.3s ease-out" }}>
                      <div style={{ fontSize: 12, color: C.t3, marginBottom: 14, lineHeight: 1.8 }}>把經理人的操作軌跡轉成選股清單:<b>近 30 個交易日</b>內被<b style={{ color: C.up }}>連續加碼</b>、權重持續上升的個股(去年的舊交易不計)。<b style={{ color: C.up }}>N 檔加碼</b>＝近 30 交易日內有幾檔 ETF 在加碼此股;<b style={{ color: C.accent }}>連續 N 次</b>＝最長一檔在近 30 交易日內連續加碼的次數。下方<span style={{ color: C.up }}>「加碼中」</span>列出各 ETF 代號,<b>×N</b>＝該檔的連續加碼次數、<b style={{ color: C.up }}>紅框</b>為連最久者;<span style={{ color: C.down }}>「減碼」</span>列則是持有此股但近 30 日最近一動為<b style={{ color: C.down }}>減碼</b>的 ETF(反向訊號)。<span style={{ opacity: 0.7 }}>(僅操作面,未納入基本面)</span>{d.date && <span style={{ fontFamily: "'Fira Code', monospace" }}>資料日 {d.date}</span>}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                        {d.picks.map((p) => (
                          <div key={p.stock_code} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                              <span style={{ fontFamily: "'Fira Code', monospace", fontWeight: 800, color: C.t1, fontSize: 14 }}>{p.stock_code}</span>
                              <span style={{ fontSize: 11, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(p.stock_name || "").slice(0, 14)}</span>
                            </div>
                            {p.sub_sector && <div style={{ fontSize: 10, color: C.t2, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 6px", display: "inline-block", marginBottom: 8 }}>{p.sub_sector}</div>}
                            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: C.up, background: `${C.up}18`, borderRadius: 4, padding: "2px 7px", fontFamily: "'Fira Code', monospace" }}>{p.accumulatingEtfs} 檔加碼</span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, background: C.accentDim, borderRadius: 4, padding: "2px 7px", fontFamily: "'Fira Code', monospace" }}>連續 {p.maxStreak} 次</span>
                              {p.reduceEtfs && p.reduceEtfs.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: C.down, background: `${C.down}18`, borderRadius: 4, padding: "2px 7px", fontFamily: "'Fira Code', monospace" }}>{p.reduceEtfs.length} 檔減碼</span>}
                            </div>
                            {/* 加碼中的 ETF:每個標連續加碼次數(×N),連最久者加紅框 */}
                            <div style={{ fontSize: 9, color: C.up, fontWeight: 700, marginBottom: 3 }}>加碼中</div>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: (p.reduceEtfs && p.reduceEtfs.length) ? 8 : 0 }}>
                              {(p.etfs || []).map((e) => {
                                const top = e.streak === p.maxStreak && p.maxStreak >= 2;
                                return (
                                  <span key={e.symbol} title={`連續加碼 ${e.streak} 次`} style={{ fontSize: 9, color: C.up, background: `${C.up}14`, border: `1px solid ${top ? C.up : "transparent"}`, borderRadius: 3, padding: "1px 5px", fontFamily: "'Fira Code', monospace" }}><EtfLink symbol={e.symbol} C={C} style={{ color: C.up }}>{displaySymbol(e.symbol)}</EtfLink><span style={{ opacity: 0.7, marginLeft: 3 }}>×{e.streak}</span></span>
                                );
                              })}
                            </div>
                            {p.reduceEtfs && p.reduceEtfs.length > 0 && (<>
                              <div style={{ fontSize: 9, color: C.down, fontWeight: 700, marginBottom: 3 }}>近 30 日最近一動為減碼</div>
                              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {p.reduceEtfs.map((e) => (
                                  <EtfLink key={e.symbol} symbol={e.symbol} C={C} style={{ fontSize: 9, color: C.down, background: `${C.down}14`, borderRadius: 3, padding: "1px 5px", fontFamily: "'Fira Code', monospace", borderBottom: "none" }}>{displaySymbol(e.symbol)}</EtfLink>
                                ))}
                              </div>
                            </>)}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* ── Per-ETF detail view (full page) ── */}
            {activeEtfDetail && !activeEtfData && (
              <div style={{ textAlign: "center", color: C.t3, padding: 40, fontSize: 13 }}>載入中…</div>
            )}
            {activeEtfDetail && activeEtfData && (() => {
                  const etf = (activeEtfData.etfs || []).find((e) => e.symbol === activeEtfDetail);
                  if (!etf) return (
                    <div style={{ textAlign: "center", color: C.t3, padding: 40, fontSize: 13 }}>
                      找不到這檔 ETF 的持股資料，請稍後再試。
                      <div style={{ marginTop: 12 }}>
                        <button onClick={() => setActiveEtfDetail(null)} style={{ background: "none", border: `1px solid ${C.border}`, color: C.accent, borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>← 返回列表</button>
                      </div>
                    </div>
                  );
                  const hist = activeEtfHistory && activeEtfHistory.symbol === activeEtfDetail ? activeEtfHistory : null;
                  const tabs = [
                    { id: "holdings", label: "目前持股" },
                    { id: "strategy", label: "策略/損益" },
                    { id: "delta", label: "當日加減碼" },
                    { id: "timeline", label: "歷史軌跡" },
                    { id: "stock", label: "單股進出場" },
                    { id: "exited", label: "已出清" },
                  ];

                  // 加減碼分類以後端 action 為準(股數優先,無股數才用權重);與卡片/共識股一致
                  const etfHoldings = etf.holdings || [];
                  const entered = etfHoldings.filter((h) => h.action ? h.action === "new" : h.isNew);
                  const increased = etfHoldings.filter((h) => h.action ? h.action === "add" : (!h.isNew && h.weight_delta > 0.00005));
                  const decreased = etfHoldings.filter((h) => h.action ? h.action === "reduce" : (!h.isNew && h.weight_delta < -0.00005));
                  const exitedList = etf.exitedList || [];
                  const allDeltas = [...entered.map((h) => h.weight), ...increased.map((h) => Math.abs(h.weight_delta || 0)), ...decreased.map((h) => Math.abs(h.weight_delta || 0)), ...exitedList.map((h) => h.weight || 0)];
                  const maxDelta = Math.max(...allDeltas, 0.001);

                  const renderBar = (items, color, deltaFn, barFn, title) => {
                    if (!items.length) return null;
                    return (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 6, fontFamily: "'Bitter', serif" }}>{title}（{items.length}）</div>
                        {items.map((h) => {
                          const d = deltaFn(h);
                          const barW = barFn(h);
                          return (
                            <div key={h.stock_code} style={{ display: "grid", gridTemplateColumns: "minmax(130px, auto) 1fr 70px 80px", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                              <div style={{ overflow: "hidden" }}>
                                <div style={{ color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  <span style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700 }}>{h.stock_code}</span>
                                  <span style={{ color: C.t3, marginLeft: 6, fontSize: 11 }}>{(h.stock_name || "").slice(0, 10)}</span>
                                </div>
                                {h.sub_sector && <div style={{ fontSize: 9, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.sub_sector}</div>}
                              </div>
                              <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                                <div style={{ width: "100%", height: "100%", background: color, borderRadius: 4, transform: `scaleX(${Math.min(barW, 100) / 100})`, transformOrigin: "left", transition: "transform 0.4s" }} />
                              </div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontWeight: 700, color }}>{d > 0 ? "+" : ""}{(d * 100).toFixed(2)}%</div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 11 }}>
                                {h.shares_delta != null && h.shares_delta !== 0 ? `${h.shares_delta > 0 ? "+" : ""}${Number(h.shares_delta).toLocaleString()}` : h.shares != null ? Number(h.shares).toLocaleString() : ""}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  };

                  const heldStocks = hist ? hist.stocks.filter((s) => s.status === "held") : [];
                  const exitedStocks = hist ? hist.stocks.filter((s) => s.status === "exited") : [];
                  const selectedStockData = hist && activeEtfSelectedStock ? hist.stocks.find((s) => s.stock_code === activeEtfSelectedStock) : null;

                  const actionLabel = { entry: "進場", add: "加碼", reduce: "減碼", exit: "出場" };
                  const actionColor = { entry: C.up, add: C.up, reduce: C.down, exit: C.down };
                  const actionIcon = { entry: "▶", add: "▲", reduce: "▼", exit: "■" };

                  return (
                    <div>
                      <button onClick={() => setActiveEtfDetail(null)} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Fira Code', monospace", padding: 0, marginBottom: 12 }}>← 返回列表</button>
                      <div style={{ background: C.card, border: `1px solid ${C.accent}44`, borderRadius: 14, padding: "18px 20px", marginBottom: 20 }}>
                      {/* Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 18, fontWeight: 900, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{displaySymbol(etf.symbol)}</span>
                          <span style={{ fontSize: 13, color: C.t2 }}>{TW_ETF_NAMES[etf.symbol] || ""}</span>
                          <span style={{ fontSize: 10, color: C.accent, background: C.accentDim, borderRadius: 4, padding: "2px 6px", fontWeight: 700 }}>{TW_ETF_THEMES[etf.symbol] || ""}</span>
                        </div>
                        <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace", textAlign: "right" }}>
                          <div>持股 {etf.count} 檔</div>
                          <div>{etf.date}</div>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 16 }}>
                        {tabs.map((t) => (
                          <button key={t.id} onClick={() => setActiveEtfDetailTab(t.id)} style={{
                            background: "none", border: "none", borderBottom: activeEtfDetailTab === t.id ? `2px solid ${C.accent}` : "2px solid transparent",
                            color: activeEtfDetailTab === t.id ? C.accent : C.t3, cursor: "pointer", padding: "8px 14px", fontSize: 12, fontWeight: 700, fontFamily: "'Fira Code', monospace", transition: "color 0.2s, border-color 0.2s"
                          }}>{t.label}</button>
                        ))}
                      </div>

                      {/* Tab: 策略/損益 */}
                      {activeEtfDetailTab === "strategy" && (() => {
                        const st = activeStrategyDetail && activeStrategyDetail.symbol === etf.symbol ? activeStrategyDetail : null;
                        if (activeStrategyLoading && !st) return <div style={{ padding: 30, textAlign: "center", color: C.t3, fontSize: 12, fontFamily: "'Fira Code', monospace" }}>載入策略分析…</div>;
                        if (!st || st.empty || !st.stocks) return <div style={{ padding: 24, textAlign: "center", color: C.t3, fontSize: 12, lineHeight: 1.8 }}>尚無策略資料。<br />請在本機執行 <code style={{ color: C.accent, fontFamily: "'Fira Code', monospace" }}>node compute-strategy.mjs</code> 產生。</div>;
                        const fmtM = (n) => n == null ? "—" : (Math.abs(n) >= 1e8 ? `${(n / 1e8).toFixed(2)}億` : Math.abs(n) >= 1e4 ? `${(n / 1e4).toFixed(0)}萬` : `${Math.round(n)}`);
                        const fmtPct = (n) => n == null ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;
                        const pl = (n) => n == null ? C.t3 : n > 0 ? C.up : n < 0 ? C.down : C.t3; // 台股漲紅跌綠
                        const held = st.stocks.filter((s) => s.status === "held");
                        return (
                          <div>
                            {st.data_suspect && <div style={{ background: `${C.warm}18`, border: `1px solid ${C.warm}55`, color: C.warm, borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12 }}>⚠ 資料品質：{st.data_suspect}（此檔數據暫不可信）</div>}
                            {/* 匯總卡 */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 8 }}>
                              {[
                                ["勝率", st.win_rate != null ? `${st.win_rate.toFixed(0)}%` : "—", C.t1, st.wins != null ? `${st.wins}W / ${st.losses}L` : ""],
                                ["報酬率(估)", fmtPct(st.return_pct), pl(st.return_pct), ""],
                                ["未實現損益", fmtM(st.total_unrealized), pl(st.total_unrealized), ""],
                                ["已實現損益", fmtM(st.total_realized), pl(st.total_realized), ""],
                                ["前5大集中度", st.top5_weight != null ? `${(st.top5_weight * 100).toFixed(0)}%` : "—", C.t1, `平均持有 ${Math.round(st.avg_days_held || 0)} 天`],
                              ].map(([label, val, col, sub]) => (
                                <div key={label} style={{ background: C.panel, borderRadius: 10, padding: "12px 14px" }}>
                                  <div style={{ fontSize: 10, color: C.t3, marginBottom: 5, fontFamily: "'Fira Code', monospace" }}>{label}</div>
                                  <div style={{ fontSize: 18, fontWeight: 900, color: col, fontFamily: "'Bitter', serif" }}>{val}</div>
                                  {sub && <div style={{ fontSize: 9, color: C.t3, marginTop: 3, fontFamily: "'Fira Code', monospace" }}>{sub}</div>}
                                </div>
                              ))}
                            </div>
                            <div style={{ fontSize: 10, color: C.t3, marginBottom: 12, fontFamily: "'Fira Code', monospace" }}>
                              以持股揭露 + Yahoo 收盤估算（非官方 NAV）· {st.priced_count}/{st.pos_count} 檔可定價 · 更新 {st.as_of}
                            </div>
                            {/* 個股均價 vs 現價 */}
                            <div style={{ display: "grid", gridTemplateColumns: "minmax(70px,auto) 1fr repeat(3, minmax(64px,auto))", gap: "0 8px", padding: "0 4px 6px", borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontWeight: 700, fontFamily: "'Fira Code', monospace" }}>
                              <div>代碼</div><div>名稱</div><div style={{ textAlign: "right" }}>均價</div><div style={{ textAlign: "right" }}>現價</div><div style={{ textAlign: "right" }}>報酬</div>
                            </div>
                            {held.slice(0, 40).map((s) => (
                              <div key={s.stock_code} style={{ display: "grid", gridTemplateColumns: "minmax(70px,auto) 1fr repeat(3, minmax(64px,auto))", gap: "0 8px", padding: "7px 4px", borderBottom: `1px solid ${C.border}44`, alignItems: "center", fontFamily: "'Fira Code', monospace", fontSize: 11 }}>
                                <div style={{ fontWeight: 700, color: C.accent }}>{displaySymbol(s.stock_code)}</div>
                                <div style={{ color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.stock_name}</div>
                                <div style={{ textAlign: "right", color: C.t2 }}>{s.avg_cost != null ? s.avg_cost.toLocaleString() : "—"}</div>
                                <div style={{ textAlign: "right", color: C.t1, fontWeight: 700 }}>{s.cur_price != null ? s.cur_price.toLocaleString() : "—"}</div>
                                <div style={{ textAlign: "right", fontWeight: 800, color: pl(s.unrealized_pct) }}>{fmtPct(s.unrealized_pct)}</div>
                              </div>
                            ))}
                            {held.length > 40 && <div style={{ fontSize: 10, color: C.t3, textAlign: "center", padding: "8px 0", fontFamily: "'Fira Code', monospace" }}>… 其餘 {held.length - 40} 檔</div>}

                            {/* 績效歸因(近 N 日):貢獻 = 權重 × 區間報酬 */}
                            {st.attribution && st.attribution.contributors && st.attribution.contributors.length > 0 && (() => {
                              const a = st.attribution;
                              const maxAbs = Math.max(...a.contributors.map((c) => Math.abs(c.contribution || 0)), 0.0001);
                              return (
                                <div style={{ marginTop: 22 }}>
                                  <div style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 4 }}>績效歸因 · 近 {a.window_days} 日</div>
                                  <div style={{ fontSize: 10, color: C.t3, marginBottom: 12, fontFamily: "'Fira Code', monospace" }}>
                                    區間估計報酬 <span style={{ color: pl(a.etf_return * 100), fontWeight: 700 }}>{fmtPct(a.etf_return * 100)}</span> · 涵蓋權重 {(a.coverage_weight * 100).toFixed(0)}% · 貢獻 = 權重 × 區間報酬
                                  </div>
                                  {a.contributors.map((c) => {
                                    const col = c.contribution >= 0 ? C.up : C.down; // 台股漲紅跌綠
                                    const barW = (Math.abs(c.contribution) / maxAbs) * 100;
                                    return (
                                      <div key={c.code} style={{ display: "grid", gridTemplateColumns: "minmax(64px,auto) minmax(70px,1fr) 1.2fr auto", alignItems: "center", gap: 8, padding: "5px 4px", borderBottom: `1px solid ${C.border}44`, fontSize: 11, fontFamily: "'Fira Code', monospace" }}>
                                        <div style={{ fontWeight: 700, color: C.accent }}>{c.code}</div>
                                        <div style={{ color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(c.name || "").slice(0, 8)}</div>
                                        <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                                          <div style={{ width: `${Math.min(barW, 100)}%`, height: "100%", background: col, borderRadius: 4 }} />
                                        </div>
                                        <div style={{ textAlign: "right", color: C.t3 }}>
                                          <span style={{ color: C.t2 }}>{(c.weight * 100).toFixed(1)}%</span>
                                          <span> × </span>
                                          <span style={{ color: col }}>{c.ret >= 0 ? "+" : ""}{(c.ret * 100).toFixed(1)}%</span>
                                          <span style={{ color: col, fontWeight: 800, marginLeft: 6 }}>= {c.contribution >= 0 ? "+" : ""}{(c.contribution * 100).toFixed(2)}%</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })()}

                      {/* Tab: 目前持股 */}
                      {activeEtfDetailTab === "holdings" && (
                        <div>
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(60px, auto) 1fr auto 80px", gap: 8, padding: "0 0 6px", borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontWeight: 700 }}>
                            {[["stock_code","代碼"],["stock_name","名稱"],["weight","權重","right"],["shares","股數","right"]].map(([field, label, align], i) => (
                              <div key={i} onClick={() => toggleDetailHoldingSort(field)} style={{ textAlign: align || "left", cursor: "pointer", userSelect: "none", color: detailHoldingSort.field === field ? C.accent : C.t3 }}>{label}{detailHoldingSortArrow(field)}</div>
                            ))}
                          </div>
                          {[...etfHoldings].sort((a, b) => {
                            const d = detailHoldingSort.dir === "asc" ? 1 : -1;
                            const f = detailHoldingSort.field;
                            if (f === "stock_code" || f === "stock_name") return d * (a[f] || "").localeCompare(b[f] || "");
                            return d * ((a[f] || 0) - (b[f] || 0));
                          }).map((h) => (
                            <div key={h.stock_code} style={{ display: "grid", gridTemplateColumns: "minmax(60px, auto) 1fr auto 80px", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12, alignItems: "center" }}>
                              <div style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.t1, whiteSpace: "nowrap" }}>{h.stock_code}</div>
                              <div style={{ overflow: "hidden" }}>
                                <div style={{ color: C.t2, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {h.stock_name || ""}
                                  {h.isNew && <span style={{ marginLeft: 6, fontSize: 9, color: C.up, background: `${C.up}18`, borderRadius: 3, padding: "1px 4px", fontWeight: 700 }}>NEW</span>}
                                </div>
                                {h.sub_sector && <div style={{ fontSize: 9, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.sub_sector}</div>}
                              </div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", color: C.t1, whiteSpace: "nowrap" }}>{h.weight != null ? (h.weight * 100).toFixed(2) + "%" : "—"}</div>
                              <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 11 }}>{h.shares != null ? Number(h.shares).toLocaleString() : "—"}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tab: 當日加減碼 */}
                      {activeEtfDetailTab === "delta" && etf.noTradeDay && (
                        <div style={{ textAlign: "center", padding: "32px 20px" }}>
                          <div style={{ fontSize: 28, marginBottom: 10 }}>🛌</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 8 }}>{etf.date} 非交易日</div>
                          <div style={{ fontSize: 12.5, color: C.t2, lineHeight: 1.8, maxWidth: 460, margin: "0 auto" }}>
                            此揭露日的持股與前一交易日<b>完全相同</b>(股數未變),為<b>週末/假日的沿用揭露</b>,當日沒有實際加減碼。
                            {etf.lastTradeDate && <><br />最近一次加減碼發生在 <b style={{ fontFamily: "'Fira Code', monospace", color: C.accent }}>{etf.lastTradeDate}</b>。</>}
                          </div>
                          <div style={{ fontSize: 11, color: C.t3, marginTop: 12 }}>要看內容請切換到「目前持股」或「歷史軌跡」。</div>
                        </div>
                      )}
                      {activeEtfDetailTab === "delta" && !etf.noTradeDay && (
                        <div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                            {[
                              { label: "新進", n: entered.length, c: C.up },
                              { label: "出場", n: exitedList.length, c: C.down },
                              { label: "加碼", n: increased.length, c: C.up },
                              { label: "減碼", n: decreased.length, c: C.down },
                            ].map((s) => (
                              <div key={s.label} style={{ textAlign: "center", background: s.n > 0 ? `${s.c}14` : C.panel, borderRadius: 8, padding: "8px 0" }}>
                                <div style={{ fontSize: 20, fontWeight: 900, color: s.n > 0 ? s.c : C.t3 }}>{s.n}</div>
                                <div style={{ color: C.t3, fontSize: 11 }}>{s.label}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(130px, auto) 1fr 70px 80px", gap: 8, padding: "0 0 6px", borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontWeight: 700 }}>
                            <div>個股</div><div>權重變化</div><div style={{ textAlign: "right" }}>權重 Δ</div><div style={{ textAlign: "right" }}>股數 Δ</div>
                          </div>
                          {renderBar(entered.sort((a, b) => b.weight - a.weight), C.up, (h) => h.weight, (h) => (h.weight / maxDelta) * 100, "新進場")}
                          {renderBar(increased.sort((a, b) => (b.weight_delta || 0) - (a.weight_delta || 0)), C.up, (h) => h.weight_delta, (h) => (Math.abs(h.weight_delta || 0) / maxDelta) * 100, "加碼")}
                          {renderBar(decreased.sort((a, b) => (a.weight_delta || 0) - (b.weight_delta || 0)), C.down, (h) => h.weight_delta, (h) => (Math.abs(h.weight_delta || 0) / maxDelta) * 100, "減碼")}
                          {renderBar(exitedList.sort((a, b) => (b.weight || 0) - (a.weight || 0)), C.down, (h) => -(h.weight || 0), (h) => ((h.weight || 0) / maxDelta) * 100, "出場")}
                          {entered.length + increased.length + decreased.length + exitedList.length === 0 && (
                            <div style={{ textAlign: "center", color: C.t3, padding: 24, fontSize: 13 }}>今日持股無異動</div>
                          )}
                        </div>
                      )}

                      {/* Tab: 歷史軌跡 */}
                      {activeEtfDetailTab === "timeline" && (
                        <div>
                          {activeEtfHistoryLoading && <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>載入歷史資料中…</div>}
                          {hist && !activeEtfHistoryLoading && (() => {
                            const allEvents = [];
                            for (const s of hist.stocks) {
                              for (const ev of s.events) {
                                allEvents.push({ ...ev, stock_code: s.stock_code, stock_name: s.stock_name, sub_sector: s.sub_sector });
                              }
                            }
                            allEvents.sort((a, b) => b.date.localeCompare(a.date));
                            const dateGroups = {};
                            for (const ev of allEvents) { (dateGroups[ev.date] || (dateGroups[ev.date] = [])).push(ev); }
                            const sortedDates = Object.keys(dateGroups).sort((a, b) => b.localeCompare(a));

                            return (
                              <div>
                                <div style={{ fontSize: 11, color: C.t3, marginBottom: 12 }}>
                                  資料範圍 {hist.dateRange.first} ~ {hist.dateRange.last} · 共 {hist.totalDates} 天 · {hist.stocks.length} 檔個股
                                </div>
                                {sortedDates.slice(0, 30).map((date) => (
                                  <div key={date} style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: C.t2, fontFamily: "'Fira Code', monospace", marginBottom: 4, padding: "4px 8px", background: C.panel, borderRadius: 6, display: "inline-block" }}>{date}</div>
                                    {dateGroups[date].map((ev, i) => (
                                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0 5px 12px", borderLeft: `2px solid ${actionColor[ev.action]}`, marginLeft: 4, fontSize: 12 }}>
                                        <span style={{ color: actionColor[ev.action], fontWeight: 900, fontSize: 11, minWidth: 16 }}>{actionIcon[ev.action]}</span>
                                        <span style={{ color: actionColor[ev.action], fontWeight: 700, fontSize: 11, minWidth: 36 }}>{actionLabel[ev.action]}</span>
                                        <span style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.t1 }}>{ev.stock_code}</span>
                                        <span style={{ color: C.t3, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{ev.stock_name || ""}</span>
                                        {ev.sub_sector && <span style={{ color: C.t3, fontSize: 9, opacity: 0.75, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 }}>{ev.sub_sector}</span>}
                                        <span style={{ marginLeft: "auto", fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 11, whiteSpace: "nowrap" }}>
                                          {fmtSharesDelta(ev.shares_delta, narrow)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                                {sortedDates.length > 30 && <div style={{ textAlign: "center", color: C.t3, fontSize: 11, padding: 8 }}>顯示最近 30 天（共 {sortedDates.length} 天有異動）</div>}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Tab: 單股進出場 */}
                      {activeEtfDetailTab === "stock" && (
                        <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", gap: narrow ? 10 : 16 }}>
                          {/* Stock sidebar（窄螢幕改頂部,高度收斂,可捲動） */}
                          <div style={narrow
                            ? { width: "100%", maxHeight: 168, overflowY: "auto", borderBottom: `1px solid ${C.border}`, paddingBottom: 8, marginBottom: 2 }
                            : { width: 200, flexShrink: 0, maxHeight: 500, overflowY: "auto", borderRight: `1px solid ${C.border}`, paddingRight: 12 }}>
                            <div style={{ fontSize: 10, color: C.t3, fontWeight: 700, marginBottom: 6 }}>目前持股（點選查看歷程）</div>
                            {heldStocks.map((s) => (
                              <div key={s.stock_code} onClick={() => setActiveEtfSelectedStock(s.stock_code)}
                                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 6px", borderRadius: 6, cursor: "pointer", fontSize: 11,
                                  background: activeEtfSelectedStock === s.stock_code ? C.accentDim : "transparent",
                                  borderLeft: activeEtfSelectedStock === s.stock_code ? `3px solid ${C.accent}` : "3px solid transparent" }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.t1, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.stock_code}</div>
                                  <div style={{ fontSize: 10, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.stock_name || ""}</div>
                                  {s.sub_sector && <div style={{ fontSize: 9, color: C.t3, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sub_sector}</div>}
                                </div>
                                <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", fontSize: 10, color: C.t2, whiteSpace: "nowrap", flexShrink: 0 }}>{s.current_weight != null ? (s.current_weight * 100).toFixed(2) + "%" : ""}</div>
                              </div>
                            ))}
                            {exitedStocks.length > 0 && (
                              <>
                                <div style={{ fontSize: 10, color: C.t3, fontWeight: 700, marginTop: 12, marginBottom: 6 }}>已出清</div>
                                {exitedStocks.slice(0, 20).map((s) => (
                                  <div key={s.stock_code} onClick={() => setActiveEtfSelectedStock(s.stock_code)}
                                    style={{ display: "flex", justifyContent: "space-between", padding: "4px 6px", borderRadius: 6, cursor: "pointer", fontSize: 11,
                                      background: activeEtfSelectedStock === s.stock_code ? C.accentDim : "transparent",
                                      borderLeft: activeEtfSelectedStock === s.stock_code ? `3px solid ${C.accent}` : "3px solid transparent", opacity: 0.7 }}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.t3, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.stock_code}</div>
                                      <div style={{ fontSize: 10, color: C.t3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.stock_name || ""}</div>
                                      {s.sub_sector && <div style={{ fontSize: 9, color: C.t3, opacity: 0.8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sub_sector}</div>}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>

                          {/* Selected stock detail */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {activeEtfHistoryLoading && <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>載入中…</div>}
                            {!activeEtfHistoryLoading && !selectedStockData && <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>← 選擇個股查看進出場歷程</div>}
                            {selectedStockData && (() => {
                              const sd = selectedStockData;
                              return (
                                <div>
                                  {/* Stock header */}
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div>
                                      <span style={{ fontSize: 16, fontWeight: 900, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{sd.stock_code}</span>
                                      <span style={{ fontSize: 13, color: C.t2, marginLeft: 8 }}>{sd.stock_name}</span>
                                      {sd.sub_sector && <span style={{ fontSize: 10, color: C.t2, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 6px", marginLeft: 8 }}>{sd.sub_sector}</span>}
                                    </div>
                                    <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, fontWeight: 700, background: sd.status === "held" ? `${C.up}18` : `${C.down}18`, color: sd.status === "held" ? C.up : C.down }}>
                                      {sd.status === "held" ? "持有中" : "已出清"}
                                    </span>
                                  </div>

                                  {/* Summary cards */}
                                  <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                                    {[
                                      { label: "持有天數", v: sd.days_held },
                                      { label: "首次進場", v: sd.first_date },
                                      { label: "最近紀錄", v: sd.last_date },
                                      { label: "異動次數", v: sd.events.length },
                                    ].map((c) => (
                                      <div key={c.label} style={{ background: C.panel, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                                        <div style={{ fontSize: 14, fontWeight: 900, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{c.v}</div>
                                        <div style={{ fontSize: 10, color: C.t3 }}>{c.label}</div>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Weight timeline mini chart */}
                                  {sd.timeline.length > 1 && (() => {
                                    const tl = sd.timeline;
                                    const weights = tl.map((t) => t.weight || 0);
                                    const maxW = Math.max(...weights, 0.001);
                                    const minW = Math.min(...weights);
                                    const wRange = maxW - minW || 0.001;
                                    const WW = narrow ? 340 : 500, WH = 80, WP = { l: 40, r: 10, t: 8, b: 18 };
                                    const wcW = WW - WP.l - WP.r, wcH = WH - WP.t - WP.b;
                                    const wxPos = (i) => WP.l + (i / (tl.length - 1)) * wcW;
                                    const wyPos = (w) => WP.t + (1 - ((w - minW) / wRange)) * wcH;
                                    const wLinePath = tl.map((t, i) => `${i === 0 ? "M" : "L"}${wxPos(i).toFixed(1)},${wyPos(t.weight || 0).toFixed(1)}`).join(" ");
                                    const wHover = weightChartHover;
                                    const wHoverData = wHover != null && tl[wHover] ? tl[wHover] : null;
                                    const wDateLabels = [0, tl.length - 1];
                                    const handleWeightMouseMove = (e) => {
                                      const svg = e.currentTarget;
                                      const rect = svg.getBoundingClientRect();
                                      const mx = ((e.clientX - rect.left) / rect.width) * WW;
                                      const ratio = (mx - WP.l) / wcW;
                                      const idx = Math.round(ratio * (tl.length - 1));
                                      if (idx >= 0 && idx < tl.length) setWeightChartHover(idx);
                                      else setWeightChartHover(null);
                                    };
                                    return (
                                      <div style={{ marginBottom: 14 }}>
                                        <div style={{ fontSize: 10, color: C.t3, fontWeight: 700, marginBottom: 4 }}>權重走勢</div>
                                        <div style={{ background: C.panel, borderRadius: 8, padding: "8px 6px", position: "relative" }}>
                                          <svg viewBox={`0 0 ${WW} ${WH}`} style={{ width: "100%", display: "block", cursor: "crosshair" }}
                                            onMouseMove={handleWeightMouseMove} onMouseLeave={() => setWeightChartHover(null)}>
                                            <path d={wLinePath} fill="none" stroke={C.accent} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                                            {wDateLabels.map((di) => (
                                              <text key={di} x={wxPos(di)} y={WH - 2} textAnchor={di === 0 ? "start" : "end"} fontSize={8} fill={C.t3} fontFamily="'Fira Code', monospace">{tl[di].date}</text>
                                            ))}
                                            <text x={WP.l - 4} y={wyPos(maxW) + 3} textAnchor="end" fontSize={7} fill={C.t3} fontFamily="'Fira Code', monospace">{(maxW * 100).toFixed(1)}%</text>
                                            <text x={WP.l - 4} y={wyPos(minW) + 3} textAnchor="end" fontSize={7} fill={C.t3} fontFamily="'Fira Code', monospace">{(minW * 100).toFixed(1)}%</text>
                                            {wHover != null && wHoverData && (
                                              <g>
                                                <line x1={wxPos(wHover)} y1={WP.t} x2={wxPos(wHover)} y2={WH - WP.b} stroke={C.t3} strokeWidth={0.5} strokeDasharray="3,2" vectorEffect="non-scaling-stroke" />
                                                <circle cx={wxPos(wHover)} cy={wyPos(wHoverData.weight || 0)} r={3} fill={C.accent} stroke={C.bg} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                                              </g>
                                            )}
                                          </svg>
                                          {wHoverData && (
                                            <div style={{ position: "absolute", top: 4, right: 8, background: `${C.card}ee`, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 8px", pointerEvents: "none", zIndex: 2 }}>
                                              <div style={{ fontSize: 9, color: C.t2, fontFamily: "'Fira Code', monospace", fontWeight: 700 }}>{wHoverData.date}</div>
                                              <div style={{ fontSize: 12, color: C.accent, fontFamily: "'Fira Code', monospace", fontWeight: 900 }}>{((wHoverData.weight || 0) * 100).toFixed(2)}%</div>
                                              {wHoverData.shares != null && <div style={{ fontSize: 9, color: C.t3, fontFamily: "'Fira Code', monospace" }}>{Number(wHoverData.shares).toLocaleString()} 股</div>}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Price chart with events */}
                                  {activeStockPrices && !activeStockPrices.loading && activeStockPrices.prices.length > 1 && (() => {
                                    const allPrices = activeStockPrices.prices;
                                    const events = sd.events;
                                    const timeline = sd.timeline;
                                    const si = Math.floor(stockChartZoom.start * (allPrices.length - 1));
                                    const ei2 = Math.ceil(stockChartZoom.end * (allPrices.length - 1));
                                    const prices = allPrices.slice(si, ei2 + 1);
                                    if (prices.length < 2) return null;
                                    const W = narrow ? 460 : 700, H = narrow ? 300 : 260, PAD = { t: 18, r: 50, b: 30, l: 56 };
                                    const cW = W - PAD.l - PAD.r, cH = H - PAD.t - PAD.b;
                                    const rightSlots = Math.max(2, Math.round(prices.length * 0.05)); // 右側留白,最新一筆不貼右緣
                                    const xDen = prices.length - 1 + rightSlots;
                                    const closes = prices.map((p) => p.close);
                                    const rawMin = Math.min(...closes), rawMax = Math.max(...closes);
                                    const rawRange = rawMax - rawMin || 1;
                                    const midP = (rawMin + rawMax) / 2;
                                    const scaledRange = rawRange / stockChartYScale;
                                    const minP = midP - scaledRange / 2, maxP = midP + scaledRange / 2;
                                    const range = maxP - minP;
                                    const xPos = (i) => PAD.l + (i / xDen) * cW;
                                    const yPos = (v) => PAD.t + (1 - (v - minP) / range) * cH;
                                    const linePath = prices.map((p, i) => `${i === 0 ? "M" : "L"}${xPos(i).toFixed(1)},${yPos(p.close).toFixed(1)}`).join(" ");
                                    const dateToIdx = {};
                                    prices.forEach((p, i) => { dateToIdx[p.date] = i; });
                                    const eventMarkers = events.map((ev) => {
                                      const idx = dateToIdx[ev.date];
                                      if (idx == null) return null;
                                      const px = xPos(idx), py = yPos(prices[idx].close);
                                      const isUp = ev.action === "entry" || ev.action === "add";
                                      const color = ev.action === "entry" ? "#22c55e" : ev.action === "add" ? "#3b82f6" : ev.action === "reduce" ? "#f97316" : "#ef4444";
                                      const label = ev.action === "entry" ? "進場" : ev.action === "add" ? "加碼" : ev.action === "reduce" ? "減碼" : "出場";
                                      return { ...ev, px, py, isUp, color, label };
                                    }).filter(Boolean);
                                    let avgCost = null;
                                    if (timeline.some((t) => t.shares > 0)) {
                                      let totalCost = 0, totalShares = 0;
                                      const allDateIdx = {};
                                      allPrices.forEach((p, i) => { allDateIdx[p.date] = i; });
                                      for (const ev of events) {
                                        if (ev.action === "entry" || ev.action === "add") {
                                          const idx2 = allDateIdx[ev.date];
                                          if (idx2 != null) { totalCost += allPrices[idx2].close * (ev.shares || 0); totalShares += (ev.shares || 0); }
                                        }
                                      }
                                      if (totalShares > 0) avgCost = totalCost / totalShares;
                                    }
                                    const SH = 60, SP = { t: 6, b: 18 };
                                    const sharesMap = {};
                                    timeline.forEach((t) => { sharesMap[t.date] = t.shares; });
                                    const maxShares = Math.max(...timeline.map((t) => t.shares), 1);
                                    const gridLines = 4;
                                    const gridValues = Array.from({ length: gridLines + 1 }, (_, gi) => minP + (range * gi) / gridLines);
                                    const dateLabels = [];
                                    const step = Math.max(Math.floor(prices.length / (narrow ? 4 : 8)), 1);
                                    for (let i = 0; i < prices.length; i += step) dateLabels.push(i);
                                    const lastIdx = prices.length - 1;
                                    if (dateLabels[dateLabels.length - 1] !== lastIdx) {
                                      // 末筆與前一個刻度太近會疊字，先移除前一個再補末筆
                                      if (lastIdx - dateLabels[dateLabels.length - 1] < step * 0.6) dateLabels.pop();
                                      dateLabels.push(lastIdx);
                                    }
                                    const hoverIdx = stockChartHover;
                                    const hoverPrice = hoverIdx != null && prices[hoverIdx] ? prices[hoverIdx] : null;
                                    const hoverShares = hoverPrice ? (sharesMap[hoverPrice.date] ?? null) : null;
                                    const handleMouseMove = (e) => {
                                      if (stockChartDragRef.current) { setStockChartHover(null); return; }
                                      const svg = e.currentTarget;
                                      const rect = svg.getBoundingClientRect();
                                      const mx = ((e.clientX - rect.left) / rect.width) * W;
                                      const ratio = (mx - PAD.l) / cW;
                                      const idx = Math.round(ratio * xDen);
                                      if (idx >= 0 && idx < prices.length) setStockChartHover(idx);
                                      else setStockChartHover(null); // 落在右側留白區 → 不 hover
                                    };
                                    return (
                                      <div style={{ marginBottom: 14 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                          <div style={{ fontSize: 10, color: C.t3, fontWeight: 700 }}>股價走勢 · 進出場標記</div>
                                          {(stockChartZoom.start > 0 || stockChartZoom.end < 1 || stockChartYScale !== 1) && (
                                            <button onClick={() => { setStockChartZoom({ start: 0, end: 1 }); setStockChartYScale(1); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 4, color: C.t3, fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "'Fira Code', monospace" }}>重置縮放</button>
                                          )}
                                        </div>
                                        <div ref={stockChartRef} style={{ background: C.panel, borderRadius: 8, padding: "10px 6px", position: "relative", overflow: "hidden" }}>
                                          <svg viewBox={`0 0 ${W} ${H + SH}`} style={{ width: "100%", display: "block", cursor: stockChartDragRef.current ? "grabbing" : "crosshair", overflow: "hidden" }} xmlns="http://www.w3.org/2000/svg"
                                            onMouseMove={handleMouseMove} onMouseLeave={() => setStockChartHover(null)}>
                                            <defs>
                                              <clipPath id="pricePlotClip"><rect x={PAD.l} y={PAD.t} width={cW} height={cH} /></clipPath>
                                            </defs>
                                            {gridValues.map((gv, gi) => (
                                              <g key={gi}>
                                                <line x1={PAD.l} y1={yPos(gv)} x2={W - PAD.r} y2={yPos(gv)} stroke={C.border} strokeWidth={0.5} strokeDasharray={gi === 0 || gi === gridLines ? "0" : "3,3"} />
                                                <text x={PAD.l - 4} y={yPos(gv) + 3} textAnchor="end" fontSize={9} fill={C.t3} fontFamily="'Fira Code', monospace">{gv.toFixed(gv >= 100 ? 0 : gv >= 10 ? 1 : 2)}</text>
                                              </g>
                                            ))}
                                            {dateLabels.map((di) => (
                                              <text key={di} x={xPos(di)} y={H - 4} textAnchor="middle" fontSize={8} fill={C.t3} fontFamily="'Fira Code', monospace">{prices[di].date.slice(2).replace(/-/g, "/")}</text>
                                            ))}
                                            <path d={linePath} fill="none" stroke={C.accent} strokeWidth={1.5} clipPath="url(#pricePlotClip)" />
                                            {avgCost != null && avgCost >= minP && avgCost <= maxP && (
                                              <g>
                                                <line x1={PAD.l} y1={yPos(avgCost)} x2={W - PAD.r} y2={yPos(avgCost)} stroke="#a855f7" strokeWidth={1} strokeDasharray="4,3" />
                                                <text x={W - PAD.r + 4} y={yPos(avgCost) + 3} fontSize={8} fill="#a855f7" fontFamily="'Fira Code', monospace">均成本 {avgCost.toFixed(avgCost >= 100 ? 0 : 1)}</text>
                                              </g>
                                            )}
                                            {eventMarkers.map((em, emi) => (
                                              <g key={emi}>
                                                <polygon points={em.isUp ? `${em.px},${em.py - 14} ${em.px - 5},${em.py - 6} ${em.px + 5},${em.py - 6}` : `${em.px},${em.py + 14} ${em.px - 5},${em.py + 6} ${em.px + 5},${em.py + 6}`} fill={em.color} />
                                                <text x={em.px} y={em.isUp ? em.py - 17 : em.py + 23} textAnchor="middle" fontSize={8} fill={em.color} fontWeight={700} fontFamily="'Fira Code', monospace">{em.label}</text>
                                              </g>
                                            ))}
                                            <line x1={PAD.l} y1={H + SP.t} x2={W - PAD.r} y2={H + SP.t} stroke={C.border} strokeWidth={0.5} />
                                            <text x={PAD.l - 4} y={H + SP.t + 12} textAnchor="end" fontSize={8} fill={C.t3} fontFamily="'Fira Code', monospace">持股</text>
                                            {prices.map((p, pi) => {
                                              const sh = sharesMap[p.date];
                                              if (sh == null || sh === 0) return null;
                                              const barH = (sh / maxShares) * (SH - SP.t - SP.b);
                                              const barW = Math.max(cW / prices.length, 2);
                                              return <rect key={pi} x={xPos(pi) - barW / 2} y={H + SH - SP.b - barH} width={barW} height={barH} fill={C.accent} opacity={0.45} rx={0.5} />;
                                            })}
                                            {hoverIdx != null && hoverPrice && (
                                              <g>
                                                <line x1={xPos(hoverIdx)} y1={PAD.t} x2={xPos(hoverIdx)} y2={H} stroke={C.t3} strokeWidth={0.5} strokeDasharray="3,2" />
                                                <circle cx={xPos(hoverIdx)} cy={yPos(hoverPrice.close)} r={3.5} fill={C.accent} stroke={C.bg} strokeWidth={1.5} clipPath="url(#pricePlotClip)" />
                                              </g>
                                            )}
                                            <rect x={W - PAD.r} y={0} width={PAD.r} height={H + SH} fill="transparent" style={{ cursor: "ns-resize" }} />
                                            <line x1={W - PAD.r} y1={PAD.t} x2={W - PAD.r} y2={H} stroke={C.border} strokeWidth={0.5} />
                                            {stockChartYScale !== 1 && <text x={W - 2} y={H + SH - 4} textAnchor="end" fontSize={7} fill={C.t3} fontFamily="'Fira Code', monospace" opacity={0.6}>Y:{stockChartYScale.toFixed(1)}x</text>}
                                          </svg>
                                          {hoverPrice && (
                                            <div style={{ position: "absolute", top: 8, right: 12, background: `${C.card}ee`, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", pointerEvents: "none", zIndex: 2 }}>
                                              <div style={{ fontSize: 10, color: C.t2, fontFamily: "'Fira Code', monospace", fontWeight: 700 }}>{hoverPrice.date}</div>
                                              <div style={{ fontSize: 13, color: C.t1, fontFamily: "'Fira Code', monospace", fontWeight: 900 }}>{hoverPrice.close.toFixed(hoverPrice.close >= 100 ? 0 : 2)}</div>
                                              {hoverShares != null && <div style={{ fontSize: 10, color: C.accent, fontFamily: "'Fira Code', monospace" }}>持股 {Number(hoverShares).toLocaleString()}</div>}
                                            </div>
                                          )}
                                          <div style={{ textAlign: "center", fontSize: 9, color: C.t3, marginTop: 4, fontFamily: "'Fira Code', monospace" }}>滾輪縮放 · 拖曳平移 · 右側拉伸Y軸</div>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  {activeStockPrices && activeStockPrices.loading && (
                                    <div style={{ marginBottom: 14, background: C.panel, borderRadius: 8, padding: 20, textAlign: "center", color: C.t3, fontSize: 11 }}>股價載入中…</div>
                                  )}
                                  {activeStockPrices && !activeStockPrices.loading && activeStockPrices.prices.length < 2 && (
                                    <div style={{ marginBottom: 14, background: C.panel, borderRadius: 8, padding: 20, textAlign: "center", color: C.t3, fontSize: 11 }}>無法取得股價資料（可能為非主要交易所個股）</div>
                                  )}

                                  {/* Event timeline */}
                                  <div style={{ fontSize: 10, color: C.t3, fontWeight: 700, marginBottom: 6 }}>進出場歷程（{sd.events.length} 筆）</div>
                                  {sd.events.slice().reverse().map((ev, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", gap: narrow ? 6 : 10, padding: "6px 0 6px 12px", borderLeft: `2px solid ${actionColor[ev.action]}`, marginLeft: 4, marginBottom: 2 }}>
                                      <span style={{ color: actionColor[ev.action], fontWeight: 900, fontSize: 12 }}>{actionIcon[ev.action]}</span>
                                      <span style={{ color: actionColor[ev.action], fontWeight: 700, fontSize: 11, minWidth: narrow ? 32 : 36 }}>{actionLabel[ev.action]}</span>
                                      <span style={{ fontFamily: "'Fira Code', monospace", color: C.t2, fontSize: 11, whiteSpace: "nowrap" }}>{ev.date}</span>
                                      <span style={{ fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 11 }}>
                                        {ev.weight != null ? `${(ev.weight * 100).toFixed(2)}%` : ""}
                                      </span>
                                      <span style={{ marginLeft: "auto", fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 11, whiteSpace: "nowrap" }}>
                                        {fmtSharesDelta(ev.shares_delta, narrow)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Tab: 已出清 */}
                      {activeEtfDetailTab === "exited" && (
                        <div>
                          {activeEtfHistoryLoading && <div style={{ textAlign: "center", color: C.t3, padding: 30, fontSize: 12 }}>載入中…</div>}
                          {hist && !activeEtfHistoryLoading && (
                            <>
                              <div style={{ fontSize: 11, color: C.t3, marginBottom: 10 }}>已完全出清的個股（{exitedStocks.length} 檔）</div>
                              <div style={{ display: "grid", gridTemplateColumns: "minmax(60px, auto) 1fr 80px 80px 60px", gap: 8, padding: "0 0 6px", borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontWeight: 700 }}>
                                <div>代碼</div><div>名稱</div><div>進場日</div><div>出場日</div><div style={{ textAlign: "right" }}>天數</div>
                              </div>
                              {exitedStocks.map((s) => {
                                const exitEv = s.events.filter((e) => e.action === "exit");
                                const exitDate = exitEv.length ? exitEv[exitEv.length - 1].date : s.last_date;
                                return (
                                  <div key={s.stock_code} onClick={() => { setActiveEtfSelectedStock(s.stock_code); setActiveEtfDetailTab("stock"); }}
                                    style={{ display: "grid", gridTemplateColumns: "minmax(60px, auto) 1fr 80px 80px 60px", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12, cursor: "pointer", alignItems: "center" }}>
                                    <div style={{ fontFamily: "'Fira Code', monospace", fontWeight: 700, color: C.t3, whiteSpace: "nowrap" }}>{s.stock_code}</div>
                                    <div style={{ overflow: "hidden" }}>
                                      <div style={{ color: C.t3, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.stock_name || ""}</div>
                                      {s.sub_sector && <div style={{ fontSize: 9, color: C.t3, opacity: 0.8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sub_sector}</div>}
                                    </div>
                                    <div style={{ fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 10 }}>{s.first_date}</div>
                                    <div style={{ fontFamily: "'Fira Code', monospace", color: C.down, fontSize: 10 }}>{exitDate}</div>
                                    <div style={{ textAlign: "right", fontFamily: "'Fira Code', monospace", color: C.t3, fontSize: 11 }}>{s.days_held}</div>
                                  </div>
                                );
                              })}
                              {exitedStocks.length === 0 && <div style={{ textAlign: "center", color: C.t3, padding: 24, fontSize: 13 }}>目前沒有已出清的個股</div>}
                            </>
                          )}
                        </div>
                      )}
                      </div>
                    </div>
                  );
            })()}
          </div>
        )}

        {view === "report" && !session && renderLoginRequired(
          "每日報告需要登入",
          "登入帳號即可閱讀 AI 每日投資報告，分析美股／台股 ETF 收盤盤勢、類股輪動與趨勢研判。"
        )}

        {view === "report" && session && (
          <div style={{ animation: "fadeUp 0.3s ease-out", maxWidth: 860, margin: "0 auto" }}>
            <div style={{ background: `linear-gradient(135deg, ${C.card} 0%, ${C.panel} 100%)`, border: `1px solid ${C.border}`, borderRadius: 14, padding: "24px 24px", marginBottom: 18, textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 6 }}>
                <img src="/logo-etfowl.png" alt="ETFowl" style={{ width: 30, height: 30, objectFit: "contain" }} />
                <span style={{ fontSize: 24, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif" }}>每日 AI 投資報告</span>
              </div>
              <div style={{ fontSize: 12, color: C.t2 }}>單日收盤後盤勢分析 · 走向 · 趨勢研判</div>
            </div>

            {/* 市場切換 + 日期 */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <div className="seg-track" style={{ display: "inline-flex" }}>
                {[["US", "美股"], ["TW", "台股"]].map(([m, label]) => (
                  <button key={m} className={`seg-btn ${reportMarket === m ? "is-active" : ""}`} onClick={() => setReportMarket(m)} style={{ padding: "7px 18px", fontSize: 13, cursor: "pointer" }}>{label}</button>
                ))}
              </div>
              {reportDates.length > 0 && (
                <select
                  value={reportSelDate}
                  onChange={(e) => { setReportSelDate(e.target.value); loadReport(reportMarket, e.target.value); }}
                  style={{ padding: "7px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.t1, fontSize: 12, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}
                >
                  {reportDates.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>

            {reportError && <div style={{ background: `${C.red}18`, border: `1px solid ${C.red}55`, color: C.red, borderRadius: 8, padding: "10px 14px", fontSize: 12, marginBottom: 14 }}>{reportError}</div>}

            {reportLoading ? (
              <div style={{ textAlign: "center", padding: 50, color: C.t3 }}>
                <img src="/logo-etfowl.png" alt="" style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 12, animation: "logoPulse 1.3s ease-in-out infinite" }} />
                <div style={{ fontSize: 12, fontFamily: "'Fira Code', monospace" }}>AI 正在分析盤勢...</div>
                <style>{`@keyframes logoPulse { 0%,100% { transform: scale(0.92); opacity: 0.5; } 50% { transform: scale(1.08); opacity: 1; } }`}</style>
              </div>
            ) : reportData ? (
              <>
                {/* 盤面快照 + 技術分析 */}
                {reportData.snapshot && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>{reportData.report_date} 盤面快照</span>
                      {reportData.snapshot?.breadth && (
                        <span style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace" }}>
                          漲 <span style={{ color: dirColor(1, reportMarket === "TW") }}>{reportData.snapshot.breadth.up}</span> / 跌 <span style={{ color: dirColor(-1, reportMarket === "TW") }}>{reportData.snapshot.breadth.down}</span>
                        </span>
                      )}
                    </div>
                    {(() => {
                      const techItems = (reportData.snapshot?.techItems || []);
                      if (!techItems.length) return null;
                      return (
                        <>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.t2 }}>技術分析</span>
                            {reportData.snapshot?.techStale && <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>即時數據</span>}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                            {techItems.map((it) => {
                              if (it.error) return (
                                <div key={it.symbol} style={{ background: C.panel, borderRadius: 10, padding: "12px 14px" }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: C.t2 }}>{it.label}</div>
                                  <div style={{ fontSize: 11, color: C.t3, marginTop: 6 }}>當日資料暫無</div>
                                </div>
                              );
                              const up = it.changePct >= 0;
                              const trendColor = it.trend === "多頭排列" ? dirColor(1, reportMarket === "TW") : it.trend === "空頭排列" ? dirColor(-1, reportMarket === "TW") : C.warm;
                              const sp = it.spark || [];
                              const mn = Math.min(...sp), mx = Math.max(...sp), rng = (mx - mn) || 1;
                              const w = 130, h = 28;
                              const pts = sp.map((v, i) => `${(i / (sp.length - 1)) * w},${h - 2 - ((v - mn) / rng) * (h - 4)}`).join(" ");
                              return (
                                <div key={it.symbol} style={{ background: C.panel, borderRadius: 10, padding: "12px 14px" }}>
                                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{it.label}</span>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: dirColor(it.changePct, reportMarket === "TW"), fontFamily: "'Fira Code', monospace" }}>{up ? "+" : ""}{it.changePct.toFixed(2)}%</span>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "2px 0 6px", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Fira Code', monospace" }}>
                                      {it.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </span>
                                    {it.change != null && (
                                      <span style={{ fontSize: 11, fontWeight: 700, color: dirColor(it.change, reportMarket === "TW"), fontFamily: "'Fira Code', monospace" }}>
                                        {up ? "▲" : "▼"}{Math.abs(it.change).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                      </span>
                                    )}
                                  </div>
                                  {sp.length > 1 && (
                                    <svg width={w} height={h} style={{ display: "block", marginBottom: 8 }}>
                                      <polyline points={pts} fill="none" stroke={dirColor(it.changePct, reportMarket === "TW")} strokeWidth="1.5" strokeLinejoin="round" />
                                    </svg>
                                  )}
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 10, fontWeight: 700, color: C.bg, background: trendColor, borderRadius: 5, padding: "2px 7px" }}>{it.trend}</span>
                                    {it.rsi != null && (
                                      <span style={{ fontSize: 10, color: it.rsiState === "超買" ? dirColor(-1, reportMarket === "TW") : it.rsiState === "超賣" ? dirColor(1, reportMarket === "TW") : C.t3, fontFamily: "'Fira Code', monospace" }}>
                                        RSI {it.rsi.toFixed(0)} {it.rsiState}
                                      </span>
                                    )}
                                  </div>
                                  {it.wave && (
                                    <div style={{ fontSize: 10, color: C.t2, marginBottom: 5 }}>
                                      波段：{it.wave}{it.rangePos != null ? ` · 區間 ${it.rangePos}%` : ""}
                                    </div>
                                  )}
                                  {(it.support != null || it.resistance != null) && (
                                    <div style={{ display: "flex", gap: 10, marginBottom: 5, fontSize: 10, fontFamily: "'Fira Code', monospace" }}>
                                      {it.support != null && <span style={{ color: C.up }}>支撐 {it.support.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                      {it.resistance != null && <span style={{ color: C.down }}>突破 {it.resistance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>
                                    {it.vsMa20 != null && <span style={{ color: dirColor(it.vsMa20 ? 1 : -1, reportMarket === "TW") }}>{it.vsMa20 ? "▲" : "▼"}MA20 </span>}
                                    {it.vsMa60 != null && <span style={{ color: dirColor(it.vsMa60 ? 1 : -1, reportMarket === "TW") }}>{it.vsMa60 ? "▲" : "▼"}MA60</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 10 }}>
                            趨勢=均線排列 · 波段=高低點結構(非正式艾略特數浪) · 支撐/突破=近20日高低點 · RSI(14)
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* 台美財經重大新聞摘要 */}
                {reportData.snapshot?.news?.summary && (() => {
                  const news = reportData.snapshot.news;
                  const srcItems = [...(news.items?.tw || []), ...(news.items?.us || [])].filter((i) => i && i.link);
                  return (
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 18 }}>📰</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>台美財經重大新聞摘要</span>
                        <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace", marginLeft: "auto" }}>每日盤後彙整</span>
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.85, color: C.t1, fontFamily: "'Bitter', serif" }}>
                        {news.summary.split("\n").map((ln, i) => {
                          const t = ln.trim();
                          if (!t) return null;
                          const head = t.match(/^【(.+?)】$/);
                          if (head) return (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, margin: i ? "14px 0 8px" : "0 0 8px", color: C.accent, fontWeight: 800, fontSize: 12.5, fontFamily: "'Fira Code', monospace", letterSpacing: 0.5 }}>
                              <span style={{ width: 4, height: 14, borderRadius: 2, background: C.accent, display: "inline-block" }} />{head[1]}
                            </div>
                          );
                          const bullet = t.replace(/^[-–•]\s*/, "");
                          return (
                            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, paddingLeft: 2 }}>
                              <span style={{ color: C.cyan, flexShrink: 0, marginTop: 1 }}>•</span>
                              <span>{bullet}</span>
                            </div>
                          );
                        })}
                      </div>
                      {srcItems.length > 0 && (
                        <details style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                          <summary style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}>新聞來源（{srcItems.length}）</summary>
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 7 }}>
                            {srcItems.map((it, i) => (
                              <a key={i} href={it.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5, color: C.t2, textDecoration: "none", display: "flex", gap: 6, alignItems: "baseline" }}>
                                <span style={{ color: C.accent, flexShrink: 0 }}>›</span>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{it.title}{it.source ? <span style={{ color: C.t3 }}> · {it.source}</span> : null}</span>
                              </a>
                            ))}
                          </div>
                        </details>
                      )}
                      <div style={{ marginTop: 12, fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>AI 依公開新聞標題彙整重點 · 可能有延遲或誤差 · 非投資建議</div>
                    </div>
                  );
                })()}

                {/* 報告內文 */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 22px" }}>
                  {(() => {
                    const raw = reportData.content || "";
                    const lines = raw.split("\n");
                    let summaryLine = "", body = raw;
                    if (lines[0] && lines[0].includes("多空傾向")) {
                      summaryLine = lines[0].trim();
                      body = lines.slice(1).join("\n").replace(/^\s+/, "");
                    }
                    const bm = summaryLine.match(/偏多|偏空|中性|震盪/);
                    const bias = bm ? bm[0] : "";
                    const biasColor = bias === "偏多" ? dirColor(1, reportMarket === "TW") : bias === "偏空" ? dirColor(-1, reportMarket === "TW") : C.warm;
                    const summaryText = summaryLine
                      .replace(/^多空傾向[:：]\s*/, "")
                      .replace(/^(偏多|偏空|中性|震盪)\s*[｜|]\s*/, "");
                    return (
                      <>
                        {summaryLine && (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, background: `${biasColor}14`, border: `1px solid ${biasColor}55`, borderRadius: 10, padding: "12px 14px", marginBottom: 16, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>今日總結</span>
                            {bias && <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", background: biasColor, borderRadius: 6, padding: "4px 12px", whiteSpace: "nowrap" }}>{bias}</span>}
                            <span style={{ fontSize: 14, fontWeight: 700, color: C.t1, fontFamily: "'Bitter', serif" }}>{summaryText}</span>
                          </div>
                        )}
                        <div style={{ fontSize: 14, color: C.t1, lineHeight: 2, fontFamily: "'Bitter', serif", whiteSpace: "pre-wrap" }}>{body}</div>
                      </>
                    );
                  })()}
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>
                    AI 依當日盤面數據生成 · 非投資建議
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: 50, color: C.t3, fontSize: 13 }}>
                目前尚無報告。報告於每日收盤同步後自動生成。
              </div>
            )}
          </div>
        )}

        {view === "admin" && (
          isAdmin ? (
            <div style={{ animation: "fadeUp 0.3s ease-out", maxWidth: 1100, margin: "0 auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: C.t1, fontFamily: "'Bitter', serif" }}>管理者後台</div>
                  <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace", marginTop: 3 }}>{session?.user?.email}</div>
                </div>
                <button onClick={loadAdminStats} disabled={adminLoading} className="etf-btn" style={{ padding: "7px 16px", fontSize: 12, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: C.card, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer" }}>
                  {adminLoading ? "載入中…" : "↻ 重新整理"}
                </button>
              </div>

              {adminError && <div style={{ background: `${C.red}18`, border: `1px solid ${C.red}55`, color: C.red, borderRadius: 8, padding: "10px 14px", fontSize: 12, marginBottom: 14 }}>{adminError}</div>}

              {/* 數據卡片 */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
                {[
                  ["使用者人數", adminStats?.userCount ?? "—", C.accent],
                  ["今日瀏覽", adminStats?.traffic?.todayViews ?? "—", C.accent],
                  ["今日不重複訪客", adminStats?.traffic?.sessionsToday ?? "—", C.accent],
                  ["近 7 日瀏覽", adminStats?.traffic?.views7d ?? "—", C.t1],
                ].map(([label, val, col]) => (
                  <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 8, fontFamily: "'Fira Code', monospace" }}>{label}</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: col, fontFamily: "'Bitter', serif" }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* 未更新標的 */}
              {(() => {
                const staleRows = [];
                ["TW", "US"].forEach((m) => {
                  const syms = Object.values(MARKET_CONFIG[m].cats).flat();
                  const mQuotes = m === market ? quotes : (quotesCache.current[m] || {});
                  const names = MARKET_CONFIG[m].names;
                  const rows = syms
                    .map((s) => ({ symbol: s, name: names[s] || mQuotes[s]?.name || "", time: mQuotes[s]?.lastCloseTime, price: mQuotes[s]?.price, market: m }))
                    .filter((r) => r.time);
                  if (!rows.length) return;
                  const dates = [...new Set(rows.map((r) => taipeiDateKey(r.time)))].sort().reverse();
                  const latestDate = dates[0];
                  const prevDate = dates[1] || dates[0];
                  rows.forEach((r) => {
                    const d = taipeiDateKey(r.time);
                    if (d < prevDate) staleRows.push({ ...r, dateKey: d, latestDate });
                  });
                  syms.forEach((s) => {
                    if (!mQuotes[s]) staleRows.push({ symbol: s, name: names[s] || "", time: null, price: null, market: m, dateKey: "—", latestDate: dates[0] || "—" });
                  });
                });
                if (!staleRows.length) return null;
                staleRows.sort((a, b) => (a.market === b.market ? (a.dateKey || "").localeCompare(b.dateKey || "") : a.market.localeCompare(b.market)));
                return (
                  <div style={{ background: C.card, border: `1px solid ${C.warm}55`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: C.warm, fontFamily: "'Bitter', serif" }}>未更新標的</span>
                      <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>({staleRows.length} 檔落後 ≥2 交易日或無報價)</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>
                        <thead>
                          <tr style={{ color: C.t3, textAlign: "left" }}>
                            <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>市場</th>
                            <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>代號</th>
                            <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>名稱</th>
                            <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>最後報價日</th>
                            <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600, textAlign: "right" }}>價格</th>
                            <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>最新交易日</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staleRows.map((r) => (
                            <tr key={r.symbol}>
                              <td style={{ padding: "6px 10px", color: r.market === "TW" ? C.accent : C.t2 }}>{r.market}</td>
                              <td style={{ padding: "6px 10px", color: C.accent, fontWeight: 700 }}>{displaySymbol(r.symbol)}</td>
                              <td style={{ padding: "6px 10px", color: C.t2, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</td>
                              <td style={{ padding: "6px 10px", color: C.warm }}>{r.time ? taipeiDateKey(r.time) : "無資料"}</td>
                              <td style={{ padding: "6px 10px", color: C.t1, textAlign: "right" }}>{r.price != null ? r.price.toFixed(2) : "—"}</td>
                              <td style={{ padding: "6px 10px", color: C.t3 }}>{r.latestDate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Cloudflare Workers AI 官方用量 */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif" }}>Workers AI 官方用量</span>
                  <span style={{ fontSize: 10, color: C.t3, fontFamily: "'Fira Code', monospace" }}>(Cloudflare Analytics · 每日約 10,000 neurons 免費)</span>
                </div>
                {adminStats?.cf?.error ? (
                  <div style={{ fontSize: 12, color: C.warm }}>無法取得:{adminStats.cf.error}</div>
                ) : adminStats?.cf ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                    {[
                      ["今日 Neurons", adminStats.cf.aiNeuronsToday, `今日額度 ${((adminStats.cf.aiNeuronsToday / 10000) * 100).toFixed(1)}%`],
                      ["近 7 日 Neurons", adminStats.cf.aiNeurons7d, null],
                      ["今日推論次數", adminStats.cf.aiRequestsToday, null],
                      ["近 7 日推論次數", adminStats.cf.aiRequests7d, null],
                    ].map(([label, val, sub]) => (
                      <div key={label} style={{ background: C.panel, borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ fontSize: 11, color: C.t3, marginBottom: 6, fontFamily: "'Fira Code', monospace" }}>{label}</div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: C.accent, fontFamily: "'Bitter', serif" }}>{fmtNum(val)}</div>
                        {sub && <div style={{ fontSize: 10, color: C.t3, marginTop: 4 }}>{sub}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: C.t3 }}>尚未設定 Cloudflare API token(設定後顯示官方 Workers AI neurons 用量)。</div>
                )}
              </div>

              {/* 更新資料 */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 4 }}>更新資料</div>
                <div style={{ fontSize: 11, color: C.t3, marginBottom: 14 }}>手動更新最新收盤報價(全部標的),寫入 Supabase。長期歷史回補請用本機 npm run sync:prices:history。</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[["ALL", "全部"], ["US", "美股"], ["TW", "台股"]].map(([m, label]) => (
                    <button key={m} onClick={() => triggerAdminSync(m)} disabled={adminSyncing} className="etf-btn" style={{ padding: "9px 20px", fontSize: 13, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: adminSyncing ? C.panel : C.accent, color: adminSyncing ? C.t3 : C.bg, border: "none", borderRadius: 8, cursor: adminSyncing ? "default" : "pointer" }}>
                      {adminSyncing ? "更新中…" : `更新 ${label}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* 重新生成每日報告 */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 4 }}>重新生成每日報告</div>
                <div style={{ fontSize: 11, color: C.t3, marginBottom: 14 }}>覆蓋最新交易日的 AI 報告與技術分析快照(含修正後的指數收盤數據)。會重新呼叫 AI，請稍候。</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[["ALL", "全部"], ["US", "美股"], ["TW", "台股"]].map(([m, label]) => (
                    <button key={m} onClick={() => regenerateReport(m)} disabled={reportRegenerating} className="etf-btn" style={{ padding: "9px 20px", fontSize: 13, fontWeight: 700, fontFamily: "'Fira Code', monospace", background: reportRegenerating ? C.panel : C.card, color: reportRegenerating ? C.t3 : C.t1, border: `1px solid ${C.border}`, borderRadius: 8, cursor: reportRegenerating ? "default" : "pointer" }}>
                      {reportRegenerating ? "生成中…" : `重生 ${label}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* 更新歷史 */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: "'Bitter', serif", marginBottom: 12 }}>更新歷史</div>
                {adminStats?.recentSyncs?.length ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'Fira Code', monospace" }}>
                      <thead>
                        <tr style={{ color: C.t3, textAlign: "left" }}>
                          {[["time","時間"],["type","類型"],["market","市場"],["rows","筆數"],["status","狀態"],["duration","耗時"],["message","說明"]].map(([field, label]) => (
                            <th key={field} onClick={() => toggleSyncSort(field)} style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", color: syncSort.field === field ? C.accent : C.t3 }}>{label}{syncSortArrow(field)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...adminStats.recentSyncs].sort((a, b) => {
                          const d = syncSort.dir === "asc" ? 1 : -1;
                          const f = syncSort.field;
                          if (f === "time") return d * ((a.started_at || "").localeCompare(b.started_at || ""));
                          if (f === "type") return d * ((a.type || "").localeCompare(b.type || ""));
                          if (f === "market") return d * ((a.market || "").localeCompare(b.market || ""));
                          if (f === "rows") return d * ((a.rows ?? 0) - (b.rows ?? 0));
                          if (f === "status") return d * ((a.status || "").localeCompare(b.status || ""));
                          if (f === "duration") return d * ((a.duration_ms ?? 0) - (b.duration_ms ?? 0));
                          if (f === "message") return d * ((a.message || "").localeCompare(b.message || ""));
                          return 0;
                        }).map((s) => (
                          <tr key={s.id}>
                            <td style={{ padding: "6px 10px", color: C.t2, whiteSpace: "nowrap" }}>{s.started_at ? new Date(s.started_at).toLocaleString("zh-TW", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                            <td style={{ padding: "6px 10px", color: C.t2 }}>{s.type}</td>
                            <td style={{ padding: "6px 10px", color: C.t2 }}>{s.market || "—"}</td>
                            <td style={{ padding: "6px 10px", color: C.t1 }}>{s.rows ?? "—"}</td>
                            <td style={{ padding: "6px 10px", color: s.status === "ok" ? C.green : s.status === "error" ? C.red : C.warm }}>{s.status}</td>
                            <td style={{ padding: "6px 10px", color: C.t3 }}>{s.duration_ms != null ? `${(s.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                            <td style={{ padding: "6px 10px", color: C.t3, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.message || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: C.t3 }}>{adminLoading ? "載入中…" : "尚無更新紀錄"}</div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px", color: C.t3 }}>需要管理者權限。</div>
          )
        )}
      </main>

      <footer
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "20px 24px 32px",
          borderTop: `1px solid ${C.border}`,
          marginTop: 24,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: C.t2, fontFamily: "'Fira Code', monospace", marginBottom: 8, letterSpacing: 0.3 }}>
          免責聲明
        </div>
        <p style={{ fontSize: 11, lineHeight: 1.85, color: C.t3, fontFamily: "'Bitter', serif", maxWidth: 880 }}>
          本平台提供的 ETF 資料、報價、新聞與 AI 推薦結果,皆僅供教育與參考用途,不構成任何投資建議。資料來源為第三方(如 Yahoo Finance),可能有延遲、錯誤或不完整,本平台不保證其正確性與即時性。
          <br />
          過去績效不代表未來表現,投資前請詳閱公開說明書,並自行或諮詢合格專業人士後做出決策,投資一切盈虧需自行承擔。
        </p>
      </footer>

      {selected && (
        <Detail
          symbol={selected}
          quote={quotes[selected]}
          onClose={() => setSelected(null)}
          currencySymbol={currencySymbol}
          activeThemes={activeThemes}
          etfMetrics={etfMetrics}
          riskCategory={catMap[selected]}
          onPickCategory={(c) => { setRiskFilter("all"); setCat(c); setView("search"); setSelected(null); }}
          etfName={mktCfg.names[selected]}
          isSaved={isWatchlisted(selected)}
          canUseWatchlist={Boolean(session)}
          onToggleWatchlist={(sym) => toggleWatchlist(sym, market)}
          onGoToActiveHoldings={(sym) => { setSelected(null); setView("active"); setActiveEtfDetail(sym); setActiveEtfDetailTab("delta"); }}
        />
      )}

      {/* Floating Compare Bar */}
      {compareList.length > 0 && !showCompare && (
        <div className="compare-bar" style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000,
          background: C.panel, borderTop: `1px solid ${C.border}`,
          padding: "10px 20px", display: "flex", alignItems: "center", gap: 12,
          flexWrap: "wrap", backdropFilter: "blur(12px)",
        }}>
          <span style={{ fontSize: 11, color: C.t3, fontFamily: "'Fira Code', monospace", fontWeight: 700, flexShrink: 0 }}>
            比較 ({compareList.length}/4)
          </span>
          {compareList.map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 4, background: C.card, border: `1px solid ${COMPARE_COLORS[i]}`, borderRadius: 6, padding: "4px 8px" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: COMPARE_COLORS[i] }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.t1, fontFamily: "'Fira Code', monospace" }}>{displaySymbol(s)}</span>
              <button onClick={() => toggleCompare(s)} style={{ background: "none", border: "none", color: C.t3, cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1 }}>✕</button>
            </div>
          ))}
          <div className="compare-spacer" style={{ flex: 1 }} />
          <button onClick={() => setCompareList([])} style={{ padding: "6px 12px", background: C.card, color: C.t2, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, fontFamily: "'Fira Code', monospace", cursor: "pointer" }}>清除</button>
          <button
            onClick={() => compareList.length >= 2 && setShowCompare(true)}
            disabled={compareList.length < 2}
            style={{
              padding: "8px 20px", background: compareList.length >= 2 ? C.accent : C.card,
              color: compareList.length >= 2 ? C.bg : C.t3, border: "none", borderRadius: 7,
              fontSize: 12, fontWeight: 800, fontFamily: "'Fira Code', monospace",
              cursor: compareList.length >= 2 ? "pointer" : "not-allowed",
            }}
          >
            比較績效
          </button>
        </div>
      )}

      {/* Compare Modal */}
      {showCompare && compareList.length >= 2 && (
        <CompareModal symbols={compareList} quotes={quotes} mktCfg={mktCfg} onClose={() => setShowCompare(false)} />
      )}

      {/* Footer */}
      <footer style={{
        textAlign: "center",
        padding: "24px 16px 20px",
        borderTop: `1px solid ${C.border}`,
        marginTop: 40,
      }}>
        <div style={{ fontSize: 11, color: C.t3, fontFamily: "'Bitter', serif", letterSpacing: 0.5 }}>
          ETF<span style={{ color: C.accent, fontWeight: 700 }}>owl</span>{" · "}
          <span style={{ opacity: 0.8 }}>Observe the market. Watch the trends. Learn the ETF.</span>
        </div>
      </footer>

      {toast && (
        <div style={{ position: "fixed", top: 24, right: 24, zIndex: 9999, animation: "toastIn 0.2s ease-out", pointerEvents: "none" }}>
          <div style={{ background: toast.type === "error" ? C.warm : C.card, color: toast.type === "error" ? "#fff" : C.t1, border: `1px solid ${toast.type === "error" ? C.warm : C.border}`, borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 700, fontFamily: "'Fira Code', monospace", boxShadow: "0 4px 20px rgba(0,0,0,0.4)", whiteSpace: "nowrap" }}>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
